export type SupportedLanguage = 'en' | 'hy' | 'ru' | 'es'

export interface LanguageOption {
  code: SupportedLanguage
  label: string
  nativeLabel: string
  locale: string
}

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en'
export const LANGUAGE_STORAGE_KEY = 'iva-language'

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  {
    code: 'en',
    label: 'English',
    nativeLabel: 'English',
    // en-GB keeps day/month/year ordering (en-US is month/day/year).
    locale: 'en-GB',
  },
  {
    code: 'hy',
    label: 'Armenian',
    nativeLabel: 'Հայերեն',
    locale: 'hy-AM',
  },
  {
    code: 'ru',
    label: 'Russian',
    nativeLabel: 'Русский',
    locale: 'ru-RU',
  },
  {
    code: 'es',
    label: 'Spanish',
    nativeLabel: 'Español',
    locale: 'es-ES',
  },
]

export type TranslationDictionary = Record<string, string>
export type TranslationCatalog = Record<
  SupportedLanguage,
  TranslationDictionary
>

export const translations: TranslationCatalog = {
  en: {
    appName: 'IVA',
    Home: 'Home',
    Notes: 'Notes',
    Memories: 'Memories',
    'To-Dos': 'To-Dos',
    Ideas: 'Ideas',
    Links: 'Links',
    Recipes: 'Recipes',
    Recipe: 'Recipe',
    Food: 'Food',
    Menus: 'Menus',
    Menu: 'Menu',
    Movies: 'Movies',
    Locations: 'Locations',
    Objectives: 'Objectives',
    Objective: 'Objective',
    Events: 'Events',
    Translation: 'Translation',
    Whiteboards: 'Whiteboards',
    Files: 'Files',
    Lunar: 'Lunar',
    Recordings: 'Recordings',
    'Microphone recordings from Lunar, grouped by user.':
      'Microphone recordings from Lunar, grouped by user.',
    '{{count}} recordings': '{{count}} recordings',
    'No recordings yet': 'No recordings yet',
    'This user has no recordings yet.': 'This user has no recordings yet.',
    'Could not load recordings.': 'Could not load recordings.',
    'Loading recordings...': 'Loading recordings...',
    'Unknown user': 'Unknown user',
    'Are you sure you want to delete this recording?':
      'Are you sure you want to delete this recording?',
    'Could not delete the recording.': 'Could not delete the recording.',
    Scenarios: 'Scenarios',
    Settings: 'Settings',
    Purchases: 'Purchases',
    Income: 'Income',
    Regular: 'Regular',
    Expected: 'Expected',
    Received: 'Received',
    'Income Date': 'Income Date',
    Source: 'Source',
    'Create Income': 'Create Income',
    'Add Income': 'Add Income',
    'Edit Income': 'Edit Income',
    'Income Details': 'Income Details',
    'No Income Yet': 'No Income Yet',
    '{{count}} incomes': '{{count}} incomes',
    'Income filters': 'Income filters',
    'Narrow income by name or date': 'Narrow income by name or date',
    'No matching income': 'No matching income',
    'Try a different name or date range.':
      'Try a different name or date range.',
    'Track salary, bonuses, and other inflows in one place.':
      'Track salary, bonuses, and other inflows in one place.',
    'Add regular income such as salary, or occasional inflows like bonuses and gifts.':
      'Add regular income such as salary, or occasional inflows like bonuses and gifts.',
    'Enter income source': 'Enter income source',
    'Enter amount': 'Enter amount',
    'Gross salary': 'Gross salary',
    'Net salary': 'Net salary',
    'Enter gross salary': 'Enter gross salary',
    'Enter net salary': 'Enter net salary',
    'Scan Payslip': 'Scan Payslip',
    'Upload a payslip image or PDF to autofill employer, net pay, date, and type. The file is saved with the income. Manual edit still works.':
      'Upload a payslip image or PDF to autofill employer, net pay, date, and type. The file is saved with the income. Manual edit still works.',
    'Payslip preview': 'Payslip preview',
    'Payslip document': 'Payslip document',
    'Choose payslip file': 'Choose payslip file',
    'Clear File': 'Clear File',
    'Payslip analyzed and filled': 'Payslip analyzed and filled',
    'Payslip analyzed. Review fields, then save.':
      'Payslip analyzed. Review fields, then save.',
    'Failed to analyze payslip.': 'Failed to analyze payslip.',
    Salary: 'Salary',
    Freelance: 'Freelance',
    Business: 'Business',
    Investment: 'Investment',
    Dividends: 'Dividends',
    Rental: 'Rental',
    Pension: 'Pension',
    Refund: 'Refund',
    Bonus: 'Bonus',
    Daily: 'Daily',
    Analytics: 'Analytics',
    'Spend analytics': 'Spend analytics',
    'Income analytics': 'Income analytics',
    Comparison: 'Comparison',
    'Utility bills': 'Utility bills',
    'Add Bill': 'Add Bill',
    'Bill Date': 'Bill Date',
    'Bill Cost': 'Bill Cost',
    'Company Name': 'Company Name',
    Provider: 'Provider',
    'Counter m³': 'Counter m³',
    'Month spend in m³': 'Month spend in m³',
    'Price per kW': 'Price per kW',
    'Month cost': 'Month cost',
    'Month spend in m³ × {{factor}} × Price per kW':
      'Month spend in m³ × {{factor}} × Price per kW',
    'Bill document': 'Bill document',
    'Choose bill file': 'Choose bill file',
    'Upload an image or PDF of the bill.':
      'Upload an image or PDF of the bill.',
    'Remove file': 'Remove file',
    'Scan gas bill': 'Scan gas bill',
    'Upload an image or PDF of the bill to autofill provider, readings, and prices.':
      'Upload an image or PDF of the bill to autofill provider, readings, and prices.',
    'Gas bill analyzed and filled': 'Gas bill analyzed and filled',
    'Gas bill analyzed. Review fields, then save.':
      'Gas bill analyzed. Review fields, then save.',
    'Choose bill files': 'Choose bill files',
    'Add bill files': 'Add bill files',
    'Upload one or more images or PDFs of the bill. All files are saved with the bill, and Analyze uses every page.':
      'Upload one or more images or PDFs of the bill. All files are saved with the bill, and Analyze uses every page.',
    'Failed to save gas bill.': 'Failed to save gas bill.',
    'Scan water bill': 'Scan water bill',
    'Water bill analyzed and filled': 'Water bill analyzed and filled',
    'Water bill analyzed. Review fields, then save.':
      'Water bill analyzed. Review fields, then save.',
    'Failed to analyze water bill.': 'Failed to analyze water bill.',
    'Failed to save water bill.': 'Failed to save water bill.',
    'Contract number': 'Contract number',
    'Current reading m³': 'Current reading m³',
    'Consumption m³': 'Consumption m³',
    'Price per m³': 'Price per m³',
    'Includes water, sewer, VAT, and waste taxes.':
      'Includes water, sewer, VAT, and waste taxes.',
    'Scan electricity bill': 'Scan electricity bill',
    'Electricity bill analyzed and filled':
      'Electricity bill analyzed and filled',
    'Electricity bill analyzed. Review fields, then save.':
      'Electricity bill analyzed. Review fields, then save.',
    'Failed to analyze electricity bill.':
      'Failed to analyze electricity bill.',
    'Failed to save electricity bill.': 'Failed to save electricity bill.',
    'Contracted power kW': 'Contracted power kW',
    'Includes power, energy, discounts, and taxes.':
      'Includes power, energy, discounts, and taxes.',
    'Bill documents': 'Bill documents',
    Invoice: 'Invoice',
    'Invoice number': 'Invoice number',
    'Supply address': 'Supply address',
    'Postal code': 'Postal code',
    'Billing period': 'Billing period',
    'Meter number': 'Meter number',
    CUPS: 'CUPS',
    'Previous reading m³': 'Previous reading m³',
    'Meter and consumption': 'Meter and consumption',
    Prices: 'Prices',
    'Conversion factor kWh/m³': 'Conversion factor kWh/m³',
    'Energy kWh': 'Energy kWh',
    'Price per kWh': 'Price per kWh',
    'Energy cost': 'Energy cost',
    'Total invoice': 'Total invoice',
    'Amount to pay or credit. Negative means a refund.':
      'Amount to pay or credit. Negative means a refund.',
    'Gas bill total (Importe Factura). Extra maintenance invoices stay in notes.':
      'Gas bill total (Importe Factura). Extra maintenance invoices stay in notes.',
    'Energy kWh × Price per kWh. This is not the invoice total.':
      'Energy kWh × Price per kWh. This is not the invoice total.',
    'Month spend in m³ × {{factor}} × Price per kWh':
      'Month spend in m³ × {{factor}} × Price per kWh',
    'Optional notes': 'Optional notes',
    'Add {{category}} Bill': 'Add {{category}} Bill',
    'Edit {{category}} Bill': 'Edit {{category}} Bill',
    'Are you sure you want to delete this bill?':
      'Are you sure you want to delete this bill?',
    'Utility Bills Total': 'Utility Bills Total',
    'All Utility Bills': 'All Utility Bills',
    'Utility Bills Timeline': 'Utility Bills Timeline',
    'No utility bills in this period.': 'No utility bills in this period.',
    'No utility bills on this day.': 'No utility bills on this day.',
    'No utility bills in this month.': 'No utility bills in this month.',
    'No utility bills in this year.': 'No utility bills in this year.',
    Bills: 'Bills',
    Bill: 'Bill',
    'Bill Details': 'Bill Details',
    'Edit Bill': 'Edit Bill',
    Price: 'Price',
    Currency: 'Currency',
    Close: 'Close',
    'Created At': 'Created At',
    'Spend charts for all utility bills together and for each utility category.':
      'Spend charts for all utility bills together and for each utility category.',
    'Breakdown of utility spend across every utility category.':
      'Breakdown of utility spend across every utility category.',
    'Total utility bill spend over time across all categories.':
      'Total utility bill spend over time across all categories.',
    'Add daily purchases or utility bills first, then this page will show spend charts by category.':
      'Add daily purchases or utility bills first, then this page will show spend charts by category.',
    'No Utility Bills Yet': 'No Utility Bills Yet',
    'Track electricity, water, gas, internet, and other recurring household bills in one place — amounts, periods, and providers.':
      'Track electricity, water, gas, internet, and other recurring household bills in one place — amounts, periods, and providers.',
    Electricity: 'Electricity',
    Gas: 'Gas',
    Internet: 'Internet',
    Phone: 'Phone',
    Rent: 'Rent',
    Telecommunications: 'Telecommunications',
    'Mortgage/Credit': 'Mortgage/Credit',
    Mortgage: 'Mortgage',
    Credit: 'Credit',
    'Bank Name': 'Bank Name',
    Amount: 'Amount',
    Percentage: 'Percentage',
    'Monthly Quota': 'Monthly Quota',
    'Monthly Quotas': 'Monthly Quotas',
    'Main Quota': 'Main Quota',
    'Percentage Quota': 'Percentage Quota',
    'Select period start and end to generate monthly quota rows.':
      'Select period start and end to generate monthly quota rows.',
    'For each month, fill both Main Quota and Percentage Quota, or leave both empty.':
      'For each month, fill both Main Quota and Percentage Quota, or leave both empty.',
    'Total To Pay': 'Total To Pay',
    Paid: 'Paid',
    Remaining: 'Remaining',
    'Payment Progress': 'Payment Progress',
    'Payments are counted at the end of each month. {{paidMonths}} of {{plannedMonths}} monthly payments completed.':
      'Payments are counted at the end of each month. {{paidMonths}} of {{plannedMonths}} monthly payments completed.',
    'Period Start': 'Period Start',
    'Period End': 'Period End',
    'Optional description': 'Optional description',
    'No {{category}} Bills Yet': 'No {{category}} Bills Yet',
    'Track electricity bills here — amounts, periods, and providers.':
      'Track electricity bills here — amounts, periods, and providers.',
    'Track water bills here — amounts, periods, and providers.':
      'Track water bills here — amounts, periods, and providers.',
    'Track gas bills here — amounts, periods, and providers.':
      'Track gas bills here — amounts, periods, and providers.',
    'Track internet, phone, and other telecom bills here — amounts, periods, and providers.':
      'Track internet, phone, and other telecom bills here — amounts, periods, and providers.',
    'Track mortgage and credit payments here — amounts, periods, and providers.':
      'Track mortgage and credit payments here — amounts, periods, and providers.',
    Trash: 'Trash',
    'No Comparison Data Yet': 'No Comparison Data Yet',
    'Add daily purchases from receipts first. Comparison tables are built by category, with markets as columns.':
      'Add daily purchases from receipts first. Comparison tables are built by category, with markets as columns.',
    'Each table groups receipt products by category. Columns are markets — if one market has several products of the same name, it gets extra columns.':
      'Each table groups receipt products by category. Columns are markets — if one market has several products of the same name, it gets extra columns.',
    Product: 'Product',
    Markets: 'Markets',
    Types: 'Types',
    'Each table is a category. Rows are specific product types (Beer, Coffee, Milk…). Columns are markets. Green is lowest price, red is highest.':
      'Each table is a category. Rows are specific product types (Beer, Coffee, Milk…). Columns are markets. Green is lowest price, red is highest.',
    Beer: 'Beer',
    Wine: 'Wine',
    Cider: 'Cider',
    Spirits: 'Spirits',
    'Soft Drink': 'Soft Drink',
    Juice: 'Juice',
    'Energy Drink': 'Energy Drink',
    Coffee: 'Coffee',
    Tea: 'Tea',
    Milk: 'Milk',
    'Plant Milk': 'Plant Milk',
    Yogurt: 'Yogurt',
    Cheese: 'Cheese',
    Butter: 'Butter',
    Cream: 'Cream',
    Bread: 'Bread',
    Pastry: 'Pastry',
    Chicken: 'Chicken',
    Beef: 'Beef',
    Pork: 'Pork',
    Fish: 'Fish',
    Seafood: 'Seafood',
    Fruit: 'Fruit',
    Vegetables: 'Vegetables',
    Pasta: 'Pasta',
    Rice: 'Rice',
    Flour: 'Flour',
    Sugar: 'Sugar',
    'Salt & Spices': 'Salt & Spices',
    'Ready Meal': 'Ready Meal',
    Tofu: 'Tofu',
    Oil: 'Oil',
    Sauce: 'Sauce',
    Snacks: 'Snacks',
    Chocolate: 'Chocolate',
    Candy: 'Candy',
    Cereal: 'Cereal',
    'Ice Cream': 'Ice Cream',
    'Frozen Meal': 'Frozen Meal',
    Shampoo: 'Shampoo',
    'Hair Spray': 'Hair Spray',
    Deodorant: 'Deodorant',
    Soap: 'Soap',
    Toothpaste: 'Toothpaste',
    Toothbrush: 'Toothbrush',
    Razor: 'Razor',
    'Feminine Care': 'Feminine Care',
    Detergent: 'Detergent',
    Cleaner: 'Cleaner',
    'Toilet Paper': 'Toilet Paper',
    'Paper Towels': 'Paper Towels',
    Dishwashing: 'Dishwashing',
    'Air Freshener': 'Air Freshener',
    'Trash Bags': 'Trash Bags',
    Diapers: 'Diapers',
    'Baby Food': 'Baby Food',
    'Pet Food': 'Pet Food',
    'Cat Litter': 'Cat Litter',
    Medicine: 'Medicine',
    Vitamins: 'Vitamins',
    Batteries: 'Batteries',
    'No Daily Purchases Yet': 'No Daily Purchases Yet',
    'Create a daily purchase and track all products bought in one market visit.':
      'Create a daily purchase and track all products bought in one market visit.',
    Occasional: 'Occasional',
    'No Occasional Purchases Yet': 'No Occasional Purchases Yet',
    'Add non-daily purchases here — larger buys or smaller one-offs — separate from day-to-day shopping.':
      'Add non-daily purchases here — larger buys or smaller one-offs — separate from day-to-day shopping.',
    'Comparison Is Coming Soon': 'Comparison Is Coming Soon',
    'This tab is intentionally left as a placeholder for now. We can return later and build product comparison.':
      'This tab is intentionally left as a placeholder for now. We can return later and build product comparison.',
    'Purchase Details': 'Purchase Details',
    'Create Daily Purchase': 'Create Daily Purchase',
    'Create Occasional Purchase': 'Create Occasional Purchase',
    'Edit Daily Purchase': 'Edit Daily Purchase',
    'Add Daily Purchase': 'Add Daily Purchase',
    'Edit Occasional Purchase': 'Edit Occasional Purchase',
    'Add Occasional Purchase': 'Add Occasional Purchase',
    'Manage your daily shopping, track occasional purchases separately, and leave room for a future products catalog.':
      'Manage your daily shopping, track occasional purchases separately, and leave room for a future products catalog.',
    'Scan Receipt': 'Scan Receipt',
    'Upload a receipt/check photo to autofill products. Manual add still works.':
      'Upload a receipt/check photo to autofill products. Manual add still works.',
    'Scan Purchase Image': 'Scan Purchase Image',
    'Upload an invoice, receipt, or product photo to autofill this occasional purchase. Manual edit still works.':
      'Upload an invoice, receipt, or product photo to autofill this occasional purchase. Manual edit still works.',
    'Choose purchase image': 'Choose purchase image',
    'Purchase image preview': 'Purchase image preview',
    'Analyze & Fill Form': 'Analyze & Fill Form',
    'Purchase image analyzed and filled': 'Purchase image analyzed and filled',
    'Purchase image analyzed. Review fields, then save.':
      'Purchase image analyzed. Review fields, then save.',
    'Failed to analyze purchase image.': 'Failed to analyze purchase image.',
    'Review and edit if needed.': 'Review and edit if needed.',
    'Receipt preview': 'Receipt preview',
    'Choose receipt image': 'Choose receipt image',
    'Analyzing...': 'Analyzing...',
    'Analyze & Fill List': 'Analyze & Fill List',
    'Clear Image': 'Clear Image',
    'Market Name': 'Market Name',
    'Enter market name': 'Enter market name',
    'Total Amount': 'Total Amount',
    'Receipt Number': 'Receipt Number',
    'Payment Method': 'Payment Method',
    'Ticket / invoice number': 'Ticket / invoice number',
    'card, cash, bizum...': 'card, cash, bizum...',
    'Add Product': 'Add Product',
    'Enter product name': 'Enter product name',
    'Line Total': 'Line Total',
    'Line importe / total': 'Line amount / total',
    'Receipt analyzed and filled': 'Receipt analyzed and filled',
    'Review and edit if needed. You can still add more manually.':
      'Review and edit if needed. You can still add more manually.',
    'Receipt analyzed. Review products, then save. You can still add more manually.':
      'Receipt analyzed. Review products, then save. You can still add more manually.',
    'Failed to analyze receipt image.': 'Failed to analyze receipt image.',
    'No Analytics Yet': 'No Analytics Yet',
    'Add daily purchases with products first, then this analytics tab will show spend charts by category.':
      'Add daily purchases with products first, then this analytics tab will show spend charts by category.',
    'Add income first, then this page will show charts by type.':
      'Add income first, then this page will show charts by type.',
    'See charts for salary, bonuses, and other inflows over time.':
      'See charts for salary, bonuses, and other inflows over time.',
    'Total Income': 'Total Income',
    'Regular Income': 'Regular Income',
    'Occasional Income': 'Occasional Income',
    'Income Records': 'Income Records',
    'Income by Type': 'Income by Type',
    'Breakdown of income across salary, freelance, and other types.':
      'Breakdown of income across salary, freelance, and other types.',
    'No income in this period.': 'No income in this period.',
    'Daily Spend': 'Daily Spend',
    'Occasional Purchases Total': 'Occasional Purchases Total',
    'Total Purchases': 'Total Purchases',
    'Tracked Products': 'Tracked Products',
    'Top Category': 'Top Category',
    Dairy: 'Dairy',
    Eggs: 'Eggs',
    Meat: 'Meat',
    'Fish & Seafood': 'Fish & Seafood',
    'Fruit & Veg': 'Fruit & Veg',
    Water: 'Water',
    Alcohol: 'Alcohol',
    'Other Drinks': 'Other Drinks',
    Drinks: 'Drinks',
    Average: 'Average',
    Bakery: 'Bakery',
    'Pasta & Rice': 'Pasta & Rice',
    'Oils & Condiments': 'Oils & Condiments',
    'Canned Food': 'Canned Food',
    Frozen: 'Frozen',
    'Breakfast & Cereals': 'Breakfast & Cereals',
    Household: 'Household',
    Hygiene: 'Hygiene',
    'Personal Care': 'Personal Care',
    Baby: 'Baby',
    Pet: 'Pet',
    Pharmacy: 'Pharmacy',
    'Ready Meals': 'Ready Meals',
    'Snacks & Sweets': 'Snacks & Sweets',
    'Coffee & Tea': 'Coffee & Tea',
    Charcuterie: 'Charcuterie',
    Legumes: 'Legumes',
    'Nuts & Seeds': 'Nuts & Seeds',
    'Baking Ingredients': 'Baking Ingredients',
    'Plant-based': 'Plant-based',
    Tobacco: 'Tobacco',
    Stationery: 'Stationery',
    'Batteries & Tech': 'Batteries & Tech',
    'Flowers & Plants': 'Flowers & Plants',
    Computers: 'Computers',
    'Phones & Tablets': 'Phones & Tablets',
    Electronics: 'Electronics',
    'TV & Audio': 'TV & Audio',
    Gaming: 'Gaming',
    'Camera & Photo': 'Camera & Photo',
    'Smart Home': 'Smart Home',
    Furniture: 'Furniture',
    'Bedding & Mattresses': 'Bedding & Mattresses',
    Appliances: 'Appliances',
    Kitchenware: 'Kitchenware',
    'Home Improvement': 'Home Improvement',
    Vehicles: 'Vehicles',
    Bicycles: 'Bicycles',
    Clothing: 'Clothing',
    'Shoes & Accessories': 'Shoes & Accessories',
    'Jewelry & Watches': 'Jewelry & Watches',
    Eyewear: 'Eyewear',
    'Luggage & Bags': 'Luggage & Bags',
    'Home & Decor': 'Home & Decor',
    'Garden & Outdoor': 'Garden & Outdoor',
    Sports: 'Sports',
    'Fitness Equipment': 'Fitness Equipment',
    'Tools & DIY': 'Tools & DIY',
    'Office Equipment': 'Office Equipment',
    'Baby Gear': 'Baby Gear',
    'Toys & Kids': 'Toys & Kids',
    Pets: 'Pets',
    'Musical Instruments': 'Musical Instruments',
    'Books & Media': 'Books & Media',
    'Hobbies & Collectibles': 'Hobbies & Collectibles',
    'Health Equipment': 'Health Equipment',
    Education: 'Education',
    'Software & Digital': 'Software & Digital',
    Travel: 'Travel',
    'Spend by Category': 'Spend by Category',
    'Based on daily-purchase product lines and their estimated totals.':
      'Based on daily-purchase product lines and their estimated totals.',
    'Category Comparison': 'Category Comparison',
    'Higher bars show categories where most of your money goes.':
      'Higher bars show categories where most of your money goes.',
    'Spend Map': 'Spend Map',
    'Larger tiles show categories where most of your money goes.':
      'Larger tiles show categories where most of your money goes.',
    Category: 'Category',
    Products: 'Products',
    Qty: 'Qty',
    'No products in this category.': 'No products in this category.',
    'Click a category to see its products.':
      'Click a category to see its products.',
    'Last month': 'Last month',
    'Last 3 months': 'Last 3 months',
    'Last 6 months': 'Last 6 months',
    'No purchases in this period.': 'No purchases in this period.',
    'Spend Timeline': 'Spend Timeline',
    'Income Timeline': 'Income Timeline',
    'By year': 'By year',
    'By month': 'By month',
    'See how your income changes over time across recorded inflows.':
      'See how your income changes over time across recorded inflows.',
    'See how your income changes over time across recorded inflows. Click a month to see its income.':
      'See how your income changes over time across recorded inflows. Click a month to see its income.',
    'A yearly view helps you compare overall income trends.':
      'A yearly view helps you compare overall income trends.',
    'A yearly view helps you compare overall income trends. Click a year to see its income.':
      'A yearly view helps you compare overall income trends. Click a year to see its income.',
    'No income in this year.': 'No income in this year.',
    'No income in this month.': 'No income in this month.',
    Month: 'Month',
    'Spend by Month': 'Spend by Month',
    'See how your spending changes over time across recorded purchases.':
      'See how your spending changes over time across recorded purchases.',
    'See how your spending changes over time across recorded purchases. Click a day to see its purchases.':
      'See how your spending changes over time across recorded purchases. Click a day to see its purchases.',
    'Spend by Year': 'Spend by Year',
    'A yearly view helps you compare overall purchase trends.':
      'A yearly view helps you compare overall purchase trends.',
    'A yearly view helps you compare overall purchase trends. Click a year to see its purchases.':
      'A yearly view helps you compare overall purchase trends. Click a year to see its purchases.',
    'No purchases on this day.': 'No purchases on this day.',
    'No purchases in this year.': 'No purchases in this year.',
    'Average Daily Receipt Spend': 'Average Daily Receipt Spend',
    Search: 'Search',
    Information: 'Information',
    Wedding: 'Wedding',
    Language: 'Language',
    'Open menu': 'Open menu',
    'Close menu': 'Close menu',
    'Search all items... (memories, todos, ideas, events, recipes, movies, and more)':
      'Search all items... (memories, todos, ideas, events, recipes, movies, and more)',
    'Show all results for "{{query}}"': 'Show all results for "{{query}}"',
    'No results found for "{{query}}"': 'No results found for "{{query}}"',
    'Open user menu': 'Open user menu',
    'User menu': 'User menu',
    Logout: 'Logout',
    'Are you sure you want to log out?': 'Are you sure you want to log out?',
    'Log Out': 'Log Out',
    'Confirmation Required': 'Confirmation Required',
    'Action not allowed': 'Action not allowed',
    Cancel: 'Cancel',
    Confirm: 'Confirm',
    'Clear selected value': 'Clear selected value',
    'Search...': 'Search...',
    'No items found': 'No items found',
    'Select {{placeholder}}': 'Select {{placeholder}}',
    '{{count}} selected': '{{count}} selected',
    'Select options': 'Select options',
    'Good morning': 'Good morning',
    'Good afternoon': 'Good afternoon',
    'Good evening': 'Good evening',
    'This Week': 'This Week',
    'Active To-Dos': 'Active To-Dos',
    'Active Objectives': 'Active Objectives',
    'Total Tasks': 'Total Tasks',
    'View All': 'View All',
    'Upcoming Events': 'Upcoming Events',
    'Next 30 Days': 'Next 30 Days',
    Today: 'Today',
    Tomorrow: 'Tomorrow',
    '{{count}} days': '{{count}} days',
    'No upcoming events': 'No upcoming events',
    'Your calendar is clear for now': 'Your calendar is clear for now',
    All: 'All',
    Vocabulary: 'Vocabulary',
    'Search all items...': 'Search all items...',
    'Found {{count}} result': 'Found {{count}} result',
    'Found {{count}} results': 'Found {{count}} results',
    'in {{type}}': 'in {{type}}',
    'No results found': 'No results found',
    'Enter a search term to find memories, todos, ideas, events, and more':
      'Enter a search term to find memories, todos, ideas, events, and more',
    'Untitled Memory': 'Untitled Memory',
    'Untitled Todo': 'Untitled Todo',
    'Untitled Idea': 'Untitled Idea',
    'Untitled Link': 'Untitled Link',
    'Untitled Event': 'Untitled Event',
    'Untitled Board': 'Untitled Board',
    'Untitled Word': 'Untitled Word',
    'Untitled Recipe': 'Untitled Recipe',
    'Untitled Movie': 'Untitled Movie',
    'Untitled Location': 'Untitled Location',
    'Untitled Objective': 'Untitled Objective',
    'Untitled File': 'Untitled File',
    'Email address': 'Email address',
    'Enter email': 'Enter email',
    'Wrong email or password': 'Wrong email or password',
    Password: 'Password',
    'Remember me': 'Remember me',
    Login: 'Login',
    'Close notification': 'Close notification',
    Success: 'Success',
    Warning: 'Warning',
    Error: 'Error',
    Info: 'Info',
    Trips: 'Trips',
    Weddings: 'Weddings',
    Filters: 'Filters',
    Reset: 'Reset',
    Delete: 'Delete',
    Name: 'Name',
    Tag: 'Tag',
    Genre: 'Genre',
    Year: 'Year',
    Status: 'Status',
    Rating: 'Rating',
    'Rating Mode': 'Rating Mode',
    'Greater than': 'Greater than',
    'Less than': 'Less than',
    'Filter by name': 'Filter by name',
    'Filter by tag': 'Filter by tag',
    'Filter by title': 'Filter by title',
    'Filter by year': 'Filter by year',
    'Filter by rating': 'Filter by rating',
    'Capture meaningful moments, lessons, and notes you want to keep close':
      'Capture meaningful moments, lessons, and notes you want to keep close',
    'Create Memory': 'Create Memory',
    '{{count}} memories': '{{count}} memories',
    'No Memories Yet': 'No Memories Yet',
    'Create your first memory to keep meaningful moments, reflections, and notes in one place.':
      'Create your first memory to keep meaningful moments, reflections, and notes in one place.',
    'Add Memory': 'Add Memory',
    'Edit Memory': 'Edit Memory',
    'Memories filters': 'Memories filters',
    'Narrow memories by name or tag': 'Narrow memories by name or tag',
    'Are you sure you want to delete this memory?':
      'Are you sure you want to delete this memory?',
    'Collect sparks, concepts, and future directions worth exploring':
      'Collect sparks, concepts, and future directions worth exploring',
    'Create Idea': 'Create Idea',
    '{{count}} ideas': '{{count}} ideas',
    'No Ideas Yet': 'No Ideas Yet',
    'Capture your first idea to start building a space for concepts, experiments, and future plans.':
      'Capture your first idea to start building a space for concepts, experiments, and future plans.',
    'Add Idea': 'Add Idea',
    'Edit Idea': 'Edit Idea',
    'Ideas filters': 'Ideas filters',
    'Refine ideas by name or tag': 'Refine ideas by name or tag',
    'Are you sure you want to delete this idea?':
      'Are you sure you want to delete this idea?',
    'Track movies, series, and animations in one place':
      'Track movies, series, and animations in one place',
    Movie: 'Movie',
    Series: 'Series',
    Animations: 'Animations',
    Animation: 'Animation',
    'Create {{item}}': 'Create {{item}}',
    '{{count}} shown in {{category}}': '{{count}} shown in {{category}}',
    Filtered: 'Filtered',
    'No Matching {{category}}': 'No Matching {{category}}',
    'No {{category}} Yet': 'No {{category}} Yet',
    'Try changing or resetting the current filters to see more results.':
      'Try changing or resetting the current filters to see more results.',
    'Create your first {{item}} to start building this list.':
      'Create your first {{item}} to start building this list.',
    'Reset Filters': 'Reset Filters',
    Previous: 'Previous',
    Next: 'Next',
    'Showing {{from}}-{{to}} of {{total}}':
      'Showing {{from}}-{{to}} of {{total}}',
    'Movies Filters': 'Movies Filters',
    '{{item}} Details': '{{item}} Details',
    'Edit {{item}}': 'Edit {{item}}',
    'Add {{item}}': 'Add {{item}}',
    'Are you sure you want to delete this {{item}}?':
      'Are you sure you want to delete this {{item}}?',
    'Narrow movies, series, and animations by key details':
      'Narrow movies, series, and animations by key details',
    Planned: 'Planned',
    Watching: 'Watching',
    Completed: 'Completed',
    'Organize your tasks across paused, active, and finished states':
      'Organize your tasks across paused, active, and finished states',
    'Create To-Do': 'Create To-Do',
    Paused: 'Paused',
    Active: 'Active',
    Finished: 'Finished',
    total: 'total',
    'Waiting or deferred': 'Waiting or deferred',
    'Current focus': 'Current focus',
    'Completed work': 'Completed work',
    'There are no active tasks yet': 'There are no active tasks yet',
    'There are no paused tasks yet': 'There are no paused tasks yet',
    'There are no finished tasks yet': 'There are no finished tasks yet',
    'Add To-Do': 'Add To-Do',
    'Edit To-Do': 'Edit To-Do',
    'Are you sure you want to delete this to-do?':
      'Are you sure you want to delete this to-do?',
    Update: 'Update',
    Create: 'Create',
    'Select genres': 'Select genres',
    '{{item}} Title': '{{item}} Title',
    'Image Link': 'Image Link',
    'Watch Link': 'Watch Link',
    Note: 'Note',
    'Rating must be a number between 0.0 and 10.0.':
      'Rating must be a number between 0.0 and 10.0.',
    'Why do you want to keep this {{item}} in the list?':
      'Why do you want to keep this {{item}} in the list?',
    Visited: 'Visited',
    Upcoming: 'Upcoming',
    'To Visit': 'To Visit',
    State: 'State',
    Location: 'Location',
    List: 'List',
    'City, place, or map link...': 'City, place, or map link...',
    'Find on map': 'Find on map',
    'Searching...': 'Searching...',
    'Map pin set': 'Map pin set',
    'Use Find on map so this trip can appear as a marker in map view.':
      'Use Find on map so this trip can appear as a marker in map view.',
    'Enter a place name or map link first.':
      'Enter a place name or map link first.',
    'No places found for this location.': 'No places found for this location.',
    'Could not look up this location. Try again.':
      'Could not look up this location. Try again.',
    'No map locations yet': 'No map locations yet',
    'Add a location to your trips so they can appear as markers on the map.':
      'Add a location to your trips so they can appear as markers on the map.',
    'Loading map markers...': 'Loading map markers...',
    'Plan and organize your travel history and future adventures.':
      'Plan and organize your travel history and future adventures.',
    'Create Trip': 'Create Trip',
    'No Trips In {{tab}}': 'No Trips In {{tab}}',
    'No Trips Yet': 'No Trips Yet',
    'No Trips Match Filters': 'No Trips Match Filters',
    'Try adjusting your filters or reset them to see all trips.':
      'Try adjusting your filters or reset them to see all trips.',
    '{{count}} trips': '{{count}} trips',
    'Trips filters': 'Trips filters',
    'Narrow trips by name or state': 'Narrow trips by name or state',
    'Add a trip and it will be categorized automatically based on its visit dates.':
      'Add a trip and it will be categorized automatically based on its visit dates.',
    'Add Trip': 'Add Trip',
    'Edit Trip': 'Edit Trip',
    'Are you sure you want to delete trip "{{name}}"?':
      'Are you sure you want to delete trip "{{name}}"?',
    'Map link': 'Map link',
    Description: 'Description',
    'Visit dates': 'Visit dates',
    'No dates selected. This trip will appear in "{{tab}}".':
      'No dates selected. This trip will appear in "{{tab}}".',
    'Image links (one URL per line)': 'Image links (one URL per line)',
    'Trip name...': 'Trip name...',
    'What is this trip about?': 'What is this trip about?',
    'Add your travel notes...': 'Add your travel notes...',
    'Open map': 'Open map',
    'View details': 'View details',
    'No notes yet': 'No notes yet',
    'Or paste an image link': 'Or paste an image link',
    'Use link': 'Use link',
    'Additional images (one URL per line)':
      'Additional images (one URL per line)',
    'Upload PDFs, videos, images, and other trip files.':
      'Upload PDFs, videos, images, and other trip files.',
    'No files yet': 'No files yet',
    'Failed to upload file.': 'Failed to upload file.',
    'Select a file first.': 'Select a file first.',
    'Add file': 'Add file',
    '{{count}} images': '{{count}} images',
    '{{count}} notes': '{{count}} notes',
    '{{count}} dates': '{{count}} dates',
    'Organize and manage your important links and resources':
      'Organize and manage your important links and resources',
    Link: 'Link',
    Folder: 'Folder',
    Board: 'Board',
    'Add Link': 'Add Link',
    'Add Folder': 'Add Folder',
    'Go back': 'Go back',
    Back: 'Back',
    '{{count}} items': '{{count}} items',
    'No Links Yet': 'No Links Yet',
    'Add your first link or folder to start organizing useful resources in one place.':
      'Add your first link or folder to start organizing useful resources in one place.',
    URL: 'URL',
    'link name...': 'link name...',
    'folder name...': 'folder name...',
    'link url...': 'link url...',
    'Save places, routes, and addresses you want to revisit':
      'Save places, routes, and addresses you want to revisit',
    'Create Location': 'Create Location',
    'No Locations Yet': 'No Locations Yet',
    'Create your first location to keep addresses, destinations, and useful places easy to find.':
      'Create your first location to keep addresses, destinations, and useful places easy to find.',
    'Add Location': 'Add Location',
    'Edit Location': 'Edit Location',
    'Are you really want to delete this Location?':
      'Are you really want to delete this Location?',
    Address: 'Address',
    Map: 'Map',
    City: 'City',
    Type: 'Type',
    'Location name...': 'Location name...',
    'Location address...': 'Location address...',
    'Location map...': 'Location map...',
    'Add an address or map link to show this place on the map.':
      'Add an address or map link to show this place on the map.',
    'Location city...': 'Location city...',
    'Location description...': 'Location description...',
    'Home-Address': 'Home-Address',
    'Work-Address': 'Work-Address',
    Hotel: 'Hotel',
    Restaurant: 'Restaurant',
    Pizzeria: 'Pizzeria',
    'Fast-Food': 'Fast-Food',
    Bar: 'Bar',
    Pub: 'Pub',
    Cafe: 'Cafe',
    Opera: 'Opera',
    Theater: 'Theater',
    Cinema: 'Cinema',
    Club: 'Club',
    Disco: 'Disco',
    'Tourist-Attraction': 'Tourist-Attraction',
    Beach: 'Beach',
    Other: 'Other',
    'Start at': 'Start at',
    'End at': 'End at',
    Priority: 'Priority',
    High: 'High',
    Medium: 'Medium',
    Low: 'Low',
    'To-Do name...': 'To-Do name...',
    'To-Do description...': 'To-Do description...',
    'Due today': 'Due today',
    Overdue: 'Overdue',
    'No deadline': 'No deadline',
    'No active ToDos': 'No active ToDos',
    'Create a new to-do to get started': 'Create a new to-do to get started',
    'Create and manage your whiteboards': 'Create and manage your whiteboards',
    'Create New Folder': 'Create New Folder',
    'New Folder': 'New Folder',
    'Create New Whiteboard': 'Create New Whiteboard',
    'New Board': 'New Board',
    'Filter By': 'Filter By',
    'Sort By': 'Sort By',
    Date: 'Date',
    Order: 'Order',
    Show: 'Show',
    'Folders only': 'Folders only',
    'Boards only': 'Boards only',
    'A - Z': 'A - Z',
    'Z - A': 'Z - A',
    'Oldest - Newest': 'Oldest - Newest',
    'Newest - Oldest': 'Newest - Oldest',
    Root: 'Root',
    'Delete folder': 'Delete folder',
    'Folder must be empty to delete': 'Folder must be empty to delete',
    'No Whiteboards Yet': 'No Whiteboards Yet',
    'Create your first whiteboard to get started':
      'Create your first whiteboard to get started',
    'Add Whiteboard': 'Add Whiteboard',
    'Edit Whiteboard': 'Edit Whiteboard',
    'Folder name is required': 'Folder name is required',
    'Could not create folder. Please try again.':
      'Could not create folder. Please try again.',
    'Could not update folder. Please try again.':
      'Could not update folder. Please try again.',
    'Create Folder': 'Create Folder',
    'Folder name...': 'Folder name...',
    'Edit Folder': 'Edit Folder',
    'Move to folder': 'Move to folder',
    Save: 'Save',
    'Are you really want to delete this Whiteboard?':
      'Are you really want to delete this Whiteboard?',
    'Delete folder "{{name}}"?': 'Delete folder "{{name}}"?',
    'Whiteboard name...': 'Whiteboard name...',
    'Manage and organize your uploaded files':
      'Manage and organize your uploaded files',
    'Upload File': 'Upload File',
    'Available Space': 'Available Space',
    Size: 'Size',
    'Smallest - Largest': 'Smallest - Largest',
    'Largest - Smallest': 'Largest - Smallest',
    Visibility: 'Visibility',
    Public: 'Public',
    Private: 'Private',
    'This folder is empty': 'This folder is empty',
    'Create a folder or upload a file here':
      'Create a folder or upload a file here',
    'Are you sure you want to delete this file?':
      'Are you sure you want to delete this file?',
    'Create folder in {{name}}': 'Create folder in {{name}}',
    'Create folder in Root': 'Create folder in Root',
    'Folder name': 'Folder name',
    'Type folder name...': 'Type folder name...',
    'Creating...': 'Creating...',
    'Upload file to {{name}}': 'Upload file to {{name}}',
    'Upload file to Root': 'Upload file to Root',
    'Edit file: {{name}}': 'Edit file: {{name}}',
    'Edit file': 'Edit file',
    'File name is required': 'File name is required',
    'Could not update file. Please try again.':
      'Could not update file. Please try again.',
    'Type file name...': 'Type file name...',
    'file description...': 'file description...',
    'Public file (accessible via direct link)':
      'Public file (accessible via direct link)',
    'When disabled, file is only accessible through the app preview.':
      'When disabled, file is only accessible through the app preview.',
    'Saving...': 'Saving...',
    'Text File Preview': 'Text File Preview',
    'Audio Preview': 'Audio Preview',
    'Loading audio...': 'Loading audio...',
    'Failed to load audio': 'Failed to load audio',
    'Office Preview': 'Office Preview',
    'Archive Preview': 'Archive Preview',
    'Loading document...': 'Loading document...',
    'Failed to load document': 'Failed to load document',
    'This file type cannot be previewed': 'This file type cannot be previewed',
    'Empty archive': 'Empty archive',
    'Failed to open archive': 'Failed to open archive',
    Extract: 'Extract',
    'This archive is password-protected': 'This archive is password-protected',
    'Open archive': 'Open archive',
    'Edit folder: {{name}}': 'Edit folder: {{name}}',
    'Edit folder': 'Edit folder',
    'Name is required': 'Name is required',
    Title: 'Title',
    'Select File': 'Select File',
    Upload: 'Upload',
    'File is required': 'File is required',
    'file name...': 'file name...',
    Download: 'Download',
    'Copied!': 'Copied!',
    'Copy link': 'Copy link',
    'Public file': 'Public file',
    'Private file': 'Private file',
    Edit: 'Edit',
    'Configure workspace-level sections from one place.':
      'Configure workspace-level sections from one place.',
    'Settings under development': 'Settings under development',
    Sections: 'Sections',
    'Workspace Settings': 'Workspace Settings',
    Users: 'Users',
    'Manage members, roles, and access for this workspace.':
      'Manage members, roles, and access for this workspace.',
    'Failed to load users.': 'Failed to load users.',
    'User created successfully.': 'User created successfully.',
    'Failed to update user.': 'Failed to update user.',
    'Failed to create user.': 'Failed to create user.',
    'You cannot delete your own user.': 'You cannot delete your own user.',
    'Failed to delete user.': 'Failed to delete user.',
    'Create User': 'Create User',
    'Workspace members': 'Workspace members',
    'Users directory': 'Users directory',
    'Review all users, update access, and create new accounts from this section.':
      'Review all users, update access, and create new accounts from this section.',
    'Loading users...': 'Loading users...',
    'No users yet': 'No users yet',
    'Create the first user for this workspace.':
      'Create the first user for this workspace.',
    Blocked: 'Blocked',
    Created: 'Created',
    'Edit user': 'Edit user',
    'Delete user': 'Delete user',
    User: 'User',
    Admin: 'Admin',
    Hide: 'Hide',
    View: 'View',
    'Edit User': 'Edit User',
    'First name, last name, and email are required.':
      'First name, last name, and email are required.',
    'Enter a valid email address.': 'Enter a valid email address.',
    'Password is required when creating a user.':
      'Password is required when creating a user.',
    'Repeat password is required when creating a user.':
      'Repeat password is required when creating a user.',
    'Password and repeat password are required.':
      'Password and repeat password are required.',
    'Password and repeat password must match.':
      'Password and repeat password must match.',
    'First Name': 'First Name',
    'Last Name': 'Last Name',
    'First name': 'First name',
    'Last name': 'Last name',
    Email: 'Email',
    Role: 'Role',
    Modules: 'Modules',
    Code: 'Code',
    Permission: 'Permission',
    'Set all': 'Set all',
    'Enable private mode for this user': 'Enable private mode for this user',
    'When private mode is on, only public modules are visible to this user.':
      'When private mode is on, only public modules are visible to this user.',
    'You cannot change your own role or blocked status.':
      'You cannot change your own role or blocked status.',
    'New Password': 'New Password',
    'Leave blank to keep current password':
      'Leave blank to keep current password',
    'Repeat New Password': 'Repeat New Password',
    'Repeat the new password': 'Repeat the new password',
    'Repeat Password': 'Repeat Password',
    'Set each module to hide, view, or edit for this user.':
      'Set each module to hide, view, or edit for this user.',
    'Admin users keep full access across every module.':
      'Admin users keep full access across every module.',
    'Update User': 'Update User',
    Add: 'Add',
    Word: 'Word',
    Done: 'Done',
    DeadLine: 'Deadline',
    'Select date': 'Select date',
    'Choose date': 'Choose date',
    'dd/mm/yyyy': 'dd/mm/yyyy',
    'Invalid date': 'Invalid date',
    Periodicity: 'Periodicity',
    Notification: 'Notification',
    'Enable notification': 'Enable notification',
    'Plan and manage your events': 'Plan and manage your events',
    'Add Event': 'Add Event',
    'Update Event': 'Update Event',
    'No Events Yet': 'No Events Yet',
    'Create Event': 'Create Event',
    'Create your first event to start planning your schedule.':
      'Create your first event to start planning your schedule.',
    'event name...': 'event name...',
    'event description...': 'event description...',
    'event note...': 'event note...',
    Once: 'Once',
    'Every Year': 'Every Year',
    'By Dates': 'By Dates',
    Period: 'Period',
    'To Do': 'To Do',
    Birthday: 'Birthday',
    Death: 'Death',
    Vacation: 'Vacation',
    From: 'From',
    to: 'to',
    'Are you really want to delete this event?':
      'Are you really want to delete this event?',
    'Learn and manage your vocabulary': 'Learn and manage your vocabulary',
    'New Vocabulary': 'New Vocabulary',
    'Select Vocabulary': 'Select Vocabulary',
    'Choose a vocabulary...': 'Choose a vocabulary...',
    'Add word': 'Add word',
    'Add Word': 'Add Word',
    'Delete vocabulary': 'Delete vocabulary',
    'Show translations': 'Show translations',
    'Show words': 'Show words',
    Words: 'Words',
    '{{count}} words': '{{count}} words',
    'No words yet': 'No words yet',
    'Start building your vocabulary by adding your first word':
      'Start building your vocabulary by adding your first word',
    'Add First Word': 'Add First Word',
    'Select a vocabulary to start': 'Select a vocabulary to start',
    'Choose a vocabulary from the dropdown above or create a new one':
      'Choose a vocabulary from the dropdown above or create a new one',
    'Add New Vocabulary': 'Add New Vocabulary',
    'Add new vocabulary': 'Add new vocabulary',
    'Edit Vocabulary': 'Edit Vocabulary',
    'Update vocabulary': 'Update vocabulary',
    'Add New Word': 'Add New Word',
    'Add word to {{name}}': 'Add word to {{name}}',
    'Edit Word': 'Edit Word',
    'Update word in {{name}}': 'Update word in {{name}}',
    'Are you sure you want to delete this vocabulary? All words will be lost.':
      'Are you sure you want to delete this vocabulary? All words will be lost.',
    'Are you sure you want to delete this word?':
      'Are you sure you want to delete this word?',
    'Vocabulary Name': 'Vocabulary Name',
    'Enter vocabulary name...': 'Enter vocabulary name...',
    'Words Voice Language': 'Words Voice Language',
    'Select language for words': 'Select language for words',
    'Translations Voice Language': 'Translations Voice Language',
    'Select language for translations': 'Select language for translations',
    'Create Vocabulary': 'Create Vocabulary',
    'Update Vocabulary': 'Update Vocabulary',
    'Enter word...': 'Enter word...',
    'Enter translation...': 'Enter translation...',
    'Dictionary (Optional)': 'Dictionary (Optional)',
    'Add definition or notes...': 'Add definition or notes...',
    'Update Word': 'Update Word',
    'This word already exists in this vocabulary.':
      'This word already exists in this vocabulary.',
    'Listen to word': 'Listen to word',
    'Listen to translation': 'Listen to translation',
    'Enable word pronunciation': 'Enable word pronunciation',
    'Disable word pronunciation': 'Disable word pronunciation',
    'Copy to clipboard': 'Copy to clipboard',
    'More options': 'More options',
    Move: 'Move',
    'Move to vocabulary': 'Move to vocabulary',
    'No other vocabularies': 'No other vocabularies',
    'Track and manage your goals': 'Track and manage your goals',
    'Create New Objective': 'Create New Objective',
    'New Objective': 'New Objective',
    'No active objectives yet': 'No active objectives yet',
    'Create your first objective': 'Create your first objective',
    'No paused objectives': 'No paused objectives',
    'No completed objectives yet': 'No completed objectives yet',
    'Add Objective': 'Add Objective',
    'Edit Objective': 'Edit Objective',
    'Are you really want to delete this Objective?':
      'Are you really want to delete this Objective?',
    'Are you really want to delete this objective?':
      'Are you really want to delete this objective?',
    'Are you really want to delete this ToDo?':
      'Are you really want to delete this ToDo?',
    'Objective name...': 'Objective name...',
    'Parent objective': 'Parent objective',
    'Child objectives': 'Child objectives',
    'parent objective': 'parent objective',
    'No parent': 'No parent',
    'Select Priority': 'Select Priority',
    'Select Status': 'Select Status',
    'Objective description...': 'Objective description...',
    Passive: 'Paused',
    'Created at:': 'Created at:',
    'Finished at:': 'Finished at:',
    'Not finished yet': 'Not finished yet',
    'Leave a comment here': 'Leave a comment here',
    Steps: 'Steps',
    Step: 'Step',
    'Start Date': 'Start Date',
    'End Date': 'End Date',
    'Add Step': 'Add Step',
    Progress: 'Progress',
    'No steps yet': 'No steps yet',
    'Add a step...': 'Add a step...',
    'Search objectives...': 'Search objectives...',
    'All priorities': 'All priorities',
    'Deadline (newest)': 'Deadline (newest)',
    'Deadline (oldest)': 'Deadline (oldest)',
    Activate: 'Activate',
    Pause: 'Pause',
    Play: 'Play',
    Complete: 'Complete',
    'Toggle step': 'Toggle step',
    'Toggle to-do': 'Toggle to-do',
    'Update Step': 'Update Step',
    'Are you really want to delete this step?':
      'Are you really want to delete this step?',
    'This step cannot be deleted because it still has to-dos. Remove or complete all to-dos in this step first.':
      'This step cannot be deleted because it still has to-dos. Remove or complete all to-dos in this step first.',
    'Objective name is required': 'Objective name is required',
    "Objective deadLine can't be earlier then current day":
      "Objective deadLine can't be earlier then current day",
    'Priority is required': 'Priority is required',
    'No whiteboards yet': 'No whiteboards yet',
    'No to-dos yet': 'No to-dos yet',
    'Browse all scenarios and open details in the drawer.':
      'Browse all scenarios and open details in the drawer.',
    'Create Scenario': 'Create Scenario',
    'Loading scenarios...': 'Loading scenarios...',
    'No Scenarios Yet': 'No Scenarios Yet',
    'Create your first scenario to start organizing story flows and reusable scripts.':
      'Create your first scenario to start organizing story flows and reusable scripts.',
    'No image': 'No image',
    'Failed to load scenarios.': 'Failed to load scenarios.',
    'Failed to load scenario details.': 'Failed to load scenario details.',
    'Failed to delete scenario.': 'Failed to delete scenario.',
    'Are you sure you want to delete this scenario?':
      'Are you sure you want to delete this scenario?',
    'Failed to load scenario.': 'Failed to load scenario.',
    'Name is required.': 'Name is required.',
    'Failed to save scenario.': 'Failed to save scenario.',
    'Loading scenario...': 'Loading scenario...',
    'Edit Scenario': 'Edit Scenario',
    'Manage scenario base fields and nested objects.':
      'Manage scenario base fields and nested objects.',
    'Scenario name': 'Scenario name',
    Text: 'Text',
    'Scenario text': 'Scenario text',
    'Scenario description': 'Scenario description',
    'Main image': 'Main image',
    Gallery: 'Gallery',
    'Add gallery image': 'Add gallery image',
    Places: 'Places',
    'No places added yet.': 'No places added yet.',
    Characters: 'Characters',
    'No characters added yet.': 'No characters added yet.',
    'Scenario Events': 'Scenario Events',
    'No events added yet.': 'No events added yet.',
    Artifacts: 'Artifacts',
    Expand: 'Expand',
    Collapse: 'Collapse',
    'No artifacts added yet.': 'No artifacts added yet.',
    'No whiteboards added yet.': 'No whiteboards added yet.',
    'World Maps': 'World Maps',
    'No maps added yet.': 'No maps added yet.',
    'Untitled map': 'Untitled map',
    'Map name': 'Map name',
    Land: 'Land',
    'Paint land': 'Paint land',
    'Outline part of the land to recolor, then press Enter or Finish shape.':
      'Outline part of the land to recolor, then press Enter or Finish shape.',
    Lake: 'Lake',
    River: 'River',
    Road: 'Road',
    Mountain: 'Mountain',
    Forest: 'Forest',
    Castle: 'Castle',
    Village: 'Village',
    'Select tool': 'Select',
    'Pan tool': 'Pan',
    'Finish shape': 'Finish shape',
    'Delete selected': 'Delete selected',
    'Failed to save map.': 'Failed to save map.',
    'Loading map...': 'Loading map...',
    'Map not found.': 'Map not found.',
    'Delete this map?': 'Delete this map?',
    'Delete this whiteboard?': 'Delete this whiteboard?',
    'Click to add points, then press Enter or Finish shape.':
      'Click to add points, then press Enter or Finish shape.',
    'Click the map to place this object.': 'Click the map to place this object.',
    'Stamp several times and overlap them, working from back to front.':
      'Stamp several times and overlap them, working from back to front.',
    'Each click uses a different shape. Overlap them from back to front.':
      'Each click uses a different shape. Overlap them from back to front.',
    'Drag objects to move them. Scroll to zoom.':
      'Drag objects to move them. Scroll to zoom.',
    'Click to outline an area, then press Enter or Finish shape.':
      'Click to outline an area, then press Enter or Finish shape.',
    'Outline the forest, then press Enter or Finish shape. Trees will mix automatically.':
      'Outline the forest, then press Enter or Finish shape. Trees will mix automatically.',
    'Outline the mountains, then press Enter or Finish shape.':
      'Outline the mountains, then press Enter or Finish shape.',
    'Choose a mountain type, then outline the range and press Enter or Finish shape.':
      'Choose a mountain type, then outline the range and press Enter or Finish shape.',
    'Click an object to erase it.': 'Click an object to erase it.',
    Eraser: 'Eraser',
    'Forest fill': 'Forest fill',
    'Mountain fill': 'Mountain fill',
    'Snow peaks': 'Snow peaks',
    'Grey peaks': 'Grey peaks',
    'Rocky peaks': 'Rocky peaks',
    Sparse: 'Sparse',
    Dense: 'Dense',
    Normal: 'Normal',
    Hills: 'Hills',
    Desert: 'Desert',
    Swamp: 'Swamp',
    Snow: 'Snow',
    Woods: 'Woods',
    Volcano: 'Volcano',
    Hill: 'Hill',
    Sea: 'Sea',
    Trail: 'Trail',
    Border: 'Border',
    Wall: 'Wall',
    Bridge: 'Bridge',
    Town: 'Town',
    Port: 'Port',
    Fortress: 'Fortress',
    Tower: 'Tower',
    Monastery: 'Monastery',
    Ruins: 'Ruins',
    Farm: 'Farm',
    Mine: 'Mine',
    Camp: 'Camp',
    Lighthouse: 'Lighthouse',
    Label: 'Label',
    Navigate: 'Navigate',
    Terrain: 'Terrain',
    Routes: 'Routes',
    Settlements: 'Settlements',
    Marks: 'Marks',
    Grid: 'Grid',
    'Fit map': 'Fit map',
    'Map size': 'Map size',
    Small: 'Small',
    Large: 'Large',
    Huge: 'Huge',
    Width: 'Width',
    Height: 'Height',
    Pines: 'Pines',
    Tree: 'Tree',
    Pine: 'Pine',
    Compass: 'Compass',
    'Scale bar': 'Scale bar',
    Ship: 'Ship',
    Waves: 'Waves',
    Cave: 'Cave',
    Waterfall: 'Waterfall',
    Oasis: 'Oasis',
    'Standing stones': 'Standing stones',
    Windmill: 'Windmill',
    Dunes: 'Dunes',
    'Draw a continent or island coast, then press Enter or Finish shape.':
      'Draw a continent or island coast, then press Enter or Finish shape.',
    'Mark a rough land border, then Shape coast or Finish shape.':
      'Mark a rough land border, then Shape coast or Finish shape.',
    'Mark a rough sea border, then Shape coast or Finish shape.':
      'Mark a rough sea border, then Shape coast or Finish shape.',
    'Mark a rough lake border, then Shape coast or Finish shape.':
      'Mark a rough lake border, then Shape coast or Finish shape.',
    'Mark a rough forest border, then Shape edge or Finish shape.':
      'Mark a rough forest border, then Shape edge or Finish shape.',
    'Choose a mountain type, then mark a rough range and Shape edge or Finish shape.':
      'Choose a mountain type, then mark a rough range and Shape edge or Finish shape.',
    'Shape coast': 'Shape coast',
    'Shaping coast...': 'Shaping coast...',
    'Shape edge': 'Shape edge',
    'Shaping edge...': 'Shaping edge...',
    'From image': 'From image',
    'Reading map...': 'Reading map...',
    'Failed to read map image.': 'Failed to read map image.',
    'Map rebuilt from the image. You can undo.':
      'Map rebuilt from the image. You can undo.',
    Duplicate: 'Duplicate',
    Properties: 'Properties',
    'Fill color': 'Fill color',
    'Custom color': 'Custom color',
    'Default colors': 'Default colors',
    Color: 'Color',
    Scale: 'Scale',
    Rotation: 'Rotation',
    'Linked place': 'Linked place',
    Rotate: 'Rotate',
    Symbol: 'Symbol',
    'Next symbol': 'Next symbol',
    'Choose a symbol': 'Choose a symbol',
    'Choose a symbol, then click the map to place it.':
      'Choose a symbol, then click the map to place it.',
    'Bring forward': 'Bring forward',
    'Send back': 'Send back',
    'Select an object to edit its name, size and rotation.':
      'Select an object to edit its name, size and rotation.',
    'Update Scenario': 'Update Scenario',
    'Delete this item?': 'Delete this item?',
    'Scenario details': 'Scenario details',
    'Select a scenario.': 'Select a scenario.',
    'Created:': 'Created:',
    'No text.': 'No text.',
    'No description.': 'No description.',
    'No description': 'No description',
    Place: 'Place',
    Event: 'Event',
    Character: 'Character',
    Artifact: 'Artifact',
    Image: 'Image',
    'No place': 'No place',
    'Choose image file': 'Choose image file',
    'Failed to upload image.': 'Failed to upload image.',
    'Uploading...': 'Uploading...',
    Remove: 'Remove',
    'No gallery images.': 'No gallery images.',
    'Create New Purchase list': 'Create New Purchase list',
    'No Purchase Lists Yet': 'No Purchase Lists Yet',
    'Your purchase lists will appear here once this section starts being used.':
      'Your purchase lists will appear here once this section starts being used.',
    Participants: 'Participants',
    'Loading participants...': 'Loading participants...',
    'This event is currently blocked.': 'This event is currently blocked.',
    'Event not found. Please check the key.':
      'Event not found. Please check the key.',
    'Please enter an event key.': 'Please enter an event key.',
    'Private List': 'Private List',
    'Please enter the event key to view the participant list.':
      'Please enter the event key to view the participant list.',
    'Enter key': 'Enter key',
    'Checking...': 'Checking...',
    'Access List': 'Access List',
    Invited: 'Invited',
    Attending: 'Attending',
    'Not attending': 'Not attending',
    Gifts: 'Gifts',
    'Husband Side': 'Husband Side',
    'Wife Side': 'Wife Side',
    'No participants on this side.': 'No participants on this side.',
    Gift: 'Gift',
    Yes: 'Yes',
    No: 'No',
    Tags: 'Tags',
    Ingredients: 'Ingredients',
    'No ingredients': 'No ingredients',
    '{{count}} ingredients': '{{count}} ingredients',
    'type ingredient': 'type ingredient',
    'Keep your favorite meals, ingredients, and cooking notes in one place':
      'Keep your favorite meals, ingredients, and cooking notes in one place',
    'Create Recipe': 'Create Recipe',
    'Create Menu': 'Create Menu',
    'Add Menu': 'Add Menu',
    'Edit Menu': 'Edit Menu',
    'Menu name...': 'Menu name...',
    'Select recipes': 'Select recipes',
    '{{count}} recipes': '{{count}} recipes',
    'Are you really want to delete this Menu?':
      'Are you really want to delete this Menu?',
    'Group recipes into shared meals.': 'Group recipes into shared meals.',
    'No Recipes Yet': 'No Recipes Yet',
    'No Menus Yet': 'No Menus Yet',
    'Create a menu and pick recipes to group meals together.':
      'Create a menu and pick recipes to group meals together.',
    'Create your first recipe to keep ingredients, cooking steps, and meal ideas together.':
      'Create your first recipe to keep ingredients, cooking steps, and meal ideas together.',
    'Add Recipe': 'Add Recipe',
    'Edit Recipe': 'Edit Recipe',
    'Are you really want to delete this Recipe?':
      'Are you really want to delete this Recipe?',
    'Recipe name...': 'Recipe name...',
    Total: 'Total',
    Participating: 'Participating',
    'Not participating': 'Not participating',
    'Manage confirmations and guest list':
      'Manage confirmations and guest list',
    Rest: 'Rest',
    'Husband side participants': 'Husband side participants',
    'No Husband Side Participants': 'No Husband Side Participants',
    'Participants for the husband side will appear here once people respond or are added.':
      'Participants for the husband side will appear here once people respond or are added.',
    'Husband side': 'Husband side',
    'Wife side participants': 'Wife side participants',
    'No Wife Side Participants': 'No Wife Side Participants',
    'Participants for the wife side will appear here once people respond or are added.':
      'Participants for the wife side will appear here once people respond or are added.',
    'Wife side': 'Wife side',
    'Are you really want to delete this Participant?':
      'Are you really want to delete this Participant?',
    daysShort: 'days',
    hoursShort: 'hrs',
    minutesShort: 'min',
    secondsShort: 'sec',
    'Loading invitation...': 'Loading invitation...',
    'Wedding event not found': 'Wedding event not found',
    'We warmly invite you': 'We warmly invite you',
    'to our wedding ceremony': 'to our wedding ceremony',
    'Respectfully,': 'Respectfully,',
    'Time left': 'Time left',
    Schedule: 'Schedule',
    'How to get there': 'How to get there',
    'Please confirm your attendance': 'Please confirm your attendance',
    'Thank you': 'Thank you',
    'Please fill in the field': 'Please fill in the field',
    'We will attend': 'We will attend',
    'We cannot attend': 'We cannot attend',
    'Guest count': 'Guest count',
    Send: 'Send',
    'Contact us': 'Contact us',
    'Wedding Events': 'Wedding Events',
    'Manage invitations, schedules, and event access from one place.':
      'Manage invitations, schedules, and event access from one place.',
    'Create New Event': 'Create New Event',
    'Total events': 'Total events',
    'Gift info enabled': 'Gift info enabled',
    'Edit Wedding Event': 'Edit Wedding Event',
    'Create New Wedding Event': 'Create New Wedding Event',
    'Event Slug (URL-friendly)': 'Event Slug (URL-friendly)',
    'e.g., arman-armine-2025': 'e.g., arman-armine-2025',
    'This will be used in the URL: /wedding/[slug]/invitation':
      'This will be used in the URL: /wedding/[slug]/invitation',
    'Event Title': 'Event Title',
    'e.g., Arman & Armine Wedding': 'e.g., Arman & Armine Wedding',
    'Husband Name': 'Husband Name',
    'e.g., Arman': 'e.g., Arman',
    'Wife Name': 'Wife Name',
    'e.g., Armine': 'e.g., Armine',
    'Wedding Date': 'Wedding Date',
    'Hero Image URL (Optional)': 'Hero Image URL (Optional)',
    'If provided, this image will replace the default wedding hero image on the invitation page':
      'If provided, this image will replace the default wedding hero image on the invitation page',
    Configuration: 'Configuration',
    'Block Public Access': 'Block Public Access',
    'When enabled, public access to this wedding will be blocked, but it will still be visible in the main list.':
      'When enabled, public access to this wedding will be blocked, but it will still be visible in the main list.',
    'Show Gift Information': 'Show Gift Information',
    'When enabled, users will be able to see gift information on public pages.':
      'When enabled, users will be able to see gift information on public pages.',
    'Location {{index}}': 'Location {{index}}',
    'Venue Name': 'Venue Name',
    'e.g., Ceremony': 'e.g., Ceremony',
    'Place Name': 'Place Name',
    'e.g., Saint Cross Church': 'e.g., Saint Cross Church',
    'Icon Type': 'Icon Type',
    'Select icon type...': 'Select icon type...',
    'Map Link': 'Map Link',
    'Image URL (Optional)': 'Image URL (Optional)',
    'No Wedding Events Yet': 'No Wedding Events Yet',
    'Create your first wedding event to manage invitations, schedules, and participants from one place.':
      'Create your first wedding event to manage invitations, schedules, and participants from one place.',
    'Public blocked': 'Public blocked',
    'Public active': 'Public active',
    'Gift info on': 'Gift info on',
    'Gift info off': 'Gift info off',
    '{{count}} locations': '{{count}} locations',
    'Copy URL': 'Copy URL',
    'View Invitation': 'View Invitation',
    'Copy Participants URL': 'Copy Participants URL',
    Slug: 'Slug',
    'Invitation URL': 'Invitation URL',
    at: 'at',
    'Are you sure you want to delete "{{title}}"? This action cannot be undone.':
      'Are you sure you want to delete "{{title}}"? This action cannot be undone.',
  },
  hy: {
    appName: 'IVA',
    Home: 'Գլխավոր',
    Notes: 'Նշումներ',
    Memories: 'Հիշողություններ',
    'To-Dos': 'Անելիքներ',
    Ideas: 'Գաղափարներ',
    Links: 'Հղումներ',
    Recipes: 'Բաղադրատոմսեր',
    Recipe: 'Բաղադրատոմս',
    Food: 'Սնունդ',
    Menus: 'Մենյուներ',
    Menu: 'Մենյու',
    Movies: 'Ֆիլմեր',
    Locations: 'Վայրեր',
    Objectives: 'Նպատակներ',
    Objective: 'Նպատակ',
    Events: 'Իրադարձություններ',
    Translation: 'Թարգմանություն',
    Whiteboards: 'Գրատախտակներ',
    Files: 'Ֆայլեր',
    Lunar: 'Lunar',
    Recordings: 'Ձայնագրություններ',
    'Microphone recordings from Lunar, grouped by user.':
      'Lunar-ի միկրոֆոնի ձայնագրությունները՝ խմբավորված ըստ օգտատիրոջ։',
    '{{count}} recordings': '{{count}} ձայնագրություն',
    'No recordings yet': 'Ձայնագրություններ դեռ չկան',
    'This user has no recordings yet.': 'Այս օգտատերը դեռ ձայնագրություն չունի։',
    'Could not load recordings.': 'Չհաջողվեց բեռնել ձայնագրությունները։',
    'Loading recordings...': 'Ձայնագրությունները բեռնվում են...',
    'Unknown user': 'Անհայտ օգտատեր',
    'Are you sure you want to delete this recording?':
      'Վստա՞հ եք, որ ուզում եք ջնջել այս ձայնագրությունը։',
    'Could not delete the recording.': 'Չհաջողվեց ջնջել ձայնագրությունը։',
    Scenarios: 'Սցենարներ',
    Settings: 'Կարգավորումներ',
    Purchases: 'Գնումներ',
    Income: 'Եկամուտ',
    Regular: 'Կանոնավոր',
    Expected: 'Սպասվող',
    Received: 'Ստացված',
    'Income Date': 'Եկամտի ամսաթիվ',
    Source: 'Աղբյուր',
    'Create Income': 'Ստեղծել եկամուտ',
    'Add Income': 'Ավելացնել եկամուտ',
    'Edit Income': 'Խմբագրել եկամուտը',
    'Income Details': 'Եկամտի մանրամասներ',
    'No Income Yet': 'Եկամուտ դեռ չկա',
    '{{count}} incomes': '{{count}} եկամուտ',
    'Income filters': 'Եկամտի ֆիլտրեր',
    'Narrow income by name or date':
      'Ֆիլտրել եկամուտը անունով կամ ամսաթվով',
    'No matching income': 'Համընկնող եկամուտ չկա',
    'Try a different name or date range.':
      'Փորձեք այլ անուն կամ ամսաթվերի միջակայք։',
    'Track salary, bonuses, and other inflows in one place.':
      'Հետևեք աշխատավարձին, բոնուսներին և այլ մուտքերին մեկ տեղում։',
    'Add regular income such as salary, or occasional inflows like bonuses and gifts.':
      'Ավելացրեք կանոնավոր եկամուտ՝ աշխատավարձ, կամ առանձին մուտքեր՝ բոնուս և նվեր։',
    'Enter income source': 'Մուտքագրեք եկամտի աղբյուրը',
    'Enter amount': 'Մուտքագրեք գումարը',
    'Gross salary': 'Համախառն աշխատավարձ',
    'Net salary': 'Զուտ աշխատավարձ',
    'Enter gross salary': 'Մուտքագրեք համախառն աշխատավարձը',
    'Enter net salary': 'Մուտքագրեք զուտ աշխատավարձը',
    'Scan Payslip': 'Սկանավորել աշխատավարձի թերթիկը',
    'Upload a payslip image or PDF to autofill employer, net pay, date, and type. The file is saved with the income. Manual edit still works.':
      'Վերբեռնեք աշխատավարձի թերթիկի նկար կամ PDF՝ գործատուին, զուտ գումարը, ամսաթիվը և տեսակը լրացնելու համար։ Ֆայլը պահվում է եկամտի հետ։ Կարելի է նաև լրացնել ձեռքով։',
    'Payslip preview': 'Աշխատավարձի թերթիկի նախադիտում',
    'Payslip document': 'Աշխատավարձի թերթիկ',
    'Choose payslip file': 'Ընտրել աշխատավարձի թերթիկի ֆայլ',
    'Clear File': 'Մաքրել ֆայլը',
    'Payslip analyzed and filled': 'Աշխատավարձի թերթիկը վերլուծվեց և լրացվեց',
    'Payslip analyzed. Review fields, then save.':
      'Աշխատավարձի թերթիկը վերլուծվեց։ Ստուգեք դաշտերը և պահպանեք։',
    'Failed to analyze payslip.': 'Չհաջողվեց վերլուծել աշխատավարձի թերթիկը։',
    Salary: 'Աշխատավարձ',
    Freelance: 'Ֆրիլանս',
    Business: 'Բիզնես',
    Investment: 'Ներդրում',
    Dividends: 'Շահաբաժիններ',
    Rental: 'Վարձակալություն',
    Pension: 'Կենսաթոշակ',
    Refund: 'Վերադարձ',
    Bonus: 'Բոնուս',
    Daily: 'Ամենօրյա',
    Analytics: 'Վերլուծություն',
    'Spend analytics': 'Ծախսերի վերլուծություն',
    'Income analytics': 'Եկամտի վերլուծություն',
    Comparison: 'Համեմատություն',
    'Utility bills': 'Կոմունալ վճարներ',
    'Add Bill': 'Ավելացնել վճար',
    'Bill Date': 'Վճարման ամսաթիվ',
    'Bill Cost': 'Վճարի գումար',
    'Company Name': 'Ընկերության անուն',
    Provider: 'Մատակարար',
    'Counter m³': 'Հաշվիչ մ³',
    'Month spend in m³': 'Ամսական ծախս մ³',
    'Price per kW': 'Գին kW-ի համար',
    'Month cost': 'Ամսական արժեք',
    'Month spend in m³ × {{factor}} × Price per kW':
      'Ամսական ծախս մ³ × {{factor}} × գին kW-ի համար',
    'Bill document': 'Վճարի փաստաթուղթ',
    'Choose bill file': 'Ընտրել վճարի ֆայլ',
    'Upload an image or PDF of the bill.':
      'Վերբեռնեք վճարի նկար կամ PDF։',
    'Remove file': 'Հեռացնել ֆայլը',
    'Scan gas bill': 'Սկանավորել գազի վճարը',
    'Upload an image or PDF of the bill to autofill provider, readings, and prices.':
      'Վերբեռնեք վճարի նկար կամ PDF՝ մատակարարը, ցուցմունքները և գները լրացնելու համար։',
    'Gas bill analyzed and filled': 'Գազի վճարը վերլուծվեց և լրացվեց',
    'Gas bill analyzed. Review fields, then save.':
      'Գազի վճարը վերլուծվեց։ Ստուգեք դաշտերը և պահպանեք։',
    'Failed to analyze gas bill.': 'Չհաջողվեց վերլուծել գազի վճարը։',
    'Choose bill files': 'Ընտրել վճարի ֆայլեր',
    'Add bill files': 'Ավելացնել վճարի ֆայլեր',
    'Upload one or more images or PDFs of the bill. All files are saved with the bill, and Analyze uses every page.':
      'Վերբեռնեք վճարի մեկ կամ մի քանի նկար/PDF։ Բոլոր ֆայլերը պահվում են վճարի հետ, իսկ վերլուծությունը օգտագործում է բոլոր էջերը։',
    'Failed to save gas bill.': 'Չհաջողվեց պահել գազի վճարը։',
    'Scan water bill': 'Սկանավորել ջրի վճարը',
    'Water bill analyzed and filled': 'Ջրի վճարը վերլուծվեց և լրացվեց',
    'Water bill analyzed. Review fields, then save.':
      'Ջրի վճարը վերլուծվեց։ Ստուգեք դաշտերը և պահպանեք։',
    'Failed to analyze water bill.': 'Չհաջողվեց վերլուծել ջրի վճարը։',
    'Failed to save water bill.': 'Չհաջողվեց պահել ջրի վճարը։',
    'Contract number': 'Պայմանագրի համար',
    'Current reading m³': 'Ընթացիկ ցուցմունք մ³',
    'Consumption m³': 'Սպառում մ³',
    'Price per m³': 'Գին մ³-ի համար',
    'Includes water, sewer, VAT, and waste taxes.':
      'Ներառում է ջուրը, կոյուղին, ԱԱՀ-ն և թափոնների հարկերը։',
    'Scan electricity bill': 'Սկանավորել էլեկտրաէներգիայի վճարը',
    'Electricity bill analyzed and filled':
      'Էլեկտրաէներգիայի վճարը վերլուծվեց և լրացվեց',
    'Electricity bill analyzed. Review fields, then save.':
      'Էլեկտրաէներգիայի վճարը վերլուծվեց։ Ստուգեք դաշտերը և պահպանեք։',
    'Failed to analyze electricity bill.':
      'Չհաջողվեց վերլուծել էլեկտրաէներգիայի վճարը։',
    'Failed to save electricity bill.':
      'Չհաջողվեց պահել էլեկտրաէներգիայի վճարը։',
    'Contracted power kW': 'Պայմանագրված հզորություն kW',
    'Includes power, energy, discounts, and taxes.':
      'Ներառում է հզորությունը, էներգիան, զեղչերը և հարկերը։',
    'Bill documents': 'Վճարի փաստաթղթեր',
    Invoice: 'Հաշիվ',
    'Invoice number': 'Հաշվի համար',
    'Supply address': 'Մատակարարման հասցե',
    'Postal code': 'Փոստային ինդեքս',
    'Billing period': 'Հաշվարկային ժամանակաշրջան',
    'Meter number': 'Հաշվիչի համար',
    CUPS: 'CUPS',
    'Previous reading m³': 'Նախորդ ցուցմունք մ³',
    'Meter and consumption': 'Հաշվիչ և սպառում',
    Prices: 'Գներ',
    'Conversion factor kWh/m³': 'Փոխակերպման գործակից kWh/m³',
    'Energy kWh': 'Էներգիա kWh',
    'Price per kWh': 'Գին kWh-ի համար',
    'Energy cost': 'Էներգիայի արժեք',
    'Total invoice': 'Հաշվի ընդհանուր',
    'Amount to pay or credit. Negative means a refund.':
      'Վճարման կամ վերադարձի գումար։ Բացասականը վերադարձ է։',
    'Gas bill total (Importe Factura). Extra maintenance invoices stay in notes.':
      'Գազի հաշվի ընդհանուր (Importe Factura)։ Սպասարկման առանձին հաշիվները մնում են նշումներում։',
    'Energy kWh × Price per kWh. This is not the invoice total.':
      'Էներգիա kWh × գին kWh-ի համար։ Սա հաշվի ընդհանուրը չէ։',
    'Month spend in m³ × {{factor}} × Price per kWh':
      'Ամսական ծախս մ³ × {{factor}} × գին kWh-ի համար',
    'Optional notes': 'Լրացուցիչ նշումներ',
    'Add {{category}} Bill': 'Ավելացնել {{category}} վճար',
    'Edit {{category}} Bill': 'Խմբագրել {{category}} վճարը',
    'Are you sure you want to delete this bill?':
      'Վստա՞հ եք, որ ուզում եք ջնջել այս վճարը։',
    'Utility Bills Total': 'Կոմունալ վճարների ընդհանուր',
    'All Utility Bills': 'Բոլոր կոմունալ վճարները',
    'Utility Bills Timeline': 'Կոմունալ վճարների ժամանակագիծ',
    'No utility bills in this period.': 'Այս ժամանակահատվածում կոմունալ վճարներ չկան։',
    'No utility bills on this day.': 'Այդ օրը կոմունալ վճարներ չկան։',
    'No utility bills in this month.': 'Այդ ամսում կոմունալ վճարներ չկան։',
    'No utility bills in this year.': 'Այդ տարում կոմունալ վճարներ չկան։',
    Bills: 'Վճարներ',
    Bill: 'Վճար',
    'Bill Details': 'Վճարի մանրամասներ',
    'Edit Bill': 'Խմբագրել վճարը',
    Price: 'Գին',
    Currency: 'Արժույթ',
    Close: 'Փակել',
    'Created At': 'Ստեղծվել է',
    'Spend charts for all utility bills together and for each utility category.':
      'Ծախսերի գծապատկերներ բոլոր կոմունալ վճարների և յուրաքանչյուր կատեգորիայի համար։',
    'Breakdown of utility spend across every utility category.':
      'Կոմունալ ծախսերի բաշխում ըստ բոլոր կատեգորիաների։',
    'Total utility bill spend over time across all categories.':
      'Կոմունալ վճարների ընդհանուր ծախսը ժամանակի ընթացքում՝ բոլոր կատեգորիաներով։',
    'Add daily purchases or utility bills first, then this page will show spend charts by category.':
      'Նախ ավելացրեք ամենօրյա գնումներ կամ կոմունալ վճարներ, ապա այս էջում կտեսնեք ծախսերի գծապատկերները։',
    'No Utility Bills Yet': 'Կոմունալ վճարներ դեռ չկան',
    'Track electricity, water, gas, internet, and other recurring household bills in one place — amounts, periods, and providers.':
      'Հետևեք էլեկտրաէներգիայի, ջրի, գազի, ինտերնետի և այլ պարբերական կոմունալ վճարներին մեկ տեղում՝ գումարներ, ժամանակաշրջաններ և մատակարարներ։',
    Electricity: 'Էլեկտրաէներգիա',
    Gas: 'Գազ',
    Internet: 'Ինտերնետ',
    Phone: 'Հեռախոս',
    Rent: 'Վարձավճար',
    Telecommunications: 'Հեռահաղորդակցություն',
    'Mortgage/Credit': 'Հիպոթեք/Վարկ',
    Mortgage: 'Հիպոթեք',
    Credit: 'Վարկ',
    'Bank Name': 'Բանկի անուն',
    Amount: 'Գումար',
    Percentage: 'Տոկոս',
    'Monthly Quota': 'Ամսական վճար',
    'Monthly Quotas': 'Ամսական վճարներ',
    'Main Quota': 'Հիմնական վճար',
    'Percentage Quota': 'Տոկոսային վճար',
    'Select period start and end to generate monthly quota rows.':
      'Ընտրեք ժամանակաշրջանի սկիզբն ու ավարտը՝ ամսական վճարների տողեր ստեղծելու համար։',
    'For each month, fill both Main Quota and Percentage Quota, or leave both empty.':
      'Յուրաքանչյուր ամսվա համար լրացրեք և՛ Հիմնական վճարը, և՛ Տոկոսային վճարը, կամ թողեք երկուսն էլ դատարկ։',
    'Total To Pay': 'Վճարման ենթակա ընդհանուր',
    Paid: 'Վճարված',
    Remaining: 'Մնացորդ',
    'Payment Progress': 'Վճարման ընթացք',
    'Payments are counted at the end of each month. {{paidMonths}} of {{plannedMonths}} monthly payments completed.':
      'Վճարումները հաշվվում են ամսվա վերջում։ Կատարվել է {{paidMonths}} վճարում {{plannedMonths}}-ից։',
    'Period Start': 'Ժամանակաշրջանի սկիզբ',
    'Period End': 'Ժամանակաշրջանի ավարտ',
    'Optional description': 'Լրացուցիչ նկարագրություն',
    'No {{category}} Bills Yet': '{{category}} վճարներ դեռ չկան',
    'Track electricity bills here — amounts, periods, and providers.':
      'Հետևեք էլեկտրաէներգիայի վճարներին այստեղ՝ գումարներ, ժամանակաշրջաններ և մատակարարներ։',
    'Track water bills here — amounts, periods, and providers.':
      'Հետևեք ջրի վճարներին այստեղ՝ գումարներ, ժամանակաշրջաններ և մատակարարներ։',
    'Track gas bills here — amounts, periods, and providers.':
      'Հետևեք գազի վճարներին այստեղ՝ գումարներ, ժամանակաշրջաններ և մատակարարներ։',
    'Track internet, phone, and other telecom bills here — amounts, periods, and providers.':
      'Հետևեք ինտերնետի, հեռախոսի և այլ հեռահաղորդակցության վճարներին այստեղ՝ գումարներ, ժամանակաշրջաններ և մատակարարներ։',
    'Track mortgage and credit payments here — amounts, periods, and providers.':
      'Հետևեք հիպոթեքի և վարկի վճարումներին այստեղ՝ գումարներ, ժամանակաշրջաններ և մատակարարներ։',
    Trash: 'Աղբահանություն',
    'No Comparison Data Yet': 'Հմեմատության տվյալներ դեռ չկան',
    'Add daily purchases from receipts first. Comparison tables are built by category, with markets as columns.':
      'Սկզբում ավելացրեք ամենօրյա գնումներ կտրոններից։ Համեմատության աղյուսակները կառուցվում են ըստ կատեգորիաների՝ խանութները որպես սյունակներ։',
    'Each table groups receipt products by category. Columns are markets — if one market has several products of the same name, it gets extra columns.':
      'Յուրաքանչյուր աղյուսակ խմբավորում է կտրոնի ապրանքները ըստ կատեգորիայի։ Սյունակները խանութներն են — եթե մեկ խանութում նույն անունով մի քանի ապրանք կա, ավելանում են լրացուցիչ սյունակներ։',
    Product: 'Ապրանք',
    Markets: 'Խանութներ',
    Types: 'Տեսակներ',
    'Each table is a category. Rows are specific product types (Beer, Coffee, Milk…). Columns are markets. Green is lowest price, red is highest.':
      'Յուրաքանչյուր աղյուսակ կատեգորիա է։ Տողերը կոնկրետ ապրանքի տեսակներ են (Գարեջուր, Սուրճ, Կաթ…)։ Սյունակները խանութներն են։ Կանաչը ամենացածր գինն է, կարմիրը՝ ամենաբարձրը։',
    Beer: 'Գարեջուր',
    Wine: 'Գինի',
    Cider: 'Սիդր',
    Spirits: 'Թունդ խմիչքներ',
    'Soft Drink': 'Զովացուցիչ ըմպելիք',
    Juice: 'Հյութ',
    'Energy Drink': 'Էներգետիկ ըմպելիք',
    Coffee: 'Սուրճ',
    Tea: 'Թեյ',
    Milk: 'Կաթ',
    'Plant Milk': 'Բուսական կաթ',
    Yogurt: 'Յոգուրտ',
    Cheese: 'Պանիր',
    Butter: 'Կարագ',
    Cream: 'Սերուցք',
    Bread: 'Հաց',
    Pastry: 'Խմորեղեն',
    Chicken: 'Հավ',
    Beef: 'Տավարի միս',
    Pork: 'Խոզի միս',
    Fish: 'Ձուկ',
    Seafood: 'Ծովամթերք',
    Fruit: 'Միրգ',
    Vegetables: 'Բանջարեղեն',
    Pasta: 'Մակարոնեղեն',
    Rice: 'Բրինձ',
    Flour: 'Ալյուր',
    Sugar: 'Շաքար',
    'Salt & Spices': 'Աղ և համեմունքներ',
    'Ready Meal': 'Պատրաստի ուտեստ',
    Tofu: 'Տոֆու',
    Oil: 'Յուղ',
    Sauce: 'Սոուս',
    Snacks: 'Խորտիկներ',
    Chocolate: 'Շոկոլադ',
    Candy: 'Քաղցրավենիք',
    Cereal: 'Հացահատիկ',
    'Ice Cream': 'Պաղպաղակ',
    'Frozen Meal': 'Սառեցված ուտեստ',
    Shampoo: 'Շամպուն',
    'Hair Spray': 'Մազերի լաք',
    Deodorant: 'Դեզոդորանտ',
    Soap: 'Օճառ',
    Toothpaste: 'Ատամի մածուկ',
    Toothbrush: 'Ատամի խոզանակ',
    Razor: 'Սափրիչ',
    'Feminine Care': 'Կանացի հիգիենա',
    Detergent: 'Լվացքի միջոց',
    Cleaner: 'Մաքրող միջոց',
    'Toilet Paper': 'Զուգարանի թուղթ',
    'Paper Towels': 'Խոհանոցային թուղթ',
    Dishwashing: 'Սպասք լվանալու միջոց',
    'Air Freshener': 'Օդը թարմացնող միջոց',
    'Trash Bags': 'Աղբի տոպրակներ',
    Diapers: 'Տակդիրներ',
    'Baby Food': 'Մանկական սնունդ',
    'Pet Food': 'Կենդանիների կեր',
    'Cat Litter': 'Կատվի ավազ',
    Medicine: 'Դեղ',
    Vitamins: 'Վիտամիններ',
    Batteries: 'Մարտկոցներ',
    'No Daily Purchases Yet': 'Ամենօրյա գնումներ դեռ չկան',
    'Create a daily purchase and track all products bought in one market visit.':
      'Ստեղծեք ամենօրյա գնում և հետևեք շուկայից գնված բոլոր ապրանքներին մեկ գնումով։',
    Occasional: 'Առանձին',
    'No Occasional Purchases Yet': 'Առանձին գնումներ դեռ չկան',
    'Add non-daily purchases here — larger buys or smaller one-offs — separate from day-to-day shopping.': 'Ավելացրեք այստեղ ոչ ամենօրյա գնումները՝ մեծ թե փոքր, որպեսզի դրանք առանձին մնան ամենօրյա գնումներից։',
    'Comparison Is Coming Soon': 'Համեմատությունը շուտով կհայտնվի',
    'This tab is intentionally left as a placeholder for now. We can return later and build product comparison.':
      'Այս բաժինը այժմ թողնված է որպես տեղապահ։ Հետագայում կարող ենք վերադառնալ և կառուցել ապրանքների համեմատությունը։',
    'Purchase Details': 'Գնման մանրամասներ',
    'Create Daily Purchase': 'Ստեղծել ամենօրյա գնում',
    'Create Occasional Purchase': 'Ստեղծել առանձին գնում',
    'Edit Daily Purchase': 'Խմբագրել ամենօրյա գնումը',
    'Add Daily Purchase': 'Ավելացնել ամենօրյա գնում',
    'Edit Occasional Purchase': 'Խմբագրել առանձին գնումը',
    'Add Occasional Purchase': 'Ավելացնել առանձին գնում',
    'Manage your daily shopping, track occasional purchases separately, and leave room for a future products catalog.': 'Կառավարեք ամենօրյա գնումները, առանձին հետևեք ոչ ամենօրյա գնումներին և թողեք տեղ ապագա ապրանքային կատալոգի համար։',
    'Scan Receipt': 'Սկանավորել կտրոնը',
    'Upload a receipt/check photo to autofill products. Manual add still works.':
      'Վերբեռնեք կտրոնի լուսանկարը, որպեսզի ապրանքների ցանկը լրացվի ինքնաշխատ։ Կարող եք նաև շարունակել ձեռքով լրացնել։',
    'Scan Purchase Image': 'Սկանավորել գնման նկարը',
    'Upload an invoice, receipt, or product photo to autofill this occasional purchase. Manual edit still works.': 'Վերբեռնեք հաշիվ, կտրոն կամ ապրանքի լուսանկար՝ առանձին գնումը ինքնաշխատ լրացնելու համար։ Կարող եք նաև խմբագրել ձեռքով։',
    'Choose purchase image': 'Ընտրել գնման նկար',
    'Purchase image preview': 'Գնման նկարի նախադիտում',
    'Analyze & Fill Form': 'Վերլուծել և լրացնել ձևը',
    'Purchase image analyzed and filled': 'Գնման նկարը վերլուծվեց և լրացվեց',
    'Purchase image analyzed. Review fields, then save.':
      'Գնման նկարը վերլուծվեց։ Ստուգեք դաշտերը և պահպանեք։',
    'Failed to analyze purchase image.': 'Չհաջողվեց վերլուծել գնման նկարը։',
    'Review and edit if needed.': 'Ստուգեք և անհրաժեշտության դեպքում խմբագրեք։',
    'Receipt preview': 'Կտրոնի նախադիտում',
    'Choose receipt image': 'Ընտրել կտրոնի նկար',
    'Analyzing...': 'Վերլուծվում է...',
    'Analyze & Fill List': 'Վերլուծել և լրացնել ցանկը',
    'Clear Image': 'Մաքրել նկարը',
    'Market Name': 'Խանութի անվանում',
    'Enter market name': 'Մուտքագրեք խանութի անվանումը',
    'Total Amount': 'Ընդհանուր գումար',
    'Receipt Number': 'Կտրոնի համար',
    'Payment Method': 'Վճարման եղանակ',
    'Ticket / invoice number': 'Կտրոնի / հաշիվ-ապրանքագրի համար',
    'card, cash, bizum...': 'քարտ, կանխիկ, bizum...',
    'Add Product': 'Ավելացնել ապրանք',
    'Enter product name': 'Մուտքագրեք ապրանքի անունը',
    'Line Total': 'Տողի գումար',
    'Line importe / total': 'Տողի արժեք / ընդհանուր',
    'Receipt analyzed and filled': 'Կտրոնը վերլուծվեց և լրացվեց',
    'Review and edit if needed. You can still add more manually.':
      'Ստուգեք և անհրաժեշտ হলে խմբագրեք։ Կարող եք նաև ձեռքով ավելացնել նոր ապրանքներ։',
    'Receipt analyzed. Review products, then save. You can still add more manually.':
      'Կտրոնը վերլուծվեց։ Ստուգեք ապրանքները, ապա պահպանեք։ Կարող եք նաև ձեռքով ավելացնել նորերը։',
    'Failed to analyze receipt image.': 'Չհաջողվեց վերլուծել կտրոնի պատկերը։',
    'No Analytics Yet': 'Վերլուծություն դեռ չկա',
    'Add daily purchases with products first, then this analytics tab will show spend charts by category.':
      'Սկզբում ավելացրեք ամենօրյա գնումներ ապրանքներով, հետո այս բաժնում կհայտնվեն ծախսերի գծապատկերները ըստ կատեգորիաների։',
    'Add income first, then this page will show charts by type.':
      'Սկզբում ավելացրեք եկամուտ, հետո այս էջում կհայտնվեն գծապատկերները ըստ տեսակի։',
    'See charts for salary, bonuses, and other inflows over time.':
      'Տեսեք աշխատավարձի, բոնուսների և այլ մուտքերի գծապատկերները ժամանակի ընթացքում։',
    'Total Income': 'Ընդհանուր եկամուտ',
    'Regular Income': 'Կանոնավոր եկամուտ',
    'Occasional Income': 'Առանձին եկամուտ',
    'Income Records': 'Եկամտի գրառումներ',
    'Income by Type': 'Եկամուտ ըստ տեսակի',
    'Breakdown of income across salary, freelance, and other types.':
      'Եկամտի բաշխում աշխատավարձի, ֆրիլանսի և այլ տեսակների միջև։',
    'No income in this period.': 'Այս ժամանակահատվածում եկամուտ չկա։',
    'Daily Spend': 'Ամենօրյա ծախս',
    'Occasional Purchases Total': 'Առանձին գնումների ընդհանուր',
    'Total Purchases': 'Գնումների քանակ',
    'Tracked Products': 'Հետևվող ապրանքներ',
    'Top Category': 'Առաջատար կատեգորիա',
    Dairy: 'Կաթնամթերք',
    Eggs: 'Ձու',
    Meat: 'Միս',
    'Fish & Seafood': 'Ձուկ և ծովամթերք',
    'Fruit & Veg': 'Մրգեր և բանջարեղեն',
    Water: 'Ջուր',
    Alcohol: 'Ալկոհոլ',
    'Other Drinks': 'Այլ խմիչքներ',
    Drinks: 'Խմիչքներ',
    Average: 'Միջին',
    Bakery: 'Հացաբուլկեղեն',
    'Pasta & Rice': 'Մակարոն և բրինձ',
    'Oils & Condiments': 'Յուղեր և համեմունքներ',
    'Canned Food': 'Պահածոներ',
    Frozen: 'Սառեցված',
    'Breakfast & Cereals': 'Նախաճաշ և հացահատիկ',
    Household: 'Տնային պարագաներ',
    Hygiene: 'Հիգիենա',
    'Personal Care': 'Անձնական խնամք',
    Baby: 'Մանկական',
    Pet: 'Կենդանիներ',
    Pharmacy: 'Դեղատուն',
    'Ready Meals': 'Պատրաստի ուտեստներ',
    'Snacks & Sweets': 'Նախուտեստներ և քաղցրավենիք',
    'Coffee & Tea': 'Սուրճ և թեյ',
    Charcuterie: 'Երշիկեղեն',
    Legumes: 'Լոբազգիներ',
    'Nuts & Seeds': 'Ընկուզեղեն և սերմեր',
    'Baking Ingredients': 'Թխման բաղադրիչներ',
    'Plant-based': 'Բուսական',
    Tobacco: 'Ծխախոտ',
    Stationery: 'Գրենական պիտույքներ',
    'Batteries & Tech': 'Մարտկոցներ և տեխնիկա',
    'Flowers & Plants': 'Ծաղիկներ և բույսեր',
    Computers: 'Համակարգիչներ',
    'Phones & Tablets': 'Հեռախոսներ և պլանշետներ',
    Electronics: 'Էլեկտրոնիկա',
    'TV & Audio': 'Հեռուստացույց և աուդիո',
    Gaming: 'Խաղեր',
    'Camera & Photo': 'Տեսախցիկ և ֆոտո',
    'Smart Home': 'Խելացի տուն',
    Furniture: 'Կահույք',
    'Bedding & Mattresses': 'Անկողին և ներքնակներ',
    Appliances: 'Կենցաղային տեխնիկա',
    Kitchenware: 'Խոհանոցային պարագաներ',
    'Home Improvement': 'Տան վերանորոգում',
    Vehicles: 'Տրանսպորտ',
    Bicycles: 'Հեծանիվներ',
    Clothing: 'Հագուստ',
    'Shoes & Accessories': 'Կոշիկ և աքսեսուարներ',
    'Jewelry & Watches': 'Զարդեր և ժամացույցներ',
    Eyewear: 'Ակնոցներ',
    'Luggage & Bags': 'Ճամպրուկ և պայուսակներ',
    'Home & Decor': 'Տուն և դեկոր',
    'Garden & Outdoor': 'Այգի և բացօթյա',
    Sports: 'Սպորտ',
    'Fitness Equipment': 'Ֆիթնես սարքավորումներ',
    'Tools & DIY': 'Գործիքներ',
    'Office Equipment': 'Գրասենյակային սարքավորումներ',
    'Baby Gear': 'Մանկական սարքավորումներ',
    'Toys & Kids': 'Խաղալիքներ',
    Pets: 'Կենդանիներ',
    'Musical Instruments': 'Երաժշտական գործիքներ',
    'Books & Media': 'Գրքեր և մեդիա',
    'Hobbies & Collectibles': 'Հոբբի և հավաքածուներ',
    'Health Equipment': 'Առողջության սարքավորումներ',
    Education: 'Կրթություն',
    'Software & Digital': 'Ծրագրեր և թվային',
    Travel: 'Ճամփորդություն',
    'Spend by Category': 'Ծախսը ըստ կատեգորիաների',
    'Based on daily-purchase product lines and their estimated totals.':
      'Հաշվարկված է ամենօրյա գնումների ապրանքային տողերի և դրանց գնահատված գումարների հիման վրա։',
    'Category Comparison': 'Կատեգորիաների համեմատություն',
    'Higher bars show categories where most of your money goes.':
      'Ավելի բարձր սյունակները ցույց են տալիս, թե որ կատեգորիաներում եք ծախսում ավելի շատ։',
    Category: 'Կատեգորիա',
    Products: 'Ապրանքներ',
    Qty: 'Քանակ',
    'No products in this category.': 'Այս կատեգորիայում ապրանքներ չկան։',
    'Click a category to see its products.':
      'Սեղմեք կատեգորիայի վրա՝ ապրանքների ցանկը տեսնելու համար։',
    'Last month': 'Վերջին ամիս',
    'Last 3 months': 'Վերջին 3 ամիս',
    'Last 6 months': 'Վերջին 6 ամիս',
    'No purchases in this period.': 'Այս ժամանակահատվածում գնումներ չկան։',
    'Spend Timeline': 'Ծախսերի ժամանակագիծ',
    'Income Timeline': 'Եկամտի ժամանակագիծ',
    'By year': 'Ըստ տարվա',
    'By month': 'Ըստ ամսի',
    'See how your income changes over time across recorded inflows.':
      'Տեսեք, թե ինչպես է ձեր եկամուտը փոխվում ժամանակի ընթացքում գրանցված մուտքերի հիման վրա։',
    'See how your income changes over time across recorded inflows. Click a month to see its income.':
      'Տեսեք, թե ինչպես է ձեր եկամուտը փոխվում ժամանակի ընթացքում։ Սեղմեք ամսվա վրա՝ այդ ամսվա եկամուտը տեսնելու համար։',
    'A yearly view helps you compare overall income trends.':
      'Տարեկան տեսքը օգնում է համեմատել եկամտի ընդհանուր միտումները։',
    'A yearly view helps you compare overall income trends. Click a year to see its income.':
      'Տարեկան տեսքը օգնում է համեմատել եկամտի ընդհանուր միտումները։ Սեղմեք տարվա վրա՝ այդ տարվա եկամուտը տեսնելու համար։',
    'No income in this year.': 'Այդ տարում եկամուտ չկա։',
    'No income in this month.': 'Այդ ամսում եկամուտ չկա։',
    Month: 'Ամիս',
    'Spend by Month': 'Ծախսը ըստ ամիսների',
    'See how your spending changes over time across recorded purchases.':
      'Տեսեք, թե ինչպես են ձեր ծախսերը փոխվում ժամանակի ընթացքում գրանցված գնումների հիման վրա։',
    'See how your spending changes over time across recorded purchases. Click a day to see its purchases.':
      'Տեսեք, թե ինչպես են ձեր ծախսերը փոխվում ժամանակի ընթացքում։ Սեղմեք օրվա վրա՝ այդ օրվա գնումները տեսնելու համար։',
    'Spend by Year': 'Ծախսը ըստ տարիների',
    'A yearly view helps you compare overall purchase trends.':
      'Տարեկան տեսքը օգնում է համեմատել գնումների ընդհանուր միտումները։',
    'A yearly view helps you compare overall purchase trends. Click a year to see its purchases.':
      'Տարեկան տեսքը օգնում է համեմատել գնումների ընդհանուր միտումները։ Սեղմեք տարվա վրա՝ այդ տարվա գնումները տեսնելու համար։',
    'No purchases on this day.': 'Այդ օրը գնումներ չկան։',
    'No purchases in this year.': 'Այդ տարում գնումներ չկան։',
    'Average Daily Receipt Spend': 'Օրվա միջին կտրոնի ծախս',
    Search: 'Որոնում',
    Information: 'Տեղեկություն',
    Wedding: 'Հարսանիք',
    Language: 'Լեզու',
    'Open menu': 'Բացել ընտրացանկը',
    'Close menu': 'Փակել ընտրացանկը',
    'Search all items... (memories, todos, ideas, events, recipes, movies, and more)':
      'Որոնել բոլոր բաժիններում... (հիշողություններ, անելիքներ, գաղափարներ, իրադարձություններ, բաղադրատոմսեր, ֆիլմեր և այլն)',
    'Show all results for "{{query}}"':
      'Ցույց տալ բոլոր արդյունքները "{{query}}" հարցման համար',
    'No results found for "{{query}}"':
      '"{{query}}" հարցման համար արդյունք չի գտնվել',
    'Open user menu': 'Բացել օգտվողի ընտրացանկը',
    'User menu': 'Օգտվողի ընտրացանկ',
    Logout: 'Դուրս գալ',
    'Are you sure you want to log out?':
      'Վստա՞հ եք, որ ցանկանում եք դուրս գալ:',
    'Log Out': 'Դուրս գալ',
    'Confirmation Required': 'Պահանջվում է հաստատում',
    'Action not allowed': 'Գործողությունը թույլատրելի չէ',
    Cancel: 'Չեղարկել',
    Confirm: 'Հաստատել',
    'Clear selected value': 'Մաքրել ընտրված արժեքը',
    'Search...': 'Որոնել...',
    'No items found': 'Տվյալներ չեն գտնվել',
    'Select {{placeholder}}': 'Ընտրել {{placeholder}}',
    '{{count}} selected': '{{count}} ընտրված',
    'Select options': 'Ընտրել տարբերակներ',
    'Good morning': 'Բարի լույս',
    'Good afternoon': 'Բարի օր',
    'Good evening': 'Բարի երեկո',
    'This Week': 'Այս շաբաթ',
    'Active To-Dos': 'Ակտիվ անելիքներ',
    'Active Objectives': 'Ակտիվ նպատակներ',
    'Total Tasks': 'Ընդհանուր առաջադրանքներ',
    'View All': 'Տեսնել բոլորը',
    'Upcoming Events': 'Մոտակա իրադարձություններ',
    'Next 30 Days': 'Հաջորդ 30 օրը',
    Today: 'Այսօր',
    Tomorrow: 'Վաղը',
    '{{count}} days': '{{count}} օր',
    'No upcoming events': 'Մոտակա իրադարձություններ չկան',
    'Your calendar is clear for now': 'Ձեր օրացույցը առայժմ ազատ է',
    All: 'Բոլորը',
    Vocabulary: 'Բառարան',
    'Search all items...': 'Որոնել բոլոր բաժիններում...',
    'Found {{count}} result': 'Գտնվել է {{count}} արդյունք',
    'Found {{count}} results': 'Գտնվել է {{count}} արդյունք',
    'in {{type}}': '{{type}} բաժնում',
    'No results found': 'Արդյունքներ չեն գտնվել',
    'Enter a search term to find memories, todos, ideas, events, and more':
      'Մուտքագրեք որոնման բառ` հիշողություններ, անելիքներ, գաղափարներ, իրադարձություններ և այլ տվյալներ գտնելու համար',
    'Untitled Memory': 'Անանուն հիշողություն',
    'Untitled Todo': 'Անանուն անելիք',
    'Untitled Idea': 'Անանուն գաղափար',
    'Untitled Link': 'Անանուն հղում',
    'Untitled Event': 'Անանուն իրադարձություն',
    'Untitled Board': 'Անանուն տախտակ',
    'Untitled Word': 'Անանուն բառ',
    'Untitled Recipe': 'Անանուն բաղադրատոմս',
    'Untitled Movie': 'Անանուն ֆիլմ',
    'Untitled Location': 'Անանուն վայր',
    'Untitled Objective': 'Անանուն նպատակ',
    'Untitled File': 'Անանուն ֆայլ',
    'Email address': 'Էլ. հասցե',
    'Enter email': 'Մուտքագրեք էլ. հասցեն',
    'Wrong email or password': 'Սխալ էլ. հասցե կամ գաղտնաբառ',
    Password: 'Գաղտնաբառ',
    'Remember me': 'Հիշել ինձ',
    Login: 'Մուտք',
    'Close notification': 'Փակել ծանուցումը',
    Success: 'Հաջող է',
    Warning: 'Զգուշացում',
    Error: 'Սխալ',
    Info: 'Տեղեկություն',
    Trips: 'Ճամփորդություններ',
    Weddings: 'Հարսանիքներ',
    Filters: 'Ֆիլտրեր',
    Reset: 'Վերակայել',
    Delete: 'Ջնջել',
    Name: 'Անուն',
    Tag: 'Պիտակ',
    Genre: 'Ժանր',
    Year: 'Տարի',
    Status: 'Կարգավիճակ',
    Rating: 'Վարկանիշ',
    'Rating Mode': 'Վարկանիշի ռեժիմ',
    'Greater than': 'Ավելի մեծ',
    'Less than': 'Ավելի փոքր',
    'Filter by name': 'Ֆիլտրել անունով',
    'Filter by tag': 'Ֆիլտրել պիտակով',
    'Filter by title': 'Ֆիլտրել վերնագրով',
    'Filter by year': 'Ֆիլտրել տարով',
    'Filter by rating': 'Ֆիլտրել վարկանիշով',
    'Capture meaningful moments, lessons, and notes you want to keep close':
      'Պահպանեք կարևոր պահերը, դասերը և գրառումները, որոնք ցանկանում եք միշտ մոտ պահել',
    'Create Memory': 'Ստեղծել հիշողություն',
    '{{count}} memories': '{{count}} հիշողություն',
    'No Memories Yet': 'Հիշողություններ դեռ չկան',
    'Create your first memory to keep meaningful moments, reflections, and notes in one place.':
      'Ստեղծեք ձեր առաջին հիշողությունը, որպեսզի կարևոր պահերը, մտքերը և գրառումները պահեք մեկ վայրում։',
    'Add Memory': 'Ավելացնել հիշողություն',
    'Edit Memory': 'Խմբագրել հիշողությունը',
    'Memories filters': 'Հիշողությունների ֆիլտրեր',
    'Narrow memories by name or tag':
      'Նեղացրեք հիշողությունները անունով կամ պիտակով',
    'Are you sure you want to delete this memory?':
      'Վստա՞հ եք, որ ցանկանում եք ջնջել այս հիշողությունը:',
    'Collect sparks, concepts, and future directions worth exploring':
      'Հավաքեք գաղափարների կայծեր, մտքեր և ապագա ուղղություններ, որոնք արժե ուսումնասիրել',
    'Create Idea': 'Ստեղծել գաղափար',
    '{{count}} ideas': '{{count}} գաղափար',
    'No Ideas Yet': 'Գաղափարներ դեռ չկան',
    'Capture your first idea to start building a space for concepts, experiments, and future plans.':
      'Գրանցեք ձեր առաջին գաղափարը, որպեսզի սկսեք կառուցել տարածք մտքերի, փորձերի և ապագա ծրագրերի համար։',
    'Add Idea': 'Ավելացնել գաղափար',
    'Edit Idea': 'Խմբագրել գաղափարը',
    'Ideas filters': 'Գաղափարների ֆիլտրեր',
    'Refine ideas by name or tag': 'Զտեք գաղափարները անունով կամ պիտակով',
    'Are you sure you want to delete this idea?':
      'Վստա՞հ եք, որ ցանկանում եք ջնջել այս գաղափարը:',
    'Track movies, series, and animations in one place':
      'Հետևեք ֆիլմերին, սերիալներին և անիմացիաներին մեկ վայրում',
    Movie: 'Ֆիլմ',
    Series: 'Սերիալներ',
    Animations: 'Անիմացիաներ',
    Animation: 'Անիմացիա',
    'Create {{item}}': 'Ստեղծել {{item}}',
    '{{count}} shown in {{category}}':
      '{{category}} բաժնում ցուցադրվում է {{count}}',
    Filtered: 'Զտված',
    'No Matching {{category}}': 'Համընկնող {{category}} չկան',
    'No {{category}} Yet': '{{category}} դեռ չկան',
    'Try changing or resetting the current filters to see more results.':
      'Փորձեք փոխել կամ վերակայել ընթացիկ ֆիլտրերը՝ ավելի շատ արդյունքներ տեսնելու համար։',
    'Create your first {{item}} to start building this list.':
      'Ստեղծեք ձեր առաջին {{item}}-ը՝ այս ցանկը սկսելու համար։',
    'Reset Filters': 'Վերակայել ֆիլտրերը',
    Previous: 'Նախորդը',
    Next: 'Հաջորդը',
    'Showing {{from}}-{{to}} of {{total}}':
      'Ցուցադրվում է {{from}}-{{to}}՝ {{total}}-ից',
    'Movies Filters': 'Ֆիլմերի ֆիլտրեր',
    '{{item}} Details': '{{item}} մանրամասներ',
    'Edit {{item}}': 'Խմբագրել {{item}}',
    'Add {{item}}': 'Ավելացնել {{item}}',
    'Are you sure you want to delete this {{item}}?':
      'Վստա՞հ եք, որ ցանկանում եք ջնջել այս {{item}}-ը:',
    'Narrow movies, series, and animations by key details':
      'Զտեք ֆիլմերը, սերիալները և անիմացիաները հիմնական տվյալներով',
    Planned: 'Պլանավորված',
    Watching: 'Դիտում եմ',
    Completed: 'Ավարտված',
    'Organize your tasks across paused, active, and finished states':
      'Կազմակերպեք ձեր առաջադրանքները դադարեցված, ակտիվ և ավարտված վիճակներով',
    'Create To-Do': 'Ստեղծել անելիք',
    Paused: 'Դադարեցված',
    Active: 'Ակտիվ',
    Finished: 'Ավարտված',
    total: 'ընդամենը',
    'Waiting or deferred': 'Սպասում է կամ հետաձգված է',
    'Current focus': 'Ընթացիկ ուշադրություն',
    'Completed work': 'Ավարտված աշխատանք',
    'There are no active tasks yet': 'Ակտիվ առաջադրանքներ դեռ չկան',
    'There are no paused tasks yet': 'Դադարեցված առաջադրանքներ դեռ չկան',
    'There are no finished tasks yet': 'Ավարտված առաջադրանքներ դեռ չկան',
    'Add To-Do': 'Ավելացնել անելիք',
    'Edit To-Do': 'Խմբագրել անելիքը',
    'Are you sure you want to delete this to-do?':
      'Վստա՞հ եք, որ ցանկանում եք ջնջել այս անելիքը:',
    Update: 'Թարմացնել',
    Create: 'Ստեղծել',
    'Select genres': 'Ընտրել ժանրերը',
    '{{item}} Title': '{{item}} վերնագիր',
    'Image Link': 'Պատկերի հղում',
    'Watch Link': 'Դիտման հղում',
    Note: 'Նշում',
    'Rating must be a number between 0.0 and 10.0.':
      'Վարկանիշը պետք է լինի 0.0-ից 10.0 միջակայքում գտնվող թիվ։',
    'Why do you want to keep this {{item}} in the list?':
      'Ինչո՞ւ եք ուզում այս {{item}}-ը պահել ցանկում:',
    Visited: 'Այցելված',
    Upcoming: 'Սպասվող',
    'To Visit': 'Այցելելու',
    State: 'Կարգավիճակ',
    Location: 'Վայր',
    List: 'Ցանկ',
    'City, place, or map link...': 'Քաղաք, վայր կամ քարտեզի հղում...',
    'Find on map': 'Գտնել քարտեզի վրա',
    'Searching...': 'Որոնում...',
    'Map pin set': 'Քարտեզի նշիչը սահմանված է',
    'Use Find on map so this trip can appear as a marker in map view.':
      'Օգտագործեք «Գտնել քարտեզի վրա», որպեսզի ճամփորդությունը երևա քարտեզի վրա։',
    'Enter a place name or map link first.':
      'Նախ մուտքագրեք վայրի անուն կամ քարտեզի հղում։',
    'No places found for this location.': 'Այս վայրի համար արդյունքներ չեն գտնվել։',
    'Could not look up this location. Try again.':
      'Չհաջողվեց գտնել այս վայրը։ Փորձեք նորից։',
    'No map locations yet': 'Քարտեզի վայրեր դեռ չկան',
    'Add a location to your trips so they can appear as markers on the map.':
      'Ավելացրեք վայր ձեր ճամփորդություններին, որպեսզի դրանք երևան քարտեզի վրա։',
    'Loading map markers...': 'Քարտեզի նշիչները բեռնվում են...',
    'Plan and organize your travel history and future adventures.':
      'Պլանավորեք և կազմակերպեք ձեր ճամփորդական պատմությունն ու ապագա արկածները։',
    'Create Trip': 'Ստեղծել ճամփորդություն',
    'No Trips In {{tab}}': '{{tab}} բաժնում ճամփորդություններ չկան',
    'No Trips Yet': 'Ճամփորդություններ դեռ չկան',
    'No Trips Match Filters': 'Ֆիլտրերին համապատասխանող ճամփորդություններ չկան',
    'Try adjusting your filters or reset them to see all trips.':
      'Փորձեք փոխել ֆիլտրերը կամ վերակայել դրանք՝ բոլոր ճամփորդությունները տեսնելու համար։',
    '{{count}} trips': '{{count}} ճամփորդություն',
    'Trips filters': 'Ճամփորդությունների ֆիլտրեր',
    'Narrow trips by name or state':
      'Զտեք ճամփորդությունները անունով կամ կարգավիճակով',
    'Add a trip and it will be categorized automatically based on its visit dates.':
      'Ավելացրեք ճամփորդություն, և այն ավտոմատ կդասավորվի ըստ այցելության օրերի։',
    'Add Trip': 'Ավելացնել ճամփորդություն',
    'Edit Trip': 'Խմբագրել ճամփորդությունը',
    'Are you sure you want to delete trip "{{name}}"?':
      'Վստա՞հ եք, որ ցանկանում եք ջնջել "{{name}}" ճամփորդությունը:',
    'Map link': 'Քարտեզի հղում',
    Description: 'Նկարագրություն',
    'Visit dates': 'Այցելության օրեր',
    'No dates selected. This trip will appear in "{{tab}}".':
      'Օրեր ընտրված չեն։ Այս ճամփորդությունը կհայտնվի "{{tab}}" բաժնում։',
    'Image links (one URL per line)':
      'Պատկերի հղումներ (յուրաքանչյուր տողում մեկ URL)',
    'Trip name...': 'Ճամփորդության անուն...',
    'What is this trip about?': 'Ինչի՞ մասին է այս ճամփորդությունը։',
    'Add your travel notes...': 'Ավելացրեք ձեր ճամփորդական նշումները...',
    'Open map': 'Բացել քարտեզը',
    'View details': 'Դիտել մանրամասները',
    'No notes yet': 'Նշումներ դեռ չկան',
    'Or paste an image link': 'Կամ տեղադրեք պատկերի հղումը',
    'Use link': 'Օգտագործել հղումը',
    'Additional images (one URL per line)':
      'Լրացուցիչ պատկերներ (յուրաքանչյուր տողում մեկ URL)',
    'Upload PDFs, videos, images, and other trip files.':
      'Վերբեռնեք PDF, տեսանյութ, պատկեր և այլ ճամփորդական ֆայլեր։',
    'No files yet': 'Ֆայլեր դեռ չկան',
    'Failed to upload file.': 'Չհաջողվեց վերբեռնել ֆայլը։',
    'Select a file first.': 'Նախ ընտրեք ֆայլ։',
    'Add file': 'Ավելացնել ֆայլ',
    '{{count}} images': '{{count}} պատկեր',
    '{{count}} notes': '{{count}} նշում',
    '{{count}} dates': '{{count}} օր',
    'Organize and manage your important links and resources':
      'Կազմակերպեք և կառավարեք ձեր կարևոր հղումներն ու ռեսուրսները',
    Link: 'Հղում',
    Folder: 'Թղթապանակ',
    Board: 'Տախտակ',
    'Add Link': 'Ավելացնել հղում',
    'Add Folder': 'Ավելացնել թղթապանակ',
    'Go back': 'Վերադառնալ',
    Back: 'Հետ',
    '{{count}} items': '{{count}} տարր',
    'No Links Yet': 'Հղումներ դեռ չկան',
    'Add your first link or folder to start organizing useful resources in one place.':
      'Ավելացրեք ձեր առաջին հղումը կամ թղթապանակը՝ օգտակար ռեսուրսները մեկ վայրում կազմակերպելու համար։',
    URL: 'URL',
    'link name...': 'հղման անուն...',
    'folder name...': 'թղթապանակի անուն...',
    'link url...': 'հղման url...',
    'Save places, routes, and addresses you want to revisit':
      'Պահեք վայրերը, երթուղիներն ու հասցեները, որոնց ցանկանում եք վերադառնալ',
    'Create Location': 'Ստեղծել վայր',
    'No Locations Yet': 'Վայրեր դեռ չկան',
    'Create your first location to keep addresses, destinations, and useful places easy to find.':
      'Ստեղծեք ձեր առաջին վայրը՝ հասցեները, ուղղությունները և օգտակար տեղերը հեշտ գտնելու համար։',
    'Add Location': 'Ավելացնել վայր',
    'Edit Location': 'Խմբագրել վայրը',
    'Are you really want to delete this Location?':
      'Իսկապե՞ս ցանկանում եք ջնջել այս վայրը:',
    Address: 'Հասցե',
    Map: 'Քարտեզ',
    City: 'Քաղաք',
    Type: 'Տեսակ',
    'Location name...': 'Վայրի անուն...',
    'Location address...': 'Վայրի հասցե...',
    'Location map...': 'Վայրի քարտեզ...',
    'Add an address or map link to show this place on the map.':
      'Ավելացրեք հասցե կամ քարտեզի հղում՝ այս վայրը քարտեզի վրա ցույց տալու համար։',
    'Location city...': 'Վայրի քաղաք...',
    'Location description...': 'Վայրի նկարագրություն...',
    'Home-Address': 'Տան հասցե',
    'Work-Address': 'Աշխատանքի հասցե',
    Hotel: 'Հյուրանոց',
    Restaurant: 'Ռեստորան',
    Pizzeria: 'Պիցցերիա',
    'Fast-Food': 'Ֆասթ-ֆուդ',
    Bar: 'Բար',
    Pub: 'Փաբ',
    Cafe: 'Սրճարան',
    Opera: 'Օպերա',
    Theater: 'Թատրոն',
    Cinema: 'Կինոթատրոն',
    Club: 'Ակումբ',
    Disco: 'Դիսկո',
    'Tourist-Attraction': 'Տուրիստական վայր',
    Beach: 'Լողափ',
    Other: 'Այլ',
    'Start at': 'Սկիզբ',
    'End at': 'Ավարտ',
    Priority: 'Առաջնահերթություն',
    High: 'Բարձր',
    Medium: 'Միջին',
    Low: 'Ցածր',
    'To-Do name...': 'Անելիքի անուն...',
    'To-Do description...': 'Անելիքի նկարագրություն...',
    'Due today': 'Այսօր վերջնաժամկետ ունի',
    Overdue: 'Ժամկետանց',
    'No deadline': 'Վերջնաժամկետ չկա',
    'No active ToDos': 'Ակտիվ անելիքներ չկան',
    'Create a new to-do to get started': 'Ստեղծեք նոր անելիք՝ սկսելու համար',
    'Create and manage your whiteboards':
      'Ստեղծեք և կառավարեք ձեր գրատախտակները',
    'Create New Folder': 'Ստեղծել նոր թղթապանակ',
    'New Folder': 'Նոր թղթապանակ',
    'Create New Whiteboard': 'Ստեղծել նոր գրատախտակ',
    'New Board': 'Նոր տախտակ',
    'Filter By': 'Ֆիլտրել ըստ',
    'Sort By': 'Տեսակավորել ըստ',
    Date: 'Ամսաթիվ',
    Order: 'Հերթականություն',
    Show: 'Ցուցադրել',
    'Folders only': 'Միայն թղթապանակները',
    'Boards only': 'Միայն տախտակները',
    'A - Z': 'Ա - Ֆ',
    'Z - A': 'Ֆ - Ա',
    'Oldest - Newest': 'Ամենահներից դեպի նորը',
    'Newest - Oldest': 'Ամենանորից դեպի հինը',
    Root: 'Արմատ',
    'Delete folder': 'Ջնջել թղթապանակը',
    'Folder must be empty to delete':
      'Ջնջելու համար թղթապանակը պետք է դատարկ լինի',
    'No Whiteboards Yet': 'Գրատախտակներ դեռ չկան',
    'Create your first whiteboard to get started':
      'Ստեղծեք ձեր առաջին գրատախտակը՝ սկսելու համար',
    'Add Whiteboard': 'Ավելացնել գրատախտակ',
    'Edit Whiteboard': 'Խմբագրել գրատախտակը',
    'Folder name is required': 'Թղթապանակի անունը պարտադիր է',
    'Could not create folder. Please try again.':
      'Չհաջողվեց ստեղծել թղթապանակը։ Խնդրում ենք կրկին փորձել։',
    'Could not update folder. Please try again.':
      'Չհաջողվեց թարմացնել թղթապանակը։ Խնդրում ենք կրկին փորձել։',
    'Create Folder': 'Ստեղծել թղթապանակ',
    'Folder name...': 'Թղթապանակի անուն...',
    'Edit Folder': 'Խմբագրել թղթապանակը',
    'Move to folder': 'Տեղափոխել թղթապանակ',
    Save: 'Պահպանել',
    'Are you really want to delete this Whiteboard?':
      'Իսկապե՞ս ցանկանում եք ջնջել այս գրատախտակը:',
    'Delete folder "{{name}}"?': 'Ջնջե՞լ "{{name}}" թղթապանակը:',
    'Whiteboard name...': 'Գրատախտակի անուն...',
    'Manage and organize your uploaded files':
      'Կառավարեք և կազմակերպեք ձեր վերբեռնված ֆայլերը',
    'Upload File': 'Վերբեռնել ֆայլ',
    'Available Space': 'Հասանելի տարածք',
    Size: 'Չափ',
    'Smallest - Largest': 'Ամենափոքրից դեպի մեծը',
    'Largest - Smallest': 'Ամենամեծից դեպի փոքրը',
    Visibility: 'Տեսանելիություն',
    Public: 'Հանրային',
    Private: 'Մասնավոր',
    'This folder is empty': 'Այս թղթապանակը դատարկ է',
    'Create a folder or upload a file here':
      'Ստեղծեք թղթապանակ կամ այստեղ վերբեռնեք ֆայլ',
    'Are you sure you want to delete this file?':
      'Վստա՞հ եք, որ ցանկանում եք ջնջել այս ֆայլը:',
    'Create folder in {{name}}': 'Ստեղծել թղթապանակ {{name}}-ում',
    'Create folder in Root': 'Ստեղծել թղթապանակ արմատում',
    'Folder name': 'Թղթապանակի անուն',
    'Type folder name...': 'Մուտքագրեք թղթապանակի անունը...',
    'Creating...': 'Ստեղծվում է...',
    'Upload file to {{name}}': 'Վերբեռնել ֆայլ {{name}}-ում',
    'Upload file to Root': 'Վերբեռնել ֆայլ արմատում',
    'Edit file: {{name}}': 'Խմբագրել ֆայլը. {{name}}',
    'Edit file': 'Խմբագրել ֆայլը',
    'File name is required': 'Ֆայլի անունը պարտադիր է',
    'Could not update file. Please try again.':
      'Չհաջողվեց թարմացնել ֆայլը։ Խնդրում ենք կրկին փորձել։',
    'Type file name...': 'Մուտքագրեք ֆայլի անունը...',
    'file description...': 'ֆայլի նկարագրություն...',
    'Public file (accessible via direct link)':
      'Հանրային ֆայլ (հասանելի է ուղիղ հղումով)',
    'When disabled, file is only accessible through the app preview.':
      'Անջատված լինելու դեպքում ֆայլը հասանելի կլինի միայն հավելվածի նախադիտմամբ։',
    'Saving...': 'Պահպանվում է...',
    'Text File Preview': 'Տեքստային ֆայլի նախադիտում',
    'Audio Preview': 'Աուդիո ֆայլի նախադիտում',
    'Loading audio...': 'Աուդիոն բեռնվում է...',
    'Failed to load audio': 'Չհաջողվեց բեռնել աուդիոն',
    'Office Preview': 'Office նախադիտում',
    'Archive Preview': 'Արխիվի նախադիտում',
    'Loading document...': 'Փաստաթուղթը բեռնվում է...',
    'Failed to load document': 'Չհաջողվեց բեռնել փաստաթուղթը',
    'This file type cannot be previewed': 'Այս ֆայլի տեսակը չի կարող նախադիտվել',
    'Empty archive': 'Արխիվը դատարկ է',
    'Failed to open archive': 'Չհաջողվեց բացել արխիվը',
    Extract: 'Արտահանել',
    'This archive is password-protected': 'Այս արխիվը պաշտպանված է գաղտնաբառով',
    'Open archive': 'Բացել արխիվը',
    'Edit folder: {{name}}': 'Խմբագրել թղթապանակը. {{name}}',
    'Edit folder': 'Խմբագրել թղթապանակը',
    'Name is required': 'Անունը պարտադիր է',
    Title: 'Վերնագիր',
    'Select File': 'Ընտրել ֆայլ',
    Upload: 'Վերբեռնել',
    'File is required': 'Ֆայլը պարտադիր է',
    'file name...': 'ֆայլի անուն...',
    Download: 'Ներբեռնել',
    'Copied!': 'Պատճենված է։',
    'Copy link': 'Պատճենել հղումը',
    'Public file': 'Հանրային ֆայլ',
    'Private file': 'Մասնավոր ֆայլ',
    Edit: 'Խմբագրել',
    'Configure workspace-level sections from one place.':
      'Կարգավորեք աշխատանքային միջավայրի բաժինները մեկ վայրից։',
    'Settings under development': 'Կարգավորումները մշակման փուլում են',
    Sections: 'Բաժիններ',
    'Workspace Settings': 'Աշխատանքային միջավայրի կարգավորումներ',
    Users: 'Օգտատերեր',
    'Manage members, roles, and access for this workspace.':
      'Կառավարեք անդամներին, դերերը և հասանելիությունը այս աշխատանքային միջավայրի համար։',
    'Failed to load users.': 'Չհաջողվեց բեռնել օգտատերերին։',
    'User created successfully.': 'Օգտատերը հաջողությամբ ստեղծվեց։',
    'Failed to update user.': 'Չհաջողվեց թարմացնել օգտատիրոջը։',
    'Failed to create user.': 'Չհաջողվեց ստեղծել օգտատիրոջը։',
    'You cannot delete your own user.':
      'Դուք չեք կարող ջնջել ձեր սեփական օգտատիրոջը։',
    'Failed to delete user.': 'Չհաջողվեց ջնջել օգտատիրոջը։',
    'Create User': 'Ստեղծել օգտատեր',
    'Workspace members': 'Աշխատանքային միջավայրի անդամներ',
    'Users directory': 'Օգտատերերի ցուցակ',
    'Review all users, update access, and create new accounts from this section.':
      'Դիտեք բոլոր օգտատերերին, թարմացրեք հասանելիությունը և ստեղծեք նոր հաշիվներ այս բաժնից։',
    'Loading users...': 'Օգտատերերը բեռնվում են...',
    'No users yet': 'Օգտատերեր դեռ չկան',
    'Create the first user for this workspace.':
      'Ստեղծեք առաջին օգտատիրոջը այս աշխատանքային միջավայրի համար։',
    Blocked: 'Արգելափակված',
    Created: 'Ստեղծված',
    'Edit user': 'Խմբագրել օգտատիրոջը',
    'Delete user': 'Ջնջել օգտատիրոջը',
    User: 'Օգտատեր',
    Admin: 'Ադմին',
    Hide: 'Թաքցնել',
    View: 'Դիտել',
    'Edit User': 'Խմբագրել օգտատիրոջը',
    'First name, last name, and email are required.':
      'Անունը, ազգանունը և էլ. հասցեն պարտադիր են։',
    'Enter a valid email address.': 'Մուտքագրեք ճիշտ էլ. հասցե։',
    'Password is required when creating a user.':
      'Օգտատեր ստեղծելիս գաղտնաբառը պարտադիր է։',
    'Repeat password is required when creating a user.':
      'Օգտատեր ստեղծելիս գաղտնաբառի կրկնությունը պարտադիր է։',
    'Password and repeat password are required.':
      'Գաղտնաբառը և դրա կրկնությունը պարտադիր են։',
    'Password and repeat password must match.':
      'Գաղտնաբառը և դրա կրկնությունը պետք է համընկնեն։',
    'First Name': 'Անուն',
    'Last Name': 'Ազգանուն',
    'First name': 'Անուն',
    'Last name': 'Ազգանուն',
    Email: 'Էլ. հասցե',
    Role: 'Դեր',
    Modules: 'Մոդուլներ',
    Code: 'Կոդ',
    Permission: 'Թույլտվություն',
    'Set all': 'Սահմանել բոլորը',
    'Enable private mode for this user':
      'Միացնել գաղտնի ռեժիմը այս օգտատիրոջ համար',
    'When private mode is on, only public modules are visible to this user.':
      'Երբ գաղտնի ռեժիմը միացված է, այս օգտատիրոջը երևում են միայն հանրային մոդուլները։',
    'You cannot change your own role or blocked status.':
      'Դուք չեք կարող փոխել ձեր սեփական դերը կամ արգելափակման կարգավիճակը։',
    'New Password': 'Նոր գաղտնաբառ',
    'Leave blank to keep current password':
      'Թողեք դատարկ՝ ընթացիկ գաղտնաբառը պահելու համար',
    'Repeat New Password': 'Կրկնել նոր գաղտնաբառը',
    'Repeat the new password': 'Կրկնեք նոր գաղտնաբառը',
    'Repeat Password': 'Կրկնել գաղտնաբառը',
    'Set each module to hide, view, or edit for this user.':
      'Սահմանեք յուրաքանչյուր մոդուլի համար թաքցնելու, դիտելու կամ խմբագրելու իրավունքներ։',
    'Admin users keep full access across every module.':
      'Ադմին օգտատերերը պահպանում են լիարժեք հասանելիություն բոլոր մոդուլներում։',
    'Update User': 'Թարմացնել օգտատիրոջը',
    Add: 'Ավելացնել',
    Word: 'Բառ',
    Done: 'Ավարտված',
    DeadLine: 'Վերջնաժամկետ',
    'Select date': 'Ընտրել ամսաթիվ',
    'Choose date': 'Ընտրել ամսաթիվ',
    'dd/mm/yyyy': 'օր/ամիս/տարի',
    'Invalid date': 'Անվավեր ամսաթիվ',
    Periodicity: 'Պարբերականություն',
    Notification: 'Ծանուցում',
    'Enable notification': 'Միացնել ծանուցումը',
    'Plan and manage your events':
      'Պլանավորեք և կառավարեք ձեր իրադարձությունները',
    'Add Event': 'Ավելացնել իրադարձություն',
    'Update Event': 'Թարմացնել իրադարձությունը',
    'No Events Yet': 'Իրադարձություններ դեռ չկան',
    'Create Event': 'Ստեղծել իրադարձություն',
    'Create your first event to start planning your schedule.':
      'Ստեղծեք ձեր առաջին իրադարձությունը՝ ձեր ժամանակացույցը պլանավորելու համար։',
    'event name...': 'իրադարձության անուն...',
    'event description...': 'իրադարձության նկարագրություն...',
    'event note...': 'իրադարձության նշում...',
    Once: 'Մեկ անգամ',
    'Every Year': 'Ամեն տարի',
    'By Dates': 'Ըստ ամսաթվերի',
    Period: 'Ժամանակահատված',
    'To Do': 'Անելու',
    Birthday: 'Ծննդյան օր',
    Death: 'Մահ',
    Vacation: 'Արձակուրդ',
    From: 'Սկսած',
    to: 'մինչև',
    'Are you really want to delete this event?':
      'Իսկապե՞ս ցանկանում եք ջնջել այս իրադարձությունը։',
    'Learn and manage your vocabulary': 'Սովորեք և կառավարեք ձեր բառապաշարը',
    'New Vocabulary': 'Նոր բառապաշար',
    'Select Vocabulary': 'Ընտրել բառապաշար',
    'Choose a vocabulary...': 'Ընտրեք բառապաշար...',
    'Add word': 'Ավելացնել բառ',
    'Add Word': 'Ավելացնել բառ',
    'Delete vocabulary': 'Ջնջել բառապաշարը',
    'Show translations': 'Ցույց տալ թարգմանությունները',
    'Show words': 'Ցույց տալ բառերը',
    Words: 'Բառեր',
    '{{count}} words': '{{count}} բառ',
    'No words yet': 'Բառեր դեռ չկան',
    'Start building your vocabulary by adding your first word':
      'Սկսեք կառուցել ձեր բառապաշարը՝ ավելացնելով առաջին բառը',
    'Add First Word': 'Ավելացնել առաջին բառը',
    'Select a vocabulary to start': 'Ընտրեք բառապաշար՝ սկսելու համար',
    'Choose a vocabulary from the dropdown above or create a new one':
      'Ընտրեք բառապաշար վերևի ցանկից կամ ստեղծեք նորը',
    'Add New Vocabulary': 'Ավելացնել նոր բառապաշար',
    'Add new vocabulary': 'Ավելացնել նոր բառապաշար',
    'Edit Vocabulary': 'Խմբագրել բառապաշարը',
    'Update vocabulary': 'Թարմացնել բառապաշարը',
    'Add New Word': 'Ավելացնել նոր բառ',
    'Add word to {{name}}': 'Ավելացնել բառ {{name}} բառապաշարին',
    'Edit Word': 'Խմբագրել բառը',
    'Update word in {{name}}': 'Թարմացնել բառը {{name}} բառապաշարում',
    'Are you sure you want to delete this vocabulary? All words will be lost.':
      'Վստա՞հ եք, որ ցանկանում եք ջնջել այս բառապաշարը։ Բոլոր բառերը կկորչեն։',
    'Are you sure you want to delete this word?':
      'Վստա՞հ եք, որ ցանկանում եք ջնջել այս բառը։',
    'Vocabulary Name': 'Բառապաշարի անուն',
    'Enter vocabulary name...': 'Մուտքագրեք բառապաշարի անունը...',
    'Words Voice Language': 'Բառերի ձայնային լեզու',
    'Select language for words': 'Ընտրեք լեզուն բառերի համար',
    'Translations Voice Language': 'Թարգմանությունների ձայնային լեզու',
    'Select language for translations':
      'Ընտրեք լեզուն թարգմանությունների համար',
    'Create Vocabulary': 'Ստեղծել բառապաշար',
    'Update Vocabulary': 'Թարմացնել բառապաշարը',
    'Enter word...': 'Մուտքագրեք բառը...',
    'Enter translation...': 'Մուտքագրեք թարգմանությունը...',
    'Dictionary (Optional)': 'Բացատրություն (ոչ պարտադիր)',
    'Add definition or notes...': 'Ավելացրեք սահմանում կամ նշումներ...',
    'Update Word': 'Թարմացնել բառը',
    'This word already exists in this vocabulary.':
      'Այս բառն արդեն կա այս բառապաշարում։',
    'Listen to word': 'Լսել բառը',
    'Listen to translation': 'Լսել թարգմանությունը',
    'Enable word pronunciation': 'Միացնել բառերի արտասանությունը',
    'Disable word pronunciation': 'Անջատել բառերի արտասանությունը',
    'Copy to clipboard': 'Պատճենել սեղմատախտակ',
    'More options': 'Այլ ընտրանքներ',
    Move: 'Տեղափոխել',
    'Move to vocabulary': 'Տեղափոխել բառապաշար',
    'No other vocabularies': 'Այլ բառապաշարներ չկան',
    'Track and manage your goals': 'Հետևեք և կառավարեք ձեր նպատակները',
    'Create New Objective': 'Ստեղծել նոր նպատակ',
    'New Objective': 'Նոր նպատակ',
    'No active objectives yet': 'Ակտիվ նպատակներ դեռ չկան',
    'Create your first objective': 'Ստեղծեք ձեր առաջին նպատակը',
    'No paused objectives': 'Դադարեցված նպատակներ չկան',
    'No completed objectives yet': 'Ավարտված նպատակներ դեռ չկան',
    'Add Objective': 'Ավելացնել նպատակ',
    'Edit Objective': 'Խմբագրել նպատակը',
    'Are you really want to delete this Objective?':
      'Իսկապե՞ս ցանկանում եք ջնջել այս նպատակը։',
    'Are you really want to delete this objective?':
      'Իսկապե՞ս ցանկանում եք ջնջել այս նպատակը։',
    'Are you really want to delete this ToDo?':
      'Իսկապե՞ս ցանկանում եք ջնջել այս անելիքը։',
    'Objective name...': 'Նպատակի անուն...',
    'Parent objective': 'Ծնող նպատակ',
    'Child objectives': 'Ենթանպատակներ',
    'parent objective': 'ծնող նպատակ',
    'No parent': 'Առանց ծնողի',
    'Select Priority': 'Ընտրեք առաջնահերթությունը',
    'Select Status': 'Ընտրեք կարգավիճակը',
    'Objective description...': 'Նպատակի նկարագրություն...',
    Passive: 'Դադարեցված',
    'Created at:': 'Ստեղծվել է՝',
    'Finished at:': 'Ավարտվել է՝',
    'Not finished yet': 'Դեռ ավարտված չէ',
    'Leave a comment here': 'Գրեք մեկնաբանությունը այստեղ',
    Steps: 'Քայլեր',
    Step: 'Քայլ',
    'Start Date': 'Սկզբի ամսաթիվ',
    'End Date': 'Ավարտի ամսաթիվ',
    'Add Step': 'Ավելացնել քայլ',
    Progress: 'Առաջընթաց',
    'No steps yet': 'Դեռ քայլեր չկան',
    'Add a step...': 'Ավելացնել քայլ...',
    'Search objectives...': 'Փնտրել նպատակներ...',
    'All priorities': 'Բոլոր առաջնահերթությունները',
    'Deadline (newest)': 'Վերջնաժամկետ (նոր)',
    'Deadline (oldest)': 'Վերջնաժամկետ (հին)',
    Activate: 'Ակտիվացնել',
    Pause: 'Դադար',
    Play: 'Նվագարկել',
    Complete: 'Ավարտել',
    'Toggle step': 'Փոխել քայլը',
    'Toggle to-do': 'Փոխել առաջադրանքը',
    'Update Step': 'Թարմացնել քայլը',
    'Are you really want to delete this step?':
      'Իսկապե՞ս ցանկանում եք ջնջել այս քայլը։',
    'This step cannot be deleted because it still has to-dos. Remove or complete all to-dos in this step first.':
      'Այս քայլը հնարավոր չէ ջնջել, քանի որ այն դեռ ունի անելիքներ: Նախ հեռացրեք կամ ավարտեք բոլոր անելիքները այս քայլում:',
    'Objective name is required': 'Նպատակի անունը պարտադիր է',
    "Objective deadLine can't be earlier then current day":
      'Նպատակի վերջնաժամկետը չի կարող լինել ընթացիկ օրվանից շուտ',
    'Priority is required': 'Առաջնահերթությունը պարտադիր է',
    'No whiteboards yet': 'Գրատախտակներ դեռ չկան',
    'No to-dos yet': 'Անելիքներ դեռ չկան',
    'Browse all scenarios and open details in the drawer.':
      'Դիտեք բոլոր սցենարները և բացեք մանրամասները կողային վահանակում։',
    'Create Scenario': 'Ստեղծել սցենար',
    'Loading scenarios...': 'Սցենարները բեռնվում են...',
    'No Scenarios Yet': 'Սցենարներ դեռ չկան',
    'Create your first scenario to start organizing story flows and reusable scripts.':
      'Ստեղծեք ձեր առաջին սցենարը՝ պատմության հոսքերն ու կրկնակի օգտագործվող սքրիպտները կազմակերպելու համար։',
    'No image': 'Պատկեր չկա',
    'Failed to load scenarios.': 'Չհաջողվեց բեռնել սցենարները։',
    'Failed to load scenario details.':
      'Չհաջողվեց բեռնել սցենարի մանրամասները։',
    'Failed to delete scenario.': 'Չհաջողվեց ջնջել սցենարը։',
    'Are you sure you want to delete this scenario?':
      'Վստա՞հ եք, որ ցանկանում եք ջնջել այս սցենարը։',
    'Failed to load scenario.': 'Չհաջողվեց բեռնել սցենարը։',
    'Name is required.': 'Անունը պարտադիր է։',
    'Failed to save scenario.': 'Չհաջողվեց պահպանել սցենարը։',
    'Loading scenario...': 'Սցենարը բեռնվում է...',
    'Edit Scenario': 'Խմբագրել սցենարը',
    'Manage scenario base fields and nested objects.':
      'Կառավարեք սցենարի հիմնական դաշտերն ու ներդրված օբյեկտները։',
    'Scenario name': 'Սցենարի անուն',
    Text: 'Տեքստ',
    'Scenario text': 'Սցենարի տեքստ',
    'Scenario description': 'Սցենարի նկարագրություն',
    'Main image': 'Հիմնական պատկեր',
    Gallery: 'Պատկերասրահ',
    'Add gallery image': 'Ավելացնել պատկեր պատկերասրահում',
    Places: 'Վայրեր',
    'No places added yet.': 'Վայրեր դեռ ավելացված չեն։',
    Characters: 'Կերպարներ',
    'No characters added yet.': 'Կերպարներ դեռ ավելացված չեն։',
    'Scenario Events': 'Սցենարի իրադարձություններ',
    'No events added yet.': 'Իրադարձություններ դեռ ավելացված չեն։',
    Artifacts: 'Արտեֆակտներ',
    Expand: 'Ընդլայնել',
    Collapse: 'Փոքրացնել',
    'No artifacts added yet.': 'Արտեֆակտներ դեռ ավելացված չեն։',
    'No whiteboards added yet.': 'Գրատախտակներ դեռ ավելացված չեն։',
    'World Maps': 'Աշխարհի քարտեզներ',
    'No maps added yet.': 'Քարտեզներ դեռ ավելացված չեն։',
    'Untitled map': 'Անանուն քարտեզ',
    'Map name': 'Քարտեզի անուն',
    Land: 'Ցամաք',
    'Paint land': 'Ներկել ցամաքը',
    'Outline part of the land to recolor, then press Enter or Finish shape.':
      'Ուրվագծեք ցամաքի մի մասը գույնը փոխելու համար, ապա Enter կամ Ավարտել ձևը։',
    Lake: 'Լիճ',
    River: 'Գետ',
    Road: 'Ճանապարհ',
    Mountain: 'Լեռ',
    Forest: 'Անտառ',
    Castle: 'Ամրոց',
    Village: 'Գյուղ',
    'Select tool': 'Ընտրել',
    'Pan tool': 'Տեղափոխել',
    'Finish shape': 'Ավարտել ձևը',
    'Delete selected': 'Ջնջել ընտրվածը',
    'Failed to save map.': 'Չհաջողվեց պահել քարտեզը։',
    'Loading map...': 'Քարտեզը բեռնվում է...',
    'Map not found.': 'Քարտեզը չի գտնվել։',
    'Delete this map?': 'Ջնջե՞լ այս քարտեզը։',
    'Delete this whiteboard?': 'Ջնջե՞լ այս գրատախտակը։',
    'Click to add points, then press Enter or Finish shape.':
      'Սեղմեք կետեր ավելացնելու համար, ապա Enter կամ Ավարտել ձևը։',
    'Click the map to place this object.':
      'Սեղմեք քարտեզի վրա՝ օբյեկտը տեղադրելու համար։',
    'Stamp several times and overlap them, working from back to front.':
      'Կնքեք մի քանի անգամ և համընկեք՝ հետևից առաջ։',
    'Each click uses a different shape. Overlap them from back to front.':
      'Ամեն սեղմումը այլ ձև է դնում։ Համընկեք՝ հետևից առաջ։',
    'Drag objects to move them. Scroll to zoom.':
      'Քաշեք օբյեկտները տեղափոխելու համար։ Սքրոլով մեծացրեք։',
    'Click to outline an area, then press Enter or Finish shape.':
      'Սեղմեք տարածքը ուրվագծելու համար, ապա Enter կամ Ավարտել ձևը։',
    'Outline the forest, then press Enter or Finish shape. Trees will mix automatically.':
      'Ուրվագծեք անտառը, ապա Enter կամ Ավարտել ձևը։ Ծառերը կխառնվեն իրենք։',
    'Outline the mountains, then press Enter or Finish shape.':
      'Ուրվագծեք լեռները, ապա Enter կամ Ավարտել ձևը։',
    'Choose a mountain type, then outline the range and press Enter or Finish shape.':
      'Ընտրեք լեռան տեսակը, ուրվագծեք շարքը, ապա Enter կամ Ավարտել ձևը։',
    'Click an object to erase it.': 'Սեղմեք օբյեկտի վրա՝ ջնջելու համար։',
    Eraser: 'Ռետին',
    'Forest fill': 'Անտառ լցնել',
    'Mountain fill': 'Լեռներ լցնել',
    'Snow peaks': 'Ձյունոտ գագաթներ',
    'Grey peaks': 'Մոխրագույն գագաթներ',
    'Rocky peaks': 'Ժայռոտ գագաթներ',
    Sparse: 'Հազվադեպ',
    Dense: 'Խիտ',
    Normal: 'Սովորական',
    Hills: 'Բլուրներ',
    Desert: 'Անապատ',
    Swamp: 'Ճահիճ',
    Snow: 'Ձյուն',
    Woods: 'Անտառածածկ',
    Volcano: 'Հրաբուխ',
    Hill: 'Բլուր',
    Sea: 'Ծով',
    Trail: 'Արահետ',
    Border: 'Սահման',
    Wall: 'Պարիսպ',
    Bridge: 'Կամուրջ',
    Town: 'Ավան',
    Port: 'Նավահանգիստ',
    Fortress: 'Բերդ',
    Tower: 'Աշտարակ',
    Monastery: 'Վանք',
    Ruins: 'Ավերակներ',
    Farm: 'Ագարակ',
    Mine: 'Հանք',
    Camp: 'Ճամբար',
    Lighthouse: 'Փարոս',
    Label: 'Մակագրություն',
    Navigate: 'Նավարկում',
    Terrain: 'Ռելիեֆ',
    Routes: 'Ուղիներ',
    Settlements: 'Բնակավայրեր',
    Marks: 'Նշաններ',
    Grid: 'Ցանց',
    'Fit map': 'Հարմարեցնել',
    'Map size': 'Քարտեզի չափ',
    Small: 'Փոքր',
    Large: 'Մեծ',
    Huge: 'Հսկա',
    Width: 'Լայնություն',
    Height: 'Բարձրություն',
    Pines: 'Սոճիներ',
    Tree: 'Ծառ',
    Pine: 'Սոճի',
    Compass: 'Կողմնացույց',
    'Scale bar': 'Մասշտաբ',
    Ship: 'Նավ',
    Waves: 'Ալիքներ',
    Cave: 'Քարանձավ',
    Waterfall: 'Ջրվեժ',
    Oasis: 'Օազիս',
    'Standing stones': 'Կանգնած քարեր',
    Windmill: 'Հողմաղաց',
    Dunes: 'Դյուներ',
    'Draw a continent or island coast, then press Enter or Finish shape.':
      'Նկարեք մայրցամաքի կամ կղզու ափը, ապա Enter կամ Ավարտել ձևը։',
    'Mark a rough land border, then Shape coast or Finish shape.':
      'Նշեք ցամաքի կոպիտ եզրը, ապա Ձևավորել ափը կամ Ավարտել ձևը։',
    'Mark a rough sea border, then Shape coast or Finish shape.':
      'Նշեք ծովի կոպիտ եզրը, ապա Ձևավորել ափը կամ Ավարտել ձևը։',
    'Mark a rough lake border, then Shape coast or Finish shape.':
      'Նշեք լճի կոպիտ եզրը, ապա Ձևավորել ափը կամ Ավարտել ձևը։',
    'Mark a rough forest border, then Shape edge or Finish shape.':
      'Նշեք անտառի կոպիտ եզրը, ապա Ձևավորել եզրը կամ Ավարտել ձևը։',
    'Choose a mountain type, then mark a rough range and Shape edge or Finish shape.':
      'Ընտրեք լեռան տեսակը, նշեք կոպիտ շարքը, ապա Ձևավորել եզրը կամ Ավարտել ձևը։',
    'Shape coast': 'Ձևավորել ափը',
    'Shaping coast...': 'Ափը ձևավորվում է...',
    'Shape edge': 'Ձևավորել եզրը',
    'Shaping edge...': 'Եզրը ձևավորվում է...',
    'From image': 'Նկարից',
    'Reading map...': 'Քարտեզը կարդացվում է...',
    'Failed to read map image.': 'Չհաջողվեց կարդալ քարտեզի նկարը։',
    'Map rebuilt from the image. You can undo.':
      'Քարտեզը վերակառուցվեց նկարից։ Կարող եք հետարկել։',
    Duplicate: 'Կրկնօրինակել',
    Properties: 'Հատկություններ',
    'Fill color': 'Լցման գույն',
    'Custom color': 'Սեփական գույն',
    'Default colors': 'Լռելյայն գույներ',
    Color: 'Գույն',
    Scale: 'Չափ',
    Rotation: 'Պտույտ',
    'Linked place': 'Կապված վայր',
    Rotate: 'Պտտել',
    Symbol: 'Նշան',
    'Next symbol': 'Հաջորդ նշան',
    'Choose a symbol': 'Ընտրեք նշան',
    'Choose a symbol, then click the map to place it.':
      'Ընտրեք նշանը, ապա սեղմեք քարտեզին՝ տեղադրելու համար։',
    'Bring forward': 'Առաջ բերել',
    'Send back': 'Հետ ուղարկել',
    'Select an object to edit its name, size and rotation.':
      'Ընտրեք օբյեկտ՝ անունը, չափը և պտույտը խմբագրելու համար։',
    'Update Scenario': 'Թարմացնել սցենարը',
    'Delete this item?': 'Ջնջե՞լ այս տարրը։',
    'Scenario details': 'Սցենարի մանրամասներ',
    'Select a scenario.': 'Ընտրեք սցենար։',
    'Created:': 'Ստեղծվել է՝',
    'No text.': 'Տեքստ չկա։',
    'No description.': 'Նկարագրություն չկա։',
    'No description': 'Նկարագրություն չկա',
    Place: 'Վայր',
    Event: 'Իրադարձություն',
    Character: 'Կերպար',
    Artifact: 'Արտեֆակտ',
    Image: 'Պատկեր',
    'No place': 'Վայր չկա',
    'Choose image file': 'Ընտրեք պատկերի ֆայլ',
    'Failed to upload image.': 'Չհաջողվեց վերբեռնել պատկերը։',
    'Uploading...': 'Վերբեռնում...',
    Remove: 'Հեռացնել',
    'No gallery images.': 'Պատկերասրահի պատկերներ չկան։',
    'Create New Purchase list': 'Ստեղծել գնումների նոր ցանկ',
    'No Purchase Lists Yet': 'Գնումների ցանկեր դեռ չկան',
    'Your purchase lists will appear here once this section starts being used.':
      'Ձեր գնումների ցանկերը կհայտնվեն այստեղ, երբ այս բաժինը սկսի օգտագործվել։',
    Participants: 'Մասնակիցներ',
    'Loading participants...': 'Մասնակիցները բեռնվում են...',
    'This event is currently blocked.':
      'Այս միջոցառումը ներկայումս արգելափակված է։',
    'Event not found. Please check the key.':
      'Միջոցառումը չի գտնվել։ Խնդրում ենք ստուգել բանալին։',
    'Please enter an event key.': 'Խնդրում ենք մուտքագրել միջոցառման բանալին։',
    'Private List': 'Փակ ցանկ',
    'Please enter the event key to view the participant list.':
      'Մուտքագրեք միջոցառման բանալին՝ մասնակիցների ցանկը դիտելու համար։',
    'Enter key': 'Մուտքագրեք բանալին',
    'Checking...': 'Ստուգվում է...',
    'Access List': 'Բացել ցանկը',
    Invited: 'Հրավիրված',
    Attending: 'Մասնակցող',
    'Not attending': 'Չի մասնակցում',
    Gifts: 'Նվերներ',
    'Husband Side': 'Փեսայի կողմ',
    'Wife Side': 'Հարսի կողմ',
    'No participants on this side.': 'Այս կողմում մասնակիցներ չկան։',
    Gift: 'Նվեր',
    Yes: 'Այո',
    No: 'Ոչ',
    Tags: 'Պիտակներ',
    Ingredients: 'Բաղադրիչներ',
    'No ingredients': 'Բաղադրիչներ չկան',
    '{{count}} ingredients': '{{count}} բաղադրիչ',
    'type ingredient': 'մուտքագրել բաղադրիչ',
    'Keep your favorite meals, ingredients, and cooking notes in one place':
      'Պահեք ձեր սիրելի ուտեստները, բաղադրիչները և խոհարարական նշումները մեկ տեղում',
    'Create Recipe': 'Ստեղծել բաղադրատոմս',
    'Create Menu': 'Ստեղծել մենյու',
    'Add Menu': 'Ավելացնել մենյու',
    'Edit Menu': 'Խմբագրել մենյուն',
    'Menu name...': 'Մենյուի անուն...',
    'Select recipes': 'Ընտրել բաղադրատոմսեր',
    '{{count}} recipes': '{{count}} բաղադրատոմս',
    'Are you really want to delete this Menu?':
      'Իսկապե՞ս ցանկանում եք ջնջել այս մենյուն։',
    'Group recipes into shared meals.':
      'Խմբավորեք բաղադրատոմսերը ընդհանուր ճաշերի մեջ։',
    'No Recipes Yet': 'Բաղադրատոմսեր դեռ չկան',
    'No Menus Yet': 'Մենյուներ դեռ չկան',
    'Create a menu and pick recipes to group meals together.':
      'Ստեղծեք մենյու և ընտրեք բաղադրատոմսեր՝ ճաշերը խմբավորելու համար։',
    'Create your first recipe to keep ingredients, cooking steps, and meal ideas together.':
      'Ստեղծեք ձեր առաջին բաղադրատոմսը, որպեսզի բաղադրիչները, պատրաստման քայլերը և գաղափարները պահվեն միասին։',
    'Add Recipe': 'Ավելացնել բաղադրատոմս',
    'Edit Recipe': 'Խմբագրել բաղադրատոմսը',
    'Are you really want to delete this Recipe?':
      'Իսկապե՞ս ցանկանում եք ջնջել այս բաղադրատոմսը։',
    'Recipe name...': 'Բաղադրատոմսի անուն...',
    Total: 'Ընդհանուր',
    Participating: 'Մասնակցում է',
    'Not participating': 'Չի մասնակցում',
    'Manage confirmations and guest list':
      'Կառավարեք հաստատումներն ու հյուրերի ցանկը',
    Rest: 'Մնացածը',
    'Husband side participants': 'Փեսայի կողմի մասնակիցներ',
    'No Husband Side Participants': 'Փեսայի կողմի մասնակիցներ չկան',
    'Participants for the husband side will appear here once people respond or are added.':
      'Փեսայի կողմի մասնակիցները կհայտնվեն այստեղ, երբ մարդիկ պատասխանեն կամ ավելացվեն։',
    'Husband side': 'Փեսայի կողմ',
    'Wife side participants': 'Հարսի կողմի մասնակիցներ',
    'No Wife Side Participants': 'Հարսի կողմի մասնակիցներ չկան',
    'Participants for the wife side will appear here once people respond or are added.':
      'Հարսի կողմի մասնակիցները կհայտնվեն այստեղ, երբ մարդիկ պատասխանեն կամ ավելացվեն։',
    'Wife side': 'Հարսի կողմ',
    'Are you really want to delete this Participant?':
      'Իսկապե՞ս ցանկանում եք ջնջել այս մասնակցին։',
    daysShort: 'օր',
    hoursShort: 'ժամ',
    minutesShort: 'րոպե',
    secondsShort: 'վրկ',
    'Loading invitation...': 'Հրավերը բեռնվում է...',
    'Wedding event not found': 'Հարսանեկան միջոցառումը չի գտնվել',
    'We warmly invite you': 'Սիրով հրավիրում ենք ձեզ',
    'to our wedding ceremony': 'մեր հարսանյաց արարողությանը',
    'Respectfully,': 'Հարգանքով՝',
    'Time left': 'Մնացել է',
    Schedule: 'Օրվա ծրագիր',
    'How to get there': 'Ինչպես հասնել',
    'Please confirm your attendance': 'Խնդրում ենք հաստատել ձեր ներկայությունը',
    'Thank you': 'Շնորհակալություն',
    'Please fill in the field': 'Խնդրում ենք լրացնել դաշտը',
    'We will attend': 'Մենք կգանք',
    'We cannot attend': 'Մենք չենք կարող գալ',
    'Guest count': 'Հյուրերի քանակը',
    Send: 'Ուղարկել',
    'Contact us': 'Կապ մեզ հետ',
    'Wedding Events': 'Հարսանեկան միջոցառումներ',
    'Manage invitations, schedules, and event access from one place.':
      'Կառավարեք հրավիրատոմսերը, ժամանակացույցերը և միջոցառման հասանելիությունը մեկ տեղից։',
    'Create New Event': 'Ստեղծել նոր միջոցառում',
    'Total events': 'Ընդհանուր միջոցառումներ',
    'Gift info enabled': 'Նվերի տվյալները միացված են',
    'Edit Wedding Event': 'Խմբագրել հարսանեկան միջոցառումը',
    'Create New Wedding Event': 'Ստեղծել նոր հարսանեկան միջոցառում',
    'Event Slug (URL-friendly)': 'Միջոցառման սլագ (URL-ի համար)',
    'e.g., arman-armine-2025': 'օր.՝ arman-armine-2025',
    'This will be used in the URL: /wedding/[slug]/invitation':
      'Սա կօգտագործվի URL-ում՝ /wedding/[slug]/invitation',
    'Event Title': 'Միջոցառման վերնագիր',
    'e.g., Arman & Armine Wedding': 'օր.՝ Arman & Armine Wedding',
    'Husband Name': 'Փեսայի անուն',
    'e.g., Arman': 'օր.՝ Արման',
    'Wife Name': 'Հարսի անուն',
    'e.g., Armine': 'օր.՝ Արմինե',
    'Wedding Date': 'Հարսանիքի ամսաթիվ',
    'Hero Image URL (Optional)': 'Գլխավոր նկարի URL (ոչ պարտադիր)',
    'If provided, this image will replace the default wedding hero image on the invitation page':
      'Եթե նշվի, այս նկարը կհայտնվի հրավիրատոմսի էջում լռելյայն գլխավոր նկարի փոխարեն։',
    Configuration: 'Կարգավորումներ',
    'Block Public Access': 'Արգելափակել հանրային հասանելիությունը',
    'When enabled, public access to this wedding will be blocked, but it will still be visible in the main list.':
      'Միացնելու դեպքում այս հարսանիքի հանրային հասանելիությունը կփակվի, բայց այն դեռ տեսանելի կլինի հիմնական ցանկում։',
    'Show Gift Information': 'Ցույց տալ նվերի տվյալները',
    'When enabled, users will be able to see gift information on public pages.':
      'Միացնելու դեպքում օգտվողները հանրային էջերում կտեսնեն նվերի տվյալները։',
    'Location {{index}}': 'Վայր {{index}}',
    'Venue Name': 'Վայրի անվանում',
    'e.g., Ceremony': 'օր.՝ Պսակադրություն',
    'Place Name': 'Տեղի անվանում',
    'e.g., Saint Cross Church': 'օր.՝ Սուրբ Խաչ եկեղեցի',
    'Icon Type': 'Պատկերակի տեսակ',
    'Select icon type...': 'Ընտրեք պատկերակի տեսակը...',
    'Map Link': 'Քարտեզի հղում',
    'Image URL (Optional)': 'Նկարի URL (ոչ պարտադիր)',
    'No Wedding Events Yet': 'Հարսանեկան միջոցառումներ դեռ չկան',
    'Create your first wedding event to manage invitations, schedules, and participants from one place.':
      'Ստեղծեք ձեր առաջին հարսանեկան միջոցառումը, որպեսզի հրավիրատոմսերը, ժամանակացույցերը և մասնակիցները կառավարվեն մեկ տեղից։',
    'Public blocked': 'Հանրային մուտքը փակ է',
    'Public active': 'Հանրային մուտքը բաց է',
    'Gift info on': 'Նվերի տվյալները միացված են',
    'Gift info off': 'Նվերի տվյալներն անջատված են',
    '{{count}} locations': '{{count}} վայր',
    'Copy URL': 'Պատճենել հղումը',
    'View Invitation': 'Դիտել հրավիրատոմսը',
    'Copy Participants URL': 'Պատճենել մասնակիցների հղումը',
    Slug: 'Սլագ',
    'Invitation URL': 'Հրավիրատոմսի հղում',
    at: 'ժամը',
    'Are you sure you want to delete "{{title}}"? This action cannot be undone.':
      'Վստա՞հ եք, որ ցանկանում եք ջնջել "{{title}}"-ը։ Այս գործողությունը հնարավոր չէ չեղարկել։',
  },
  ru: {
    appName: 'IVA',
    Home: 'Главная',
    Notes: 'Заметки',
    Memories: 'Воспоминания',
    'To-Dos': 'Задачи',
    Ideas: 'Идеи',
    Links: 'Ссылки',
    Recipes: 'Рецепты',
    Recipe: 'Рецепт',
    Food: 'Еда',
    Menus: 'Меню',
    Menu: 'Меню',
    Movies: 'Фильмы',
    Locations: 'Места',
    Objectives: 'Цели',
    Objective: 'Цель',
    Events: 'События',
    Translation: 'Переводы',
    Whiteboards: 'Доски',
    Files: 'Файлы',
    Lunar: 'Lunar',
    Recordings: 'Записи',
    'Microphone recordings from Lunar, grouped by user.':
      'Записи микрофона Lunar, сгруппированные по пользователям.',
    '{{count}} recordings': '{{count}} записей',
    'No recordings yet': 'Записей пока нет',
    'This user has no recordings yet.': 'У этого пользователя пока нет записей.',
    'Could not load recordings.': 'Не удалось загрузить записи.',
    'Loading recordings...': 'Загрузка записей...',
    'Unknown user': 'Неизвестный пользователь',
    'Are you sure you want to delete this recording?':
      'Удалить эту запись?',
    'Could not delete the recording.': 'Не удалось удалить запись.',
    Scenarios: 'Сценарии',
    Settings: 'Настройки',
    Purchases: 'Покупки',
    Income: 'Доход',
    Regular: 'Регулярный',
    Expected: 'Ожидается',
    Received: 'Получено',
    'Income Date': 'Дата дохода',
    Source: 'Источник',
    'Create Income': 'Создать доход',
    'Add Income': 'Добавить доход',
    'Edit Income': 'Редактировать доход',
    'Income Details': 'Детали дохода',
    'No Income Yet': 'Дохода пока нет',
    '{{count}} incomes': '{{count}} доходов',
    'Income filters': 'Фильтры дохода',
    'Narrow income by name or date':
      'Отфильтруйте доход по названию или дате',
    'No matching income': 'Нет подходящего дохода',
    'Try a different name or date range.':
      'Попробуйте другое название или диапазон дат.',
    'Track salary, bonuses, and other inflows in one place.':
      'Отслеживайте зарплату, бонусы и другие поступления в одном месте.',
    'Add regular income such as salary, or occasional inflows like bonuses and gifts.':
      'Добавьте регулярный доход, например зарплату, или разовые поступления — бонусы и подарки.',
    'Enter income source': 'Введите источник дохода',
    'Enter amount': 'Введите сумму',
    'Gross salary': 'Оклад брутто',
    'Net salary': 'Оклад нетто',
    'Enter gross salary': 'Введите зарплату брутто',
    'Enter net salary': 'Введите зарплату нетто',
    'Scan Payslip': 'Сканировать расчётный лист',
    'Upload a payslip image or PDF to autofill employer, net pay, date, and type. The file is saved with the income. Manual edit still works.':
      'Загрузите изображение или PDF расчётного листа, чтобы заполнить работодателя, чистую сумму, дату и тип. Файл сохраняется вместе с доходом. Можно править вручную.',
    'Payslip preview': 'Предпросмотр расчётного листа',
    'Payslip document': 'Расчётный лист',
    'Choose payslip file': 'Выбрать файл расчётного листа',
    'Clear File': 'Очистить файл',
    'Payslip analyzed and filled': 'Расчётный лист проанализирован и заполнен',
    'Payslip analyzed. Review fields, then save.':
      'Расчётный лист проанализирован. Проверьте поля и сохраните.',
    'Failed to analyze payslip.': 'Не удалось проанализировать расчётный лист.',
    Salary: 'Зарплата',
    Freelance: 'Фриланс',
    Business: 'Бизнес',
    Investment: 'Инвестиции',
    Dividends: 'Дивиденды',
    Rental: 'Аренда',
    Pension: 'Пенсия',
    Refund: 'Возврат',
    Bonus: 'Бонус',
    Daily: 'Ежедневные',
    Analytics: 'Аналитика',
    'Spend analytics': 'Аналитика расходов',
    'Income analytics': 'Аналитика доходов',
    Comparison: 'Сравнение',
    'Utility bills': 'Коммунальные',
    'Add Bill': 'Добавить счет',
    'Bill Date': 'Дата счета',
    'Bill Cost': 'Сумма счета',
    'Company Name': 'Название компании',
    Provider: 'Поставщик',
    'Counter m³': 'Счётчик м³',
    'Month spend in m³': 'Расход за месяц м³',
    'Price per kW': 'Цена за kW',
    'Month cost': 'Стоимость за месяц',
    'Month spend in m³ × {{factor}} × Price per kW':
      'Расход за месяц м³ × {{factor}} × цена за kW',
    'Bill document': 'Документ счёта',
    'Choose bill file': 'Выбрать файл счёта',
    'Upload an image or PDF of the bill.':
      'Загрузите изображение или PDF счёта.',
    'Remove file': 'Удалить файл',
    'Scan gas bill': 'Сканировать счёт за газ',
    'Upload an image or PDF of the bill to autofill provider, readings, and prices.':
      'Загрузите изображение или PDF счёта, чтобы заполнить поставщика, показания и цены.',
    'Gas bill analyzed and filled': 'Счёт за газ проанализирован и заполнен',
    'Gas bill analyzed. Review fields, then save.':
      'Счёт за газ проанализирован. Проверьте поля и сохраните.',
    'Failed to analyze gas bill.': 'Не удалось проанализировать счёт за газ.',
    'Choose bill files': 'Выбрать файлы счёта',
    'Add bill files': 'Добавить файлы счёта',
    'Upload one or more images or PDFs of the bill. All files are saved with the bill, and Analyze uses every page.':
      'Загрузите одно или несколько изображений/PDF счёта. Все файлы сохраняются вместе со счётом, анализ использует все страницы.',
    'Failed to save gas bill.': 'Не удалось сохранить счёт за газ.',
    'Scan water bill': 'Сканировать счёт за воду',
    'Water bill analyzed and filled': 'Счёт за воду проанализирован и заполнен',
    'Water bill analyzed. Review fields, then save.':
      'Счёт за воду проанализирован. Проверьте поля и сохраните.',
    'Failed to analyze water bill.': 'Не удалось проанализировать счёт за воду.',
    'Failed to save water bill.': 'Не удалось сохранить счёт за воду.',
    'Contract number': 'Номер договора',
    'Current reading m³': 'Текущее показание м³',
    'Consumption m³': 'Расход м³',
    'Price per m³': 'Цена за м³',
    'Includes water, sewer, VAT, and waste taxes.':
      'Включает воду, канализацию, НДС и налоги на отходы.',
    'Scan electricity bill': 'Сканировать счёт за электричество',
    'Electricity bill analyzed and filled':
      'Счёт за электричество проанализирован и заполнен',
    'Electricity bill analyzed. Review fields, then save.':
      'Счёт за электричество проанализирован. Проверьте поля и сохраните.',
    'Failed to analyze electricity bill.':
      'Не удалось проанализировать счёт за электричество.',
    'Failed to save electricity bill.':
      'Не удалось сохранить счёт за электричество.',
    'Contracted power kW': 'Договорная мощность kW',
    'Includes power, energy, discounts, and taxes.':
      'Включает мощность, энергию, скидки и налоги.',
    'Bill documents': 'Документы счёта',
    Invoice: 'Счёт',
    'Invoice number': 'Номер счёта',
    'Supply address': 'Адрес поставки',
    'Postal code': 'Почтовый индекс',
    'Billing period': 'Расчётный период',
    'Meter number': 'Номер счётчика',
    CUPS: 'CUPS',
    'Previous reading m³': 'Предыдущее показание м³',
    'Meter and consumption': 'Счётчик и расход',
    Prices: 'Цены',
    'Conversion factor kWh/m³': 'Коэффициент kWh/m³',
    'Energy kWh': 'Энергия kWh',
    'Price per kWh': 'Цена за kWh',
    'Energy cost': 'Стоимость энергии',
    'Total invoice': 'Итого по счёту',
    'Amount to pay or credit. Negative means a refund.':
      'Сумма к оплате или возврату. Отрицательная — это возврат.',
    'Gas bill total (Importe Factura). Extra maintenance invoices stay in notes.':
      'Итого по счёту за газ (Importe Factura). Отдельные счета за обслуживание остаются в заметках.',
    'Energy kWh × Price per kWh. This is not the invoice total.':
      'Энергия kWh × цена за kWh. Это не итоговая сумма счёта.',
    'Month spend in m³ × {{factor}} × Price per kWh':
      'Расход за месяц м³ × {{factor}} × цена за kWh',
    'Optional notes': 'Дополнительные заметки',
    'Add {{category}} Bill': 'Добавить счет: {{category}}',
    'Edit {{category}} Bill': 'Редактировать счет: {{category}}',
    'Are you sure you want to delete this bill?':
      'Вы уверены, что хотите удалить этот счет?',
    'Utility Bills Total': 'Сумма коммунальных',
    'All Utility Bills': 'Все коммунальные',
    'Utility Bills Timeline': 'Хронология коммунальных',
    'No utility bills in this period.': 'В этом периоде коммунальных платежей нет.',
    'No utility bills on this day.': 'В этот день коммунальных платежей нет.',
    'No utility bills in this month.': 'В этом месяце коммунальных платежей нет.',
    'No utility bills in this year.': 'В этом году коммунальных платежей нет.',
    Bills: 'Счета',
    Bill: 'Счет',
    'Bill Details': 'Детали счета',
    'Edit Bill': 'Редактировать счет',
    Price: 'Цена',
    Currency: 'Валюта',
    Close: 'Закрыть',
    'Created At': 'Создано',
    'Spend charts for all utility bills together and for each utility category.':
      'Графики расходов по всем коммунальным платежам и по каждой категории.',
    'Breakdown of utility spend across every utility category.':
      'Распределение коммунальных расходов по всем категориям.',
    'Total utility bill spend over time across all categories.':
      'Общие коммунальные расходы во времени по всем категориям.',
    'Add daily purchases or utility bills first, then this page will show spend charts by category.':
      'Сначала добавьте ежедневные покупки или коммунальные платежи — затем здесь появятся графики расходов.',
    'No Utility Bills Yet': 'Коммунальных платежей пока нет',
    'Track electricity, water, gas, internet, and other recurring household bills in one place — amounts, periods, and providers.':
      'Отслеживайте электричество, воду, газ, интернет и другие регулярные коммунальные платежи в одном месте — суммы, периоды и поставщиков.',
    Electricity: 'Электричество',
    Gas: 'Газ',
    Internet: 'Интернет',
    Phone: 'Телефон',
    Rent: 'Аренда',
    Telecommunications: 'Телекоммуникации',
    'Mortgage/Credit': 'Ипотека/Кредит',
    Mortgage: 'Ипотека',
    Credit: 'Кредит',
    'Bank Name': 'Название банка',
    Amount: 'Сумма',
    Percentage: 'Процент',
    'Monthly Quota': 'Ежемесячный платеж',
    'Monthly Quotas': 'Ежемесячные платежи',
    'Main Quota': 'Основной платеж',
    'Percentage Quota': 'Процентный платеж',
    'Select period start and end to generate monthly quota rows.':
      'Выберите начало и конец периода, чтобы создать строки ежемесячных платежей.',
    'For each month, fill both Main Quota and Percentage Quota, or leave both empty.':
      'Для каждого месяца заполните и основной платеж, и процентный платеж — или оставьте оба поля пустыми.',
    'Total To Pay': 'Итого к оплате',
    Paid: 'Оплачено',
    Remaining: 'Остаток',
    'Payment Progress': 'Прогресс оплаты',
    'Payments are counted at the end of each month. {{paidMonths}} of {{plannedMonths}} monthly payments completed.':
      'Платежи учитываются в конце каждого месяца. Выполнено {{paidMonths}} из {{plannedMonths}} ежемесячных платежей.',
    'Period Start': 'Начало периода',
    'Period End': 'Конец периода',
    'Optional description': 'Дополнительное описание',
    'No {{category}} Bills Yet': 'Пока нет счетов: {{category}}',
    'Track electricity bills here — amounts, periods, and providers.':
      'Отслеживайте счета за электричество здесь — суммы, периоды и поставщиков.',
    'Track water bills here — amounts, periods, and providers.':
      'Отслеживайте счета за воду здесь — суммы, периоды и поставщиков.',
    'Track gas bills here — amounts, periods, and providers.':
      'Отслеживайте счета за газ здесь — суммы, периоды и поставщиков.',
    'Track internet, phone, and other telecom bills here — amounts, periods, and providers.':
      'Отслеживайте счета за интернет, телефон и другие телеком-услуги здесь — суммы, периоды и поставщиков.',
    'Track mortgage and credit payments here — amounts, periods, and providers.':
      'Отслеживайте платежи по ипотеке и кредиту здесь — суммы, периоды и поставщиков.',
    Trash: 'Вывоз мусора',
    'No Comparison Data Yet': 'Данных для сравнения пока нет',
    'Add daily purchases from receipts first. Comparison tables are built by category, with markets as columns.':
      'Сначала добавьте ежедневные покупки из чеков. Таблицы сравнения строятся по категориям, а рынки — это колонки.',
    'Each table groups receipt products by category. Columns are markets — if one market has several products of the same name, it gets extra columns.':
      'Каждая таблица группирует товары из чеков по категории. Колонки — это магазины: если в одном магазине несколько товаров с одним именем, появляются дополнительные колонки.',
    Product: 'Товар',
    Markets: 'Магазины',
    Types: 'Типы',
    'Each table is a category. Rows are specific product types (Beer, Coffee, Milk…). Columns are markets. Green is lowest price, red is highest.':
      'Каждая таблица — категория. Строки — конкретные типы товаров (Пиво, Кофе, Молоко…). Колонки — магазины. Зелёный — самая низкая цена, красный — самая высокая.',
    Beer: 'Пиво',
    Wine: 'Вино',
    Cider: 'Сидр',
    Spirits: 'Крепкий алкоголь',
    'Soft Drink': 'Газировка',
    Juice: 'Сок',
    'Energy Drink': 'Энергетик',
    Coffee: 'Кофе',
    Tea: 'Чай',
    Milk: 'Молоко',
    'Plant Milk': 'Растительное молоко',
    Yogurt: 'Йогурт',
    Cheese: 'Сыр',
    Butter: 'Масло',
    Cream: 'Сливки',
    Bread: 'Хлеб',
    Pastry: 'Выпечка',
    Chicken: 'Курица',
    Beef: 'Говядина',
    Pork: 'Свинина',
    Fish: 'Рыба',
    Seafood: 'Морепродукты',
    Fruit: 'Фрукты',
    Vegetables: 'Овощи',
    Pasta: 'Паста',
    Rice: 'Рис',
    Flour: 'Мука',
    Sugar: 'Сахар',
    'Salt & Spices': 'Соль и специи',
    'Ready Meal': 'Готовое блюдо',
    Tofu: 'Тофу',
    Oil: 'Масло растительное',
    Sauce: 'Соус',
    Snacks: 'Снеки',
    Chocolate: 'Шоколад',
    Candy: 'Конфеты',
    Cereal: 'Хлопья',
    'Ice Cream': 'Мороженое',
    'Frozen Meal': 'Замороженное блюдо',
    Shampoo: 'Шампунь',
    'Hair Spray': 'Лак для волос',
    Deodorant: 'Дезодорант',
    Soap: 'Мыло',
    Toothpaste: 'Зубная паста',
    Toothbrush: 'Зубная щётка',
    Razor: 'Бритва',
    'Feminine Care': 'Женская гигиена',
    Detergent: 'Стиральный порошок',
    Cleaner: 'Чистящее средство',
    'Toilet Paper': 'Туалетная бумага',
    'Paper Towels': 'Бумажные полотенца',
    Dishwashing: 'Средство для посуды',
    'Air Freshener': 'Освежитель воздуха',
    'Trash Bags': 'Мусорные пакеты',
    Diapers: 'Подгузники',
    'Baby Food': 'Детское питание',
    'Pet Food': 'Корм для животных',
    'Cat Litter': 'Наполнитель для кошачьего туалета',
    Medicine: 'Лекарство',
    Vitamins: 'Витамины',
    Batteries: 'Батарейки',
    'No Daily Purchases Yet': 'Ежедневных покупок пока нет',
    'Create a daily purchase and track all products bought in one market visit.':
      'Создайте ежедневную покупку и отслеживайте все товары, купленные за один поход в магазин.',
    Occasional: 'Разовые',
    'No Occasional Purchases Yet': 'Разовых покупок пока нет',
    'Add non-daily purchases here — larger buys or smaller one-offs — separate from day-to-day shopping.':
      'Добавляйте сюда неповседневные покупки — крупные или небольшие разовые — отдельно от ежедневных расходов.',
    'Comparison Is Coming Soon': 'Сравнение скоро появится',
    'This tab is intentionally left as a placeholder for now. We can return later and build product comparison.':
      'Эта вкладка пока оставлена как заглушка. Позже можно вернуться и сделать сравнение товаров.',
    'Purchase Details': 'Детали покупки',
    'Create Daily Purchase': 'Создать ежедневную покупку',
    'Create Occasional Purchase': 'Создать разовую покупку',
    'Edit Daily Purchase': 'Редактировать ежедневную покупку',
    'Add Daily Purchase': 'Добавить ежедневную покупку',
    'Edit Occasional Purchase': 'Редактировать разовую покупку',
    'Add Occasional Purchase': 'Добавить разовую покупку',
    'Manage your daily shopping, track occasional purchases separately, and leave room for a future products catalog.':
      'Управляйте повседневными покупками, отдельно учитывайте разовые покупки и оставляйте место для будущего каталога товаров.',
    'Scan Receipt': 'Сканировать чек',
    'Upload a receipt/check photo to autofill products. Manual add still works.':
      'Загрузите фото чека, чтобы автоматически заполнить товары. Ручное добавление тоже доступно.',
    'Scan Purchase Image': 'Сканировать изображение покупки',
    'Upload an invoice, receipt, or product photo to autofill this occasional purchase. Manual edit still works.':
      'Загрузите счет, чек или фото товара, чтобы заполнить разовую покупку. Можно править вручную.',
    'Choose purchase image': 'Выбрать изображение покупки',
    'Purchase image preview': 'Превью изображения покупки',
    'Analyze & Fill Form': 'Проанализировать и заполнить форму',
    'Purchase image analyzed and filled': 'Изображение покупки проанализировано и заполнено',
    'Purchase image analyzed. Review fields, then save.':
      'Изображение покупки проанализировано. Проверьте поля и сохраните.',
    'Failed to analyze purchase image.': 'Не удалось проанализировать изображение покупки.',
    'Review and edit if needed.': 'Проверьте и при необходимости отредактируйте.',
    'Receipt preview': 'Предпросмотр чека',
    'Choose receipt image': 'Выбрать изображение чека',
    'Analyzing...': 'Анализ...',
    'Analyze & Fill List': 'Проанализировать и заполнить список',
    'Clear Image': 'Очистить изображение',
    'Market Name': 'Название магазина',
    'Enter market name': 'Введите название магазина',
    'Total Amount': 'Итоговая сумма',
    'Receipt Number': 'Номер чека',
    'Payment Method': 'Способ оплаты',
    'Ticket / invoice number': 'Номер чека / счета',
    'card, cash, bizum...': 'карта, наличные, bizum...',
    'Add Product': 'Добавить товар',
    'Enter product name': 'Введите название товара',
    'Line Total': 'Сумма строки',
    'Line importe / total': 'Сумма строки / итог',
    'Receipt analyzed and filled': 'Чек проанализирован и заполнен',
    'Review and edit if needed. You can still add more manually.':
      'Проверьте и при необходимости отредактируйте. Вы все еще можете добавить больше вручную.',
    'Receipt analyzed. Review products, then save. You can still add more manually.':
      'Чек проанализирован. Проверьте товары, затем сохраните. При необходимости можно добавить еще вручную.',
    'Failed to analyze receipt image.': 'Не удалось проанализировать изображение чека.',
    'No Analytics Yet': 'Аналитики пока нет',
    'Add daily purchases with products first, then this analytics tab will show spend charts by category.':
      'Сначала добавьте ежедневные покупки с товарами, и тогда эта вкладка покажет графики расходов по категориям.',
    'Add income first, then this page will show charts by type.':
      'Сначала добавьте доход, и тогда эта страница покажет графики по типу.',
    'See charts for salary, bonuses, and other inflows over time.':
      'Смотрите графики зарплаты, бонусов и других поступлений во времени.',
    'Total Income': 'Общий доход',
    'Regular Income': 'Регулярный доход',
    'Occasional Income': 'Разовый доход',
    'Income Records': 'Записи дохода',
    'Income by Type': 'Доход по типу',
    'Breakdown of income across salary, freelance, and other types.':
      'Распределение дохода по зарплате, фрилансу и другим типам.',
    'No income in this period.': 'В этом периоде дохода нет.',
    'Daily Spend': 'Ежедневные траты',
    'Occasional Purchases Total': 'Сумма разовых покупок',
    'Total Purchases': 'Количество покупок',
    'Tracked Products': 'Отслеживаемые товары',
    'Top Category': 'Главная категория',
    Dairy: 'Молочные продукты',
    Eggs: 'Яйца',
    Meat: 'Мясо',
    'Fish & Seafood': 'Рыба и морепродукты',
    'Fruit & Veg': 'Фрукты и овощи',
    Water: 'Вода',
    Alcohol: 'Алкоголь',
    'Other Drinks': 'Другие напитки',
    Drinks: 'Напитки',
    Average: 'Среднее',
    Bakery: 'Выпечка',
    'Pasta & Rice': 'Паста и рис',
    'Oils & Condiments': 'Масла и приправы',
    'Canned Food': 'Консервы',
    Frozen: 'Замороженные',
    'Breakfast & Cereals': 'Завтраки и хлопья',
    Household: 'Бытовые товары',
    Hygiene: 'Гигиена',
    'Personal Care': 'Уход за собой',
    Baby: 'Детские товары',
    Pet: 'Для животных',
    Pharmacy: 'Аптека',
    'Ready Meals': 'Готовые блюда',
    'Snacks & Sweets': 'Снеки и сладости',
    'Coffee & Tea': 'Кофе и чай',
    Charcuterie: 'Колбасные изделия',
    Legumes: 'Бобовые',
    'Nuts & Seeds': 'Орехи и семена',
    'Baking Ingredients': 'Для выпечки',
    'Plant-based': 'Растительные продукты',
    Tobacco: 'Табак',
    Stationery: 'Канцтовары',
    'Batteries & Tech': 'Батарейки и техника',
    'Flowers & Plants': 'Цветы и растения',
    Computers: 'Компьютеры',
    'Phones & Tablets': 'Телефоны и планшеты',
    Electronics: 'Электроника',
    'TV & Audio': 'ТВ и аудио',
    Gaming: 'Игры',
    'Camera & Photo': 'Фото и видео',
    'Smart Home': 'Умный дом',
    Furniture: 'Мебель',
    'Bedding & Mattresses': 'Постель и матрасы',
    Appliances: 'Бытовая техника',
    Kitchenware: 'Кухонная утварь',
    'Home Improvement': 'Ремонт дома',
    Vehicles: 'Транспорт',
    Bicycles: 'Велосипеды',
    Clothing: 'Одежда',
    'Shoes & Accessories': 'Обувь и аксессуары',
    'Jewelry & Watches': 'Украшения и часы',
    Eyewear: 'Очки',
    'Luggage & Bags': 'Чемоданы и сумки',
    'Home & Decor': 'Дом и декор',
    'Garden & Outdoor': 'Сад и улица',
    Sports: 'Спорт',
    'Fitness Equipment': 'Фитнес-оборудование',
    'Tools & DIY': 'Инструменты',
    'Office Equipment': 'Офисная техника',
    'Baby Gear': 'Детское оборудование',
    'Toys & Kids': 'Игрушки',
    Pets: 'Питомцы',
    'Musical Instruments': 'Музыкальные инструменты',
    'Books & Media': 'Книги и медиа',
    'Hobbies & Collectibles': 'Хобби и коллекции',
    'Health Equipment': 'Медоборудование',
    Education: 'Образование',
    'Software & Digital': 'ПО и цифровое',
    Travel: 'Путешествия',
    'Spend by Category': 'Расходы по категориям',
    'Based on daily-purchase product lines and their estimated totals.':
      'Основано на строках товаров в ежедневных покупках и их расчетных суммах.',
    'Category Comparison': 'Сравнение категорий',
    'Higher bars show categories where most of your money goes.':
      'Более высокие столбцы показывают категории, на которые уходит больше всего денег.',
    'Spend Map': 'Карта расходов',
    'Larger tiles show categories where most of your money goes.':
      'Более крупные плитки показывают категории, на которые уходит больше всего денег.',
    Category: 'Категория',
    Products: 'Товары',
    Qty: 'Кол-во',
    'No products in this category.': 'В этой категории нет товаров.',
    'Click a category to see its products.':
      'Нажмите на категорию, чтобы увидеть список товаров.',
    'Last month': 'Последний месяц',
    'Last 3 months': 'Последние 3 месяца',
    'Last 6 months': 'Последние 6 месяцев',
    'No purchases in this period.': 'В этом периоде покупок нет.',
    'Spend Timeline': 'Хронология расходов',
    'Income Timeline': 'Хронология доходов',
    'By year': 'По годам',
    'By month': 'По месяцам',
    'See how your income changes over time across recorded inflows.':
      'Посмотрите, как меняется ваш доход со временем на основе сохраненных поступлений.',
    'See how your income changes over time across recorded inflows. Click a month to see its income.':
      'Посмотрите, как меняется ваш доход со временем. Нажмите на месяц, чтобы увидеть доход за этот месяц.',
    'A yearly view helps you compare overall income trends.':
      'Годовой обзор помогает сравнивать общие тенденции дохода.',
    'A yearly view helps you compare overall income trends. Click a year to see its income.':
      'Годовой обзор помогает сравнивать общие тенденции дохода. Нажмите на год, чтобы увидеть доход за этот год.',
    'No income in this year.': 'В этом году дохода нет.',
    'No income in this month.': 'В этом месяце дохода нет.',
    Month: 'Месяц',
    'Spend by Month': 'Расходы по месяцам',
    'See how your spending changes over time across recorded purchases.':
      'Посмотрите, как меняются ваши расходы со временем на основе сохраненных покупок.',
    'See how your spending changes over time across recorded purchases. Click a day to see its purchases.':
      'Посмотрите, как меняются ваши расходы со временем. Нажмите на день, чтобы увидеть покупки за этот день.',
    'Spend by Year': 'Расходы по годам',
    'A yearly view helps you compare overall purchase trends.':
      'Годовой обзор помогает сравнивать общие тенденции покупок.',
    'A yearly view helps you compare overall purchase trends. Click a year to see its purchases.':
      'Годовой обзор помогает сравнивать общие тенденции покупок. Нажмите на год, чтобы увидеть покупки за этот год.',
    'No purchases on this day.': 'В этот день покупок нет.',
    'No purchases in this year.': 'В этом году покупок нет.',
    'Average Daily Receipt Spend': 'Средний расход по ежедневному чеку',
    Search: 'Поиск',
    Information: 'Информация',
    Wedding: 'Свадьба',
    Language: 'Язык',
    'Open menu': 'Открыть меню',
    'Close menu': 'Закрыть меню',
    'Search all items... (memories, todos, ideas, events, recipes, movies, and more)':
      'Искать по всем разделам... (воспоминания, задачи, идеи, события, рецепты, фильмы и другое)',
    'Show all results for "{{query}}"':
      'Показать все результаты для "{{query}}"',
    'No results found for "{{query}}"':
      'По запросу "{{query}}" ничего не найдено',
    'Open user menu': 'Открыть меню пользователя',
    'User menu': 'Меню пользователя',
    Logout: 'Выйти',
    'Are you sure you want to log out?': 'Вы уверены, что хотите выйти?',
    'Log Out': 'Выйти',
    'Confirmation Required': 'Требуется подтверждение',
    'Action not allowed': 'Действие не разрешено',
    Cancel: 'Отмена',
    Confirm: 'Подтвердить',
    'Clear selected value': 'Очистить выбранное значение',
    'Search...': 'Поиск...',
    'No items found': 'Ничего не найдено',
    'Select {{placeholder}}': 'Выберите {{placeholder}}',
    '{{count}} selected': 'Выбрано: {{count}}',
    'Select options': 'Выберите варианты',
    'Good morning': 'Доброе утро',
    'Good afternoon': 'Добрый день',
    'Good evening': 'Добрый вечер',
    'This Week': 'На этой неделе',
    'Active To-Dos': 'Активные задачи',
    'Active Objectives': 'Активные цели',
    'Total Tasks': 'Всего задач',
    'View All': 'Показать все',
    'Upcoming Events': 'Ближайшие события',
    'Next 30 Days': 'Следующие 30 дней',
    Today: 'Сегодня',
    Tomorrow: 'Завтра',
    '{{count}} days': '{{count}} дн.',
    'No upcoming events': 'Нет ближайших событий',
    'Your calendar is clear for now': 'Ваш календарь пока свободен',
    All: 'Все',
    Vocabulary: 'Словарь',
    'Search all items...': 'Искать по всем разделам...',
    'Found {{count}} result': 'Найден {{count}} результат',
    'Found {{count}} results': 'Найдено результатов: {{count}}',
    'in {{type}}': 'в разделе {{type}}',
    'No results found': 'Ничего не найдено',
    'Enter a search term to find memories, todos, ideas, events, and more':
      'Введите запрос, чтобы найти воспоминания, задачи, идеи, события и многое другое',
    'Untitled Memory': 'Без названия: воспоминание',
    'Untitled Todo': 'Без названия: задача',
    'Untitled Idea': 'Без названия: идея',
    'Untitled Link': 'Без названия: ссылка',
    'Untitled Event': 'Без названия: событие',
    'Untitled Board': 'Без названия: доска',
    'Untitled Word': 'Без названия: слово',
    'Untitled Recipe': 'Без названия: рецепт',
    'Untitled Movie': 'Без названия: фильм',
    'Untitled Location': 'Без названия: место',
    'Untitled Objective': 'Без названия: цель',
    'Untitled File': 'Без названия: файл',
    'Email address': 'Электронная почта',
    'Enter email': 'Введите email',
    'Wrong email or password': 'Неверный email или пароль',
    Password: 'Пароль',
    'Remember me': 'Запомнить меня',
    Login: 'Войти',
    'Close notification': 'Закрыть уведомление',
    Success: 'Успешно',
    Warning: 'Предупреждение',
    Error: 'Ошибка',
    Info: 'Информация',
    Trips: 'Поездки',
    Weddings: 'Свадьбы',
    Filters: 'Фильтры',
    Reset: 'Сбросить',
    Delete: 'Удалить',
    Name: 'Название',
    Tag: 'Тег',
    Genre: 'Жанр',
    Year: 'Год',
    Status: 'Статус',
    Rating: 'Рейтинг',
    'Rating Mode': 'Режим рейтинга',
    'Greater than': 'Больше чем',
    'Less than': 'Меньше чем',
    'Filter by name': 'Фильтр по названию',
    'Filter by tag': 'Фильтр по тегу',
    'Filter by title': 'Фильтр по заголовку',
    'Filter by year': 'Фильтр по году',
    'Filter by rating': 'Фильтр по рейтингу',
    'Capture meaningful moments, lessons, and notes you want to keep close':
      'Сохраняйте важные моменты, выводы и заметки, которые хотите держать рядом',
    'Create Memory': 'Создать воспоминание',
    '{{count}} memories': '{{count}} воспоминаний',
    'No Memories Yet': 'Воспоминаний пока нет',
    'Create your first memory to keep meaningful moments, reflections, and notes in one place.':
      'Создайте первое воспоминание, чтобы хранить важные моменты, мысли и заметки в одном месте.',
    'Add Memory': 'Добавить воспоминание',
    'Edit Memory': 'Редактировать воспоминание',
    'Memories filters': 'Фильтры воспоминаний',
    'Narrow memories by name or tag':
      'Сузьте воспоминания по названию или тегу',
    'Are you sure you want to delete this memory?':
      'Вы уверены, что хотите удалить это воспоминание?',
    'Collect sparks, concepts, and future directions worth exploring':
      'Собирайте идеи, концепции и будущие направления, которые стоит изучить',
    'Create Idea': 'Создать идею',
    '{{count}} ideas': '{{count}} идей',
    'No Ideas Yet': 'Идей пока нет',
    'Capture your first idea to start building a space for concepts, experiments, and future plans.':
      'Сохраните первую идею, чтобы начать формировать пространство для концепций, экспериментов и будущих планов.',
    'Add Idea': 'Добавить идею',
    'Edit Idea': 'Редактировать идею',
    'Ideas filters': 'Фильтры идей',
    'Refine ideas by name or tag': 'Уточните идеи по названию или тегу',
    'Are you sure you want to delete this idea?':
      'Вы уверены, что хотите удалить эту идею?',
    'Track movies, series, and animations in one place':
      'Отслеживайте фильмы, сериалы и анимацию в одном месте',
    Movie: 'Фильм',
    Series: 'Сериалы',
    Animations: 'Анимация',
    Animation: 'Анимация',
    'Create {{item}}': 'Создать {{item}}',
    '{{count}} shown in {{category}}':
      'Показано {{count}} в разделе {{category}}',
    Filtered: 'Отфильтровано',
    'No Matching {{category}}': 'Нет подходящих {{category}}',
    'No {{category}} Yet': '{{category}} пока нет',
    'Try changing or resetting the current filters to see more results.':
      'Попробуйте изменить или сбросить текущие фильтры, чтобы увидеть больше результатов.',
    'Create your first {{item}} to start building this list.':
      'Создайте первый объект типа {{item}}, чтобы начать формировать этот список.',
    'Reset Filters': 'Сбросить фильтры',
    Previous: 'Назад',
    Next: 'Вперёд',
    'Showing {{from}}-{{to}} of {{total}}':
      'Показано {{from}}-{{to}} из {{total}}',
    'Movies Filters': 'Фильтры фильмов',
    '{{item}} Details': 'Детали: {{item}}',
    'Edit {{item}}': 'Редактировать: {{item}}',
    'Add {{item}}': 'Добавить: {{item}}',
    'Are you sure you want to delete this {{item}}?':
      'Вы уверены, что хотите удалить {{item}}?',
    'Narrow movies, series, and animations by key details':
      'Сузьте фильмы, сериалы и анимацию по ключевым параметрам',
    Planned: 'Запланировано',
    Watching: 'Смотрю',
    Completed: 'Завершено',
    'Organize your tasks across paused, active, and finished states':
      'Организуйте задачи по состояниям: на паузе, активные и завершённые',
    'Create To-Do': 'Создать задачу',
    Paused: 'На паузе',
    Active: 'Активные',
    Finished: 'Завершённые',
    total: 'всего',
    'Waiting or deferred': 'Ожидает или отложено',
    'Current focus': 'Текущий фокус',
    'Completed work': 'Завершённая работа',
    'There are no active tasks yet': 'Активных задач пока нет',
    'There are no paused tasks yet': 'Отложенных задач пока нет',
    'There are no finished tasks yet': 'Завершённых задач пока нет',
    'Add To-Do': 'Добавить задачу',
    'Edit To-Do': 'Редактировать задачу',
    'Are you sure you want to delete this to-do?':
      'Вы уверены, что хотите удалить эту задачу?',
    Update: 'Обновить',
    Create: 'Создать',
    'Select genres': 'Выбрать жанры',
    '{{item}} Title': 'Название: {{item}}',
    'Image Link': 'Ссылка на изображение',
    'Watch Link': 'Ссылка для просмотра',
    Note: 'Заметка',
    'Rating must be a number between 0.0 and 10.0.':
      'Рейтинг должен быть числом от 0.0 до 10.0.',
    'Why do you want to keep this {{item}} in the list?':
      'Почему вы хотите сохранить {{item}} в этом списке?',
    Visited: 'Посещено',
    Upcoming: 'Предстоит',
    'To Visit': 'К посещению',
    State: 'Статус',
    Location: 'Место',
    List: 'Список',
    'City, place, or map link...': 'Город, место или ссылка на карту...',
    'Find on map': 'Найти на карте',
    'Searching...': 'Поиск...',
    'Map pin set': 'Метка на карте задана',
    'Use Find on map so this trip can appear as a marker in map view.':
      'Используйте «Найти на карте», чтобы поездка отображалась на карте.',
    'Enter a place name or map link first.':
      'Сначала введите название места или ссылку на карту.',
    'No places found for this location.': 'По этому месту ничего не найдено.',
    'Could not look up this location. Try again.':
      'Не удалось найти это место. Попробуйте снова.',
    'No map locations yet': 'Пока нет мест на карте',
    'Add a location to your trips so they can appear as markers on the map.':
      'Добавьте место к поездкам, чтобы они появились маркерами на карте.',
    'Loading map markers...': 'Загрузка маркеров карты...',
    'Plan and organize your travel history and future adventures.':
      'Планируйте и организуйте свои поездки, прошлые путешествия и будущие приключения.',
    'Create Trip': 'Создать поездку',
    'No Trips In {{tab}}': 'В разделе {{tab}} поездок нет',
    'No Trips Yet': 'Поездок пока нет',
    'No Trips Match Filters': 'Нет поездок по выбранным фильтрам',
    'Try adjusting your filters or reset them to see all trips.':
      'Измените фильтры или сбросьте их, чтобы увидеть все поездки.',
    '{{count}} trips': '{{count}} поездок',
    'Trips filters': 'Фильтры поездок',
    'Narrow trips by name or state':
      'Отфильтруйте поездки по названию или статусу',
    'Add a trip and it will be categorized automatically based on its visit dates.':
      'Добавьте поездку, и она будет автоматически распределена по датам посещения.',
    'Add Trip': 'Добавить поездку',
    'Edit Trip': 'Редактировать поездку',
    'Are you sure you want to delete trip "{{name}}"?':
      'Вы уверены, что хотите удалить поездку "{{name}}"?',
    'Map link': 'Ссылка на карту',
    Description: 'Описание',
    'Visit dates': 'Даты посещения',
    'No dates selected. This trip will appear in "{{tab}}".':
      'Даты не выбраны. Эта поездка появится в разделе "{{tab}}".',
    'Image links (one URL per line)':
      'Ссылки на изображения (по одному URL в строке)',
    'Trip name...': 'Название поездки...',
    'What is this trip about?': 'О чем эта поездка?',
    'Add your travel notes...': 'Добавьте заметки о поездке...',
    'Open map': 'Открыть карту',
    'View details': 'Смотреть детали',
    'No notes yet': 'Заметок пока нет',
    'Or paste an image link': 'Или вставьте ссылку на изображение',
    'Use link': 'Использовать ссылку',
    'Additional images (one URL per line)':
      'Дополнительные изображения (по одному URL в строке)',
    'Upload PDFs, videos, images, and other trip files.':
      'Загружайте PDF, видео, изображения и другие файлы поездки.',
    'No files yet': 'Файлов пока нет',
    'Failed to upload file.': 'Не удалось загрузить файл.',
    'Select a file first.': 'Сначала выберите файл.',
    'Add file': 'Добавить файл',
    '{{count}} images': '{{count}} изображений',
    '{{count}} notes': '{{count}} заметок',
    '{{count}} dates': '{{count}} дат',
    'Organize and manage your important links and resources':
      'Организуйте и управляйте важными ссылками и ресурсами',
    Link: 'Ссылка',
    Folder: 'Папка',
    Board: 'Доска',
    'Add Link': 'Добавить ссылку',
    'Add Folder': 'Добавить папку',
    'Go back': 'Назад',
    Back: 'Назад',
    '{{count}} items': '{{count}} элементов',
    'No Links Yet': 'Ссылок пока нет',
    'Add your first link or folder to start organizing useful resources in one place.':
      'Добавьте первую ссылку или папку, чтобы собирать полезные ресурсы в одном месте.',
    URL: 'URL',
    'link name...': 'название ссылки...',
    'folder name...': 'название папки...',
    'link url...': 'url ссылки...',
    'Save places, routes, and addresses you want to revisit':
      'Сохраняйте места, маршруты и адреса, к которым хотите вернуться',
    'Create Location': 'Создать место',
    'No Locations Yet': 'Мест пока нет',
    'Create your first location to keep addresses, destinations, and useful places easy to find.':
      'Создайте первое место, чтобы адреса, направления и полезные точки всегда было легко найти.',
    'Add Location': 'Добавить место',
    'Edit Location': 'Редактировать место',
    'Are you really want to delete this Location?':
      'Вы действительно хотите удалить это место?',
    Address: 'Адрес',
    Map: 'Карта',
    City: 'Город',
    Type: 'Тип',
    'Location name...': 'Название места...',
    'Location address...': 'Адрес места...',
    'Location map...': 'Карта места...',
    'Add an address or map link to show this place on the map.':
      'Добавьте адрес или ссылку на карту, чтобы показать это место.',
    'Location city...': 'Город места...',
    'Location description...': 'Описание места...',
    'Home-Address': 'Домашний адрес',
    'Work-Address': 'Рабочий адрес',
    Hotel: 'Отель',
    Restaurant: 'Ресторан',
    Pizzeria: 'Пиццерия',
    'Fast-Food': 'Фастфуд',
    Bar: 'Бар',
    Pub: 'Паб',
    Cafe: 'Кафе',
    Opera: 'Опера',
    Theater: 'Театр',
    Cinema: 'Кинотеатр',
    Club: 'Клуб',
    Disco: 'Диско',
    'Tourist-Attraction': 'Достопримечательность',
    Beach: 'Пляж',
    Other: 'Другое',
    'Start at': 'Начать',
    'End at': 'Завершить',
    Priority: 'Приоритет',
    High: 'Высокий',
    Medium: 'Средний',
    Low: 'Низкий',
    'To-Do name...': 'Название задачи...',
    'To-Do description...': 'Описание задачи...',
    'Due today': 'Срок сегодня',
    Overdue: 'Просрочено',
    'No deadline': 'Без срока',
    'No active ToDos': 'Нет активных задач',
    'Create a new to-do to get started': 'Создайте новую задачу, чтобы начать',
    'Create and manage your whiteboards':
      'Создавайте и управляйте своими досками',
    'Create New Folder': 'Создать новую папку',
    'New Folder': 'Новая папка',
    'Create New Whiteboard': 'Создать новую доску',
    'New Board': 'Новая доска',
    'Filter By': 'Фильтровать по',
    'Sort By': 'Сортировать по',
    Date: 'Дата',
    Order: 'Порядок',
    Show: 'Показать',
    'Folders only': 'Только папки',
    'Boards only': 'Только доски',
    'A - Z': 'A - Z',
    'Z - A': 'Z - A',
    'Oldest - Newest': 'Старые - новые',
    'Newest - Oldest': 'Новые - старые',
    Root: 'Корень',
    'Delete folder': 'Удалить папку',
    'Folder must be empty to delete': 'Папка должна быть пустой для удаления',
    'No Whiteboards Yet': 'Досок пока нет',
    'Create your first whiteboard to get started':
      'Создайте первую доску, чтобы начать',
    'Add Whiteboard': 'Добавить доску',
    'Edit Whiteboard': 'Редактировать доску',
    'Folder name is required': 'Название папки обязательно',
    'Could not create folder. Please try again.':
      'Не удалось создать папку. Попробуйте еще раз.',
    'Could not update folder. Please try again.':
      'Не удалось обновить папку. Попробуйте еще раз.',
    'Create Folder': 'Создать папку',
    'Folder name...': 'Название папки...',
    'Edit Folder': 'Редактировать папку',
    'Move to folder': 'Переместить в папку',
    Save: 'Сохранить',
    'Are you really want to delete this Whiteboard?':
      'Вы действительно хотите удалить эту доску?',
    'Delete folder "{{name}}"?': 'Удалить папку "{{name}}"?',
    'Whiteboard name...': 'Название доски...',
    'Manage and organize your uploaded files':
      'Управляйте и организуйте загруженные файлы',
    'Upload File': 'Загрузить файл',
    'Available Space': 'Доступное место',
    Size: 'Размер',
    'Smallest - Largest': 'От меньшего к большему',
    'Largest - Smallest': 'От большего к меньшему',
    Visibility: 'Видимость',
    Public: 'Публичный',
    Private: 'Приватный',
    'This folder is empty': 'Эта папка пуста',
    'Create a folder or upload a file here':
      'Создайте папку или загрузите сюда файл',
    'Are you sure you want to delete this file?':
      'Вы уверены, что хотите удалить этот файл?',
    'Create folder in {{name}}': 'Создать папку в {{name}}',
    'Create folder in Root': 'Создать папку в корне',
    'Folder name': 'Название папки',
    'Type folder name...': 'Введите название папки...',
    'Creating...': 'Создание...',
    'Upload file to {{name}}': 'Загрузить файл в {{name}}',
    'Upload file to Root': 'Загрузить файл в корень',
    'Edit file: {{name}}': 'Редактировать файл: {{name}}',
    'Edit file': 'Редактировать файл',
    'File name is required': 'Название файла обязательно',
    'Could not update file. Please try again.':
      'Не удалось обновить файл. Попробуйте еще раз.',
    'Type file name...': 'Введите название файла...',
    'file description...': 'описание файла...',
    'Public file (accessible via direct link)':
      'Публичный файл (доступен по прямой ссылке)',
    'When disabled, file is only accessible through the app preview.':
      'Когда отключено, файл доступен только через предпросмотр в приложении.',
    'Saving...': 'Сохранение...',
    'Text File Preview': 'Предпросмотр текстового файла',
    'Audio Preview': 'Предпросмотр аудиофайла',
    'Loading audio...': 'Загрузка аудио...',
    'Failed to load audio': 'Не удалось загрузить аудио',
    'Office Preview': 'Предпросмотр Office',
    'Archive Preview': 'Предпросмотр архива',
    'Loading document...': 'Загрузка документа...',
    'Failed to load document': 'Не удалось загрузить документ',
    'This file type cannot be previewed': 'Этот тип файла нельзя просмотреть',
    'Empty archive': 'Архив пуст',
    'Failed to open archive': 'Не удалось открыть архив',
    Extract: 'Извлечь',
    'This archive is password-protected': 'Этот архив защищён паролем',
    'Open archive': 'Открыть архив',
    'Edit folder: {{name}}': 'Редактировать папку: {{name}}',
    'Edit folder': 'Редактировать папку',
    'Name is required': 'Название обязательно',
    Title: 'Название',
    'Select File': 'Выбрать файл',
    Upload: 'Загрузить',
    'File is required': 'Файл обязателен',
    'file name...': 'название файла...',
    Download: 'Скачать',
    'Copied!': 'Скопировано!',
    'Copy link': 'Копировать ссылку',
    'Public file': 'Публичный файл',
    'Private file': 'Приватный файл',
    Edit: 'Редактировать',
    'Configure workspace-level sections from one place.':
      'Настраивайте разделы рабочего пространства из одного места.',
    'Settings under development': 'Настройки находятся в разработке',
    Sections: 'Разделы',
    'Workspace Settings': 'Настройки рабочего пространства',
    Users: 'Пользователи',
    'Manage members, roles, and access for this workspace.':
      'Управляйте участниками, ролями и доступом в этом рабочем пространстве.',
    'Failed to load users.': 'Не удалось загрузить пользователей.',
    'User created successfully.': 'Пользователь успешно создан.',
    'Failed to update user.': 'Не удалось обновить пользователя.',
    'Failed to create user.': 'Не удалось создать пользователя.',
    'You cannot delete your own user.':
      'Нельзя удалить собственную учетную запись.',
    'Failed to delete user.': 'Не удалось удалить пользователя.',
    'Create User': 'Создать пользователя',
    'Workspace members': 'Участники рабочего пространства',
    'Users directory': 'Список пользователей',
    'Review all users, update access, and create new accounts from this section.':
      'Просматривайте всех пользователей, обновляйте доступ и создавайте новые учетные записи в этом разделе.',
    'Loading users...': 'Загрузка пользователей...',
    'No users yet': 'Пользователей пока нет',
    'Create the first user for this workspace.':
      'Создайте первого пользователя для этого рабочего пространства.',
    Blocked: 'Заблокирован',
    Created: 'Создан',
    'Edit user': 'Редактировать пользователя',
    'Delete user': 'Удалить пользователя',
    User: 'Пользователь',
    Admin: 'Администратор',
    Hide: 'Скрыть',
    View: 'Просмотр',
    'Edit User': 'Редактировать пользователя',
    'First name, last name, and email are required.':
      'Имя, фамилия и email обязательны.',
    'Enter a valid email address.': 'Введите корректный email.',
    'Password is required when creating a user.':
      'При создании пользователя пароль обязателен.',
    'Repeat password is required when creating a user.':
      'При создании пользователя нужно повторить пароль.',
    'Password and repeat password are required.':
      'Пароль и повтор пароля обязательны.',
    'Password and repeat password must match.':
      'Пароль и повтор пароля должны совпадать.',
    'First Name': 'Имя',
    'Last Name': 'Фамилия',
    'First name': 'Имя',
    'Last name': 'Фамилия',
    Email: 'Email',
    Role: 'Роль',
    Modules: 'Модули',
    Code: 'Код',
    Permission: 'Разрешение',
    'Set all': 'Для всех',
    'Enable private mode for this user':
      'Включить приватный режим для этого пользователя',
    'When private mode is on, only public modules are visible to this user.':
      'Когда приватный режим включён, этому пользователю видны только публичные модули.',
    'You cannot change your own role or blocked status.':
      'Нельзя изменить свою собственную роль или статус блокировки.',
    'New Password': 'Новый пароль',
    'Leave blank to keep current password':
      'Оставьте пустым, чтобы сохранить текущий пароль',
    'Repeat New Password': 'Повторите новый пароль',
    'Repeat the new password': 'Повторите новый пароль',
    'Repeat Password': 'Повторите пароль',
    'Set each module to hide, view, or edit for this user.':
      'Укажите для каждого модуля скрытие, просмотр или редактирование для этого пользователя.',
    'Admin users keep full access across every module.':
      'Пользователи-администраторы имеют полный доступ ко всем модулям.',
    'Update User': 'Обновить пользователя',
    Add: 'Добавить',
    Word: 'Слово',
    Done: 'Готово',
    DeadLine: 'Срок',
    'Select date': 'Выберите дату',
    'Choose date': 'Выберите дату',
    'dd/mm/yyyy': 'дд/мм/гггг',
    'Invalid date': 'Неверная дата',
    Periodicity: 'Периодичность',
    Notification: 'Уведомление',
    'Enable notification': 'Включить уведомление',
    'Plan and manage your events': 'Планируйте и управляйте своими событиями',
    'Add Event': 'Добавить событие',
    'Update Event': 'Обновить событие',
    'No Events Yet': 'Событий пока нет',
    'Create Event': 'Создать событие',
    'Create your first event to start planning your schedule.':
      'Создайте первое событие, чтобы начать планировать свое расписание.',
    'event name...': 'название события...',
    'event description...': 'описание события...',
    'event note...': 'заметка о событии...',
    Once: 'Один раз',
    'Every Year': 'Каждый год',
    'By Dates': 'По датам',
    Period: 'Период',
    'To Do': 'Сделать',
    Birthday: 'День рождения',
    Death: 'Смерть',
    Vacation: 'Отпуск',
    From: 'С',
    to: 'по',
    'Are you really want to delete this event?':
      'Вы действительно хотите удалить это событие?',
    'Learn and manage your vocabulary': 'Изучайте и управляйте своим словарем',
    'New Vocabulary': 'Новый словарь',
    'Select Vocabulary': 'Выбрать словарь',
    'Choose a vocabulary...': 'Выберите словарь...',
    'Add word': 'Добавить слово',
    'Add Word': 'Добавить слово',
    'Delete vocabulary': 'Удалить словарь',
    'Show translations': 'Показать переводы',
    'Show words': 'Показать слова',
    Words: 'Слова',
    '{{count}} words': '{{count}} слов',
    'No words yet': 'Слов пока нет',
    'Start building your vocabulary by adding your first word':
      'Начните пополнять словарь, добавив первое слово',
    'Add First Word': 'Добавить первое слово',
    'Select a vocabulary to start': 'Выберите словарь, чтобы начать',
    'Choose a vocabulary from the dropdown above or create a new one':
      'Выберите словарь из списка выше или создайте новый',
    'Add New Vocabulary': 'Добавить новый словарь',
    'Add new vocabulary': 'Добавить новый словарь',
    'Edit Vocabulary': 'Редактировать словарь',
    'Update vocabulary': 'Обновить словарь',
    'Add New Word': 'Добавить новое слово',
    'Add word to {{name}}': 'Добавить слово в {{name}}',
    'Edit Word': 'Редактировать слово',
    'Update word in {{name}}': 'Обновить слово в {{name}}',
    'Are you sure you want to delete this vocabulary? All words will be lost.':
      'Вы уверены, что хотите удалить этот словарь? Все слова будут потеряны.',
    'Are you sure you want to delete this word?':
      'Вы уверены, что хотите удалить это слово?',
    'Vocabulary Name': 'Название словаря',
    'Enter vocabulary name...': 'Введите название словаря...',
    'Words Voice Language': 'Язык озвучки слов',
    'Select language for words': 'Выберите язык для слов',
    'Translations Voice Language': 'Язык озвучки переводов',
    'Select language for translations': 'Выберите язык для переводов',
    'Create Vocabulary': 'Создать словарь',
    'Update Vocabulary': 'Обновить словарь',
    'Enter word...': 'Введите слово...',
    'Enter translation...': 'Введите перевод...',
    'Dictionary (Optional)': 'Словарь (необязательно)',
    'Add definition or notes...': 'Добавьте определение или заметки...',
    'Update Word': 'Обновить слово',
    'This word already exists in this vocabulary.':
      'Это слово уже есть в этом словаре.',
    'Listen to word': 'Прослушать слово',
    'Listen to translation': 'Прослушать перевод',
    'Enable word pronunciation': 'Включить произношение слов',
    'Disable word pronunciation': 'Выключить произношение слов',
    'Copy to clipboard': 'Скопировать в буфер обмена',
    'More options': 'Другие действия',
    Move: 'Переместить',
    'Move to vocabulary': 'Переместить в словарь',
    'No other vocabularies': 'Других словарей нет',
    'Track and manage your goals': 'Отслеживайте и управляйте своими целями',
    'Create New Objective': 'Создать новую цель',
    'New Objective': 'Новая цель',
    'No active objectives yet': 'Активных целей пока нет',
    'Create your first objective': 'Создайте свою первую цель',
    'No paused objectives': 'Целей на паузе нет',
    'No completed objectives yet': 'Завершенных целей пока нет',
    'Add Objective': 'Добавить цель',
    'Edit Objective': 'Редактировать цель',
    'Are you really want to delete this Objective?':
      'Вы действительно хотите удалить эту цель?',
    'Are you really want to delete this objective?':
      'Вы действительно хотите удалить эту цель?',
    'Are you really want to delete this ToDo?':
      'Вы действительно хотите удалить эту задачу?',
    'Objective name...': 'Название цели...',
    'Parent objective': 'Родительская цель',
    'Child objectives': 'Дочерние цели',
    'parent objective': 'родительскую цель',
    'No parent': 'Без родителя',
    'Select Priority': 'Выберите приоритет',
    'Select Status': 'Выберите статус',
    'Objective description...': 'Описание цели...',
    Passive: 'На паузе',
    'Created at:': 'Создано:',
    'Finished at:': 'Завершено:',
    'Not finished yet': 'Еще не завершено',
    'Leave a comment here': 'Оставьте комментарий здесь',
    Steps: 'Шаги',
    Step: 'Шаг',
    'Start Date': 'Дата начала',
    'End Date': 'Дата окончания',
    'Add Step': 'Добавить шаг',
    Progress: 'Прогресс',
    'No steps yet': 'Пока нет шагов',
    'Add a step...': 'Добавить шаг...',
    'Search objectives...': 'Поиск целей...',
    'All priorities': 'Все приоритеты',
    'Deadline (newest)': 'Срок (новые)',
    'Deadline (oldest)': 'Срок (старые)',
    Activate: 'Активировать',
    Pause: 'Пауза',
    Play: 'Воспроизвести',
    Complete: 'Завершить',
    'Toggle step': 'Переключить шаг',
    'Toggle to-do': 'Переключить задачу',
    'Update Step': 'Обновить шаг',
    'Are you really want to delete this step?':
      'Вы действительно хотите удалить этот шаг?',
    'This step cannot be deleted because it still has to-dos. Remove or complete all to-dos in this step first.':
      'Этот шаг нельзя удалить, так как в нём ещё есть задачи. Сначала удалите или завершите все задачи в этом шаге.',
    'Objective name is required': 'Название цели обязательно',
    "Objective deadLine can't be earlier then current day":
      'Срок цели не может быть раньше текущего дня',
    'Priority is required': 'Приоритет обязателен',
    'No whiteboards yet': 'Досок пока нет',
    'No to-dos yet': 'Задач пока нет',
    'Browse all scenarios and open details in the drawer.':
      'Просматривайте все сценарии и открывайте детали в боковой панели.',
    'Create Scenario': 'Создать сценарий',
    'Loading scenarios...': 'Загрузка сценариев...',
    'No Scenarios Yet': 'Сценариев пока нет',
    'Create your first scenario to start organizing story flows and reusable scripts.':
      'Создайте свой первый сценарий, чтобы начать организовывать сюжетные линии и повторно используемые скрипты.',
    'No image': 'Нет изображения',
    'Failed to load scenarios.': 'Не удалось загрузить сценарии.',
    'Failed to load scenario details.': 'Не удалось загрузить детали сценария.',
    'Failed to delete scenario.': 'Не удалось удалить сценарий.',
    'Are you sure you want to delete this scenario?':
      'Вы уверены, что хотите удалить этот сценарий?',
    'Failed to load scenario.': 'Не удалось загрузить сценарий.',
    'Name is required.': 'Имя обязательно.',
    'Failed to save scenario.': 'Не удалось сохранить сценарий.',
    'Loading scenario...': 'Загрузка сценария...',
    'Edit Scenario': 'Редактировать сценарий',
    'Manage scenario base fields and nested objects.':
      'Управляйте основными полями сценария и вложенными объектами.',
    'Scenario name': 'Название сценария',
    Text: 'Текст',
    'Scenario text': 'Текст сценария',
    'Scenario description': 'Описание сценария',
    'Main image': 'Главное изображение',
    Gallery: 'Галерея',
    'Add gallery image': 'Добавить изображение в галерею',
    Places: 'Места',
    'No places added yet.': 'Места еще не добавлены.',
    Characters: 'Персонажи',
    'No characters added yet.': 'Персонажи еще не добавлены.',
    'Scenario Events': 'События сценария',
    'No events added yet.': 'События еще не добавлены.',
    Artifacts: 'Артефакты',
    Expand: 'Развернуть',
    Collapse: 'Свернуть',
    'No artifacts added yet.': 'Артефакты еще не добавлены.',
    'No whiteboards added yet.': 'Доски еще не добавлены.',
    'World Maps': 'Карты мира',
    'No maps added yet.': 'Карты еще не добавлены.',
    'Untitled map': 'Карта без названия',
    'Map name': 'Название карты',
    Land: 'Суша',
    'Paint land': 'Закрасить сушу',
    'Outline part of the land to recolor, then press Enter or Finish shape.':
      'Обведите часть суши, чтобы перекрасить её, затем Enter или «Завершить фигуру».',
    Lake: 'Озеро',
    River: 'Река',
    Road: 'Дорога',
    Mountain: 'Гора',
    Forest: 'Лес',
    Castle: 'Замок',
    Village: 'Деревня',
    'Select tool': 'Выбор',
    'Pan tool': 'Перемещение',
    'Finish shape': 'Завершить фигуру',
    'Delete selected': 'Удалить выбранное',
    'Failed to save map.': 'Не удалось сохранить карту.',
    'Loading map...': 'Загрузка карты...',
    'Map not found.': 'Карта не найдена.',
    'Delete this map?': 'Удалить эту карту?',
    'Delete this whiteboard?': 'Удалить эту доску?',
    'Click to add points, then press Enter or Finish shape.':
      'Кликайте, чтобы добавить точки, затем Enter или «Завершить фигуру».',
    'Click the map to place this object.':
      'Кликните по карте, чтобы поставить объект.',
    'Stamp several times and overlap them, working from back to front.':
      'Ставьте несколько раз внахлест, двигаясь с дальнего плана вперед.',
    'Each click uses a different shape. Overlap them from back to front.':
      'Каждый клик ставит другую форму. Накладывайте с дальнего плана вперёд.',
    'Drag objects to move them. Scroll to zoom.':
      'Перетаскивайте объекты. Колесо мыши — масштаб.',
    'Click to outline an area, then press Enter or Finish shape.':
      'Кликайте, чтобы обвести область, затем Enter или «Завершить фигуру».',
    'Outline the forest, then press Enter or Finish shape. Trees will mix automatically.':
      'Обведите лес, затем Enter или «Завершить фигуру». Деревья смешаются сами.',
    'Outline the mountains, then press Enter or Finish shape.':
      'Обведите горы, затем Enter или «Завершить фигуру».',
    'Choose a mountain type, then outline the range and press Enter or Finish shape.':
      'Выберите тип гор, обведите хребет, затем Enter или «Завершить фигуру».',
    'Click an object to erase it.': 'Кликните по объекту, чтобы стереть его.',
    Eraser: 'Ластик',
    'Forest fill': 'Заполнить лесом',
    'Mountain fill': 'Заполнить горами',
    'Snow peaks': 'Снежные вершины',
    'Grey peaks': 'Серые вершины',
    'Rocky peaks': 'Скальные вершины',
    Sparse: 'Редко',
    Dense: 'Густо',
    Normal: 'Обычно',
    Hills: 'Холмы',
    Desert: 'Пустыня',
    Swamp: 'Болото',
    Snow: 'Снег',
    Woods: 'Чаща',
    Volcano: 'Вулкан',
    Hill: 'Холм',
    Sea: 'Море',
    Trail: 'Тропа',
    Border: 'Граница',
    Wall: 'Стена',
    Bridge: 'Мост',
    Town: 'Городок',
    Port: 'Порт',
    Fortress: 'Крепость',
    Tower: 'Башня',
    Monastery: 'Монастырь',
    Ruins: 'Руины',
    Farm: 'Ферма',
    Mine: 'Шахта',
    Camp: 'Лагерь',
    Lighthouse: 'Маяк',
    Label: 'Подпись',
    Navigate: 'Навигация',
    Terrain: 'Рельеф',
    Routes: 'Пути',
    Settlements: 'Поселения',
    Marks: 'Метки',
    Grid: 'Сетка',
    'Fit map': 'Вписать карту',
    'Map size': 'Размер карты',
    Small: 'Маленькая',
    Large: 'Большая',
    Huge: 'Огромная',
    Width: 'Ширина',
    Height: 'Высота',
    Pines: 'Хвои',
    Tree: 'Дерево',
    Pine: 'Сосна',
    Compass: 'Компас',
    'Scale bar': 'Масштабная линейка',
    Ship: 'Корабль',
    Waves: 'Волны',
    Cave: 'Пещера',
    Waterfall: 'Водопад',
    Oasis: 'Оазис',
    'Standing stones': 'Мегалиты',
    Windmill: 'Мельница',
    Dunes: 'Дюны',
    'Draw a continent or island coast, then press Enter or Finish shape.':
      'Обведите берег материка или острова, затем Enter или «Завершить фигуру».',
    'Mark a rough land border, then Shape coast or Finish shape.':
      'Наметьте грубый контур суши, затем «Форма берега» или «Завершить фигуру».',
    'Mark a rough sea border, then Shape coast or Finish shape.':
      'Наметьте грубый контур моря, затем «Форма берега» или «Завершить фигуру».',
    'Mark a rough lake border, then Shape coast or Finish shape.':
      'Наметьте грубый контур озера, затем «Форма берега» или «Завершить фигуру».',
    'Mark a rough forest border, then Shape edge or Finish shape.':
      'Наметьте грубый контур леса, затем «Форма края» или «Завершить фигуру».',
    'Choose a mountain type, then mark a rough range and Shape edge or Finish shape.':
      'Выберите тип гор, наметьте грубый хребет, затем «Форма края» или «Завершить фигуру».',
    'Shape coast': 'Форма берега',
    'Shaping coast...': 'Формируется берег...',
    'Shape edge': 'Форма края',
    'Shaping edge...': 'Формируется край...',
    'From image': 'С картинки',
    'Reading map...': 'Читаем карту...',
    'Failed to read map image.': 'Не удалось разобрать изображение карты.',
    'Map rebuilt from the image. You can undo.':
      'Карта собрана по изображению. Можно отменить.',
    Duplicate: 'Дублировать',
    Properties: 'Свойства',
    'Fill color': 'Цвет заливки',
    'Custom color': 'Свой цвет',
    'Default colors': 'Цвета по умолчанию',
    Color: 'Цвет',
    Scale: 'Масштаб',
    Rotation: 'Поворот',
    'Linked place': 'Связанное место',
    Rotate: 'Повернуть',
    Symbol: 'Знак',
    'Next symbol': 'Следующий знак',
    'Choose a symbol': 'Выберите знак',
    'Choose a symbol, then click the map to place it.':
      'Выберите знак, затем кликните по карте, чтобы поставить его.',
    'Bring forward': 'На передний план',
    'Send back': 'На задний план',
    'Select an object to edit its name, size and rotation.':
      'Выберите объект, чтобы изменить имя, размер и поворот.',
    'Update Scenario': 'Обновить сценарий',
    'Delete this item?': 'Удалить этот элемент?',
    'Scenario details': 'Детали сценария',
    'Select a scenario.': 'Выберите сценарий.',
    'Created:': 'Создано:',
    'No text.': 'Текста нет.',
    'No description.': 'Описания нет.',
    'No description': 'Нет описания',
    Place: 'Место',
    Event: 'Событие',
    Character: 'Персонаж',
    Artifact: 'Артефакт',
    Image: 'Изображение',
    'No place': 'Без места',
    'Choose image file': 'Выберите файл изображения',
    'Failed to upload image.': 'Не удалось загрузить изображение.',
    'Uploading...': 'Загрузка...',
    Remove: 'Удалить',
    'No gallery images.': 'В галерее нет изображений.',
    'Create New Purchase list': 'Создать новый список покупок',
    'No Purchase Lists Yet': 'Списков покупок пока нет',
    'Your purchase lists will appear here once this section starts being used.':
      'Ваши списки покупок появятся здесь, как только этот раздел начнет использоваться.',
    Participants: 'Участники',
    'Loading participants...': 'Загрузка участников...',
    'This event is currently blocked.': 'Это событие сейчас заблокировано.',
    'Event not found. Please check the key.':
      'Событие не найдено. Проверьте ключ.',
    'Please enter an event key.': 'Пожалуйста, введите ключ события.',
    'Private List': 'Закрытый список',
    'Please enter the event key to view the participant list.':
      'Введите ключ события, чтобы посмотреть список участников.',
    'Enter key': 'Введите ключ',
    'Checking...': 'Проверка...',
    'Access List': 'Открыть список',
    Invited: 'Приглашены',
    Attending: 'Придут',
    'Not attending': 'Не придут',
    Gifts: 'Подарки',
    'Husband Side': 'Сторона жениха',
    'Wife Side': 'Сторона невесты',
    'No participants on this side.': 'На этой стороне участников нет.',
    Gift: 'Подарок',
    Yes: 'Да',
    No: 'Нет',
    Tags: 'Теги',
    Ingredients: 'Ингредиенты',
    'No ingredients': 'Нет ингредиентов',
    '{{count}} ingredients': '{{count}} ингредиентов',
    'type ingredient': 'введите ингредиент',
    'Keep your favorite meals, ingredients, and cooking notes in one place':
      'Храните любимые блюда, ингредиенты и заметки по готовке в одном месте',
    'Create Recipe': 'Создать рецепт',
    'Create Menu': 'Создать меню',
    'Add Menu': 'Добавить меню',
    'Edit Menu': 'Редактировать меню',
    'Menu name...': 'Название меню...',
    'Select recipes': 'Выберите рецепты',
    '{{count}} recipes': '{{count}} рецептов',
    'Are you really want to delete this Menu?':
      'Вы действительно хотите удалить это меню?',
    'Group recipes into shared meals.':
      'Группируйте рецепты в общие приёмы пищи.',
    'No Recipes Yet': 'Рецептов пока нет',
    'No Menus Yet': 'Меню пока нет',
    'Create a menu and pick recipes to group meals together.':
      'Создайте меню и выберите рецепты, чтобы сгруппировать блюда.',
    'Create your first recipe to keep ingredients, cooking steps, and meal ideas together.':
      'Создайте первый рецепт, чтобы хранить ингредиенты, шаги приготовления и идеи для блюд вместе.',
    'Add Recipe': 'Добавить рецепт',
    'Edit Recipe': 'Редактировать рецепт',
    'Are you really want to delete this Recipe?':
      'Вы действительно хотите удалить этот рецепт?',
    'Recipe name...': 'Название рецепта...',
    Total: 'Всего',
    Participating: 'Участвуют',
    'Not participating': 'Не участвуют',
    'Manage confirmations and guest list':
      'Управляйте подтверждениями и списком гостей',
    Rest: 'Остальные',
    'Husband side participants': 'Участники со стороны жениха',
    'No Husband Side Participants': 'Нет участников со стороны жениха',
    'Participants for the husband side will appear here once people respond or are added.':
      'Участники со стороны жениха появятся здесь после ответа или добавления.',
    'Husband side': 'Сторона жениха',
    'Wife side participants': 'Участники со стороны невесты',
    'No Wife Side Participants': 'Нет участников со стороны невесты',
    'Participants for the wife side will appear here once people respond or are added.':
      'Участники со стороны невесты появятся здесь после ответа или добавления.',
    'Wife side': 'Сторона невесты',
    'Are you really want to delete this Participant?':
      'Вы действительно хотите удалить этого участника?',
    daysShort: 'дн',
    hoursShort: 'ч',
    minutesShort: 'мин',
    secondsShort: 'сек',
    'Loading invitation...': 'Загрузка приглашения...',
    'Wedding event not found': 'Свадебное событие не найдено',
    'We warmly invite you': 'С радостью приглашаем вас',
    'to our wedding ceremony': 'на нашу свадебную церемонию',
    'Respectfully,': 'С уважением,',
    'Time left': 'Осталось',
    Schedule: 'Программа дня',
    'How to get there': 'Как добраться',
    'Please confirm your attendance':
      'Пожалуйста, подтвердите ваше присутствие',
    'Thank you': 'Спасибо',
    'Please fill in the field': 'Пожалуйста, заполните поле',
    'We will attend': 'Мы придем',
    'We cannot attend': 'Мы не сможем прийти',
    'Guest count': 'Количество гостей',
    Send: 'Отправить',
    'Contact us': 'Свяжитесь с нами',
    'Wedding Events': 'Свадебные события',
    'Manage invitations, schedules, and event access from one place.':
      'Управляйте приглашениями, расписанием и доступом к событиям из одного места.',
    'Create New Event': 'Создать новое событие',
    'Total events': 'Всего событий',
    'Gift info enabled': 'Информация о подарках включена',
    'Edit Wedding Event': 'Редактировать свадебное событие',
    'Create New Wedding Event': 'Создать новое свадебное событие',
    'Event Slug (URL-friendly)': 'Слаг события (для URL)',
    'e.g., arman-armine-2025': 'например, arman-armine-2025',
    'This will be used in the URL: /wedding/[slug]/invitation':
      'Это будет использоваться в URL: /wedding/[slug]/invitation',
    'Event Title': 'Название события',
    'e.g., Arman & Armine Wedding': 'например, Arman & Armine Wedding',
    'Husband Name': 'Имя жениха',
    'e.g., Arman': 'например, Arman',
    'Wife Name': 'Имя невесты',
    'e.g., Armine': 'например, Armine',
    'Wedding Date': 'Дата свадьбы',
    'Hero Image URL (Optional)': 'URL главного изображения (необязательно)',
    'If provided, this image will replace the default wedding hero image on the invitation page':
      'Если указать, это изображение заменит стандартное главное изображение на странице приглашения.',
    Configuration: 'Настройки',
    'Block Public Access': 'Закрыть публичный доступ',
    'When enabled, public access to this wedding will be blocked, but it will still be visible in the main list.':
      'Если включено, публичный доступ к этой свадьбе будет закрыт, но она останется видимой в основном списке.',
    'Show Gift Information': 'Показывать информацию о подарках',
    'When enabled, users will be able to see gift information on public pages.':
      'Если включено, пользователи смогут видеть информацию о подарках на публичных страницах.',
    'Location {{index}}': 'Локация {{index}}',
    'Venue Name': 'Название места',
    'e.g., Ceremony': 'например, Церемония',
    'Place Name': 'Название площадки',
    'e.g., Saint Cross Church': 'например, церковь Святого Креста',
    'Icon Type': 'Тип иконки',
    'Select icon type...': 'Выберите тип иконки...',
    'Map Link': 'Ссылка на карту',
    'Image URL (Optional)': 'URL изображения (необязательно)',
    'No Wedding Events Yet': 'Свадебных событий пока нет',
    'Create your first wedding event to manage invitations, schedules, and participants from one place.':
      'Создайте первое свадебное событие, чтобы управлять приглашениями, расписанием и участниками из одного места.',
    'Public blocked': 'Публичный доступ закрыт',
    'Public active': 'Публичный доступ открыт',
    'Gift info on': 'Подарки включены',
    'Gift info off': 'Подарки отключены',
    '{{count}} locations': '{{count}} локаций',
    'Copy URL': 'Копировать URL',
    'View Invitation': 'Открыть приглашение',
    'Copy Participants URL': 'Копировать URL участников',
    Slug: 'Слаг',
    'Invitation URL': 'URL приглашения',
    at: 'в',
    'Are you sure you want to delete "{{title}}"? This action cannot be undone.':
      'Вы уверены, что хотите удалить "{{title}}"? Это действие нельзя отменить.',
  },
  es: {
    appName: 'IVA',
    Home: 'Inicio',
    Notes: 'Notas',
    Memories: 'Recuerdos',
    'To-Dos': 'Tareas',
    Ideas: 'Ideas',
    Links: 'Enlaces',
    Recipes: 'Recetas',
    Recipe: 'Receta',
    Food: 'Comida',
    Menus: 'Menús',
    Menu: 'Menú',
    Movies: 'Películas',
    Locations: 'Lugares',
    Objectives: 'Objetivos',
    Objective: 'Objetivo',
    Events: 'Eventos',
    Translation: 'Traducción',
    Whiteboards: 'Pizarras',
    Files: 'Archivos',
    Lunar: 'Lunar',
    Recordings: 'Grabaciones',
    'Microphone recordings from Lunar, grouped by user.':
      'Grabaciones de micrófono de Lunar, agrupadas por usuario.',
    '{{count}} recordings': '{{count}} grabaciones',
    'No recordings yet': 'Todavía no hay grabaciones',
    'This user has no recordings yet.': 'Este usuario todavía no tiene grabaciones.',
    'Could not load recordings.': 'No se pudieron cargar las grabaciones.',
    'Loading recordings...': 'Cargando grabaciones...',
    'Unknown user': 'Usuario desconocido',
    'Are you sure you want to delete this recording?':
      '¿Seguro que quieres eliminar esta grabación?',
    'Could not delete the recording.': 'No se pudo eliminar la grabación.',
    Scenarios: 'Escenarios',
    Settings: 'Configuración',
    Purchases: 'Compras',
    Income: 'Ingresos',
    Regular: 'Regular',
    Expected: 'Previsto',
    Received: 'Recibido',
    'Income Date': 'Fecha de ingreso',
    Source: 'Fuente',
    'Create Income': 'Crear ingreso',
    'Add Income': 'Añadir ingreso',
    'Edit Income': 'Editar ingreso',
    'Income Details': 'Detalles del ingreso',
    'No Income Yet': 'Todavía no hay ingresos',
    '{{count}} incomes': '{{count}} ingresos',
    'Income filters': 'Filtros de ingresos',
    'Narrow income by name or date':
      'Filtra ingresos por nombre o fecha',
    'No matching income': 'No hay ingresos coincidentes',
    'Try a different name or date range.':
      'Prueba con otro nombre o rango de fechas.',
    'Track salary, bonuses, and other inflows in one place.':
      'Controla el sueldo, los bonos y otros ingresos en un solo lugar.',
    'Add regular income such as salary, or occasional inflows like bonuses and gifts.':
      'Añade ingresos regulares como el sueldo, o ingresos ocasionales como bonos y regalos.',
    'Enter income source': 'Introduce la fuente del ingreso',
    'Enter amount': 'Introduce el importe',
    'Gross salary': 'Salario bruto',
    'Net salary': 'Salario neto',
    'Enter gross salary': 'Introduce el salario bruto',
    'Enter net salary': 'Introduce el salario neto',
    'Scan Payslip': 'Escanear nómina',
    'Upload a payslip image or PDF to autofill employer, net pay, date, and type. The file is saved with the income. Manual edit still works.':
      'Sube una imagen o PDF de la nómina para rellenar empresa, líquido, fecha y tipo. El archivo se guarda con el ingreso. También puedes editarlo a mano.',
    'Payslip preview': 'Vista previa de la nómina',
    'Payslip document': 'Nómina',
    'Choose payslip file': 'Elegir archivo de nómina',
    'Clear File': 'Borrar archivo',
    'Payslip analyzed and filled': 'Nómina analizada y rellenada',
    'Payslip analyzed. Review fields, then save.':
      'Nómina analizada. Revisa los campos y guarda.',
    'Failed to analyze payslip.': 'No se pudo analizar la nómina.',
    Salary: 'Sueldo',
    Freelance: 'Autónomo',
    Business: 'Negocio',
    Investment: 'Inversión',
    Dividends: 'Dividendos',
    Rental: 'Alquiler',
    Pension: 'Pensión',
    Refund: 'Reembolso',
    Bonus: 'Bono',
    Daily: 'Diarias',
    Analytics: 'Analítica',
    'Spend analytics': 'Analítica de gastos',
    'Income analytics': 'Analítica de ingresos',
    Comparison: 'Comparación',
    'Utility bills': 'Servicios',
    'Add Bill': 'Añadir factura',
    'Bill Date': 'Fecha de factura',
    'Bill Cost': 'Importe de factura',
    'Company Name': 'Nombre de la empresa',
    Provider: 'Proveedor',
    'Counter m³': 'Contador m³',
    'Month spend in m³': 'Consumo del mes m³',
    'Price per kW': 'Precio por kW',
    'Month cost': 'Coste del mes',
    'Month spend in m³ × {{factor}} × Price per kW':
      'Consumo del mes m³ × {{factor}} × precio por kW',
    'Bill document': 'Documento de la factura',
    'Choose bill file': 'Elegir archivo de factura',
    'Upload an image or PDF of the bill.':
      'Sube una imagen o PDF de la factura.',
    'Remove file': 'Quitar archivo',
    'Scan gas bill': 'Escanear factura de gas',
    'Upload an image or PDF of the bill to autofill provider, readings, and prices.':
      'Sube una imagen o PDF de la factura para rellenar proveedor, lecturas y precios.',
    'Gas bill analyzed and filled': 'Factura de gas analizada y rellenada',
    'Gas bill analyzed. Review fields, then save.':
      'Factura de gas analizada. Revisa los campos y guarda.',
    'Failed to analyze gas bill.': 'No se pudo analizar la factura de gas.',
    'Choose bill files': 'Elegir archivos de factura',
    'Add bill files': 'Añadir archivos de factura',
    'Upload one or more images or PDFs of the bill. All files are saved with the bill, and Analyze uses every page.':
      'Sube una o varias imágenes o PDFs de la factura. Todos los archivos se guardan con la factura y Analizar usa cada página.',
    'Failed to save gas bill.': 'No se pudo guardar la factura de gas.',
    'Scan water bill': 'Escanear factura de agua',
    'Water bill analyzed and filled': 'Factura de agua analizada y rellenada',
    'Water bill analyzed. Review fields, then save.':
      'Factura de agua analizada. Revisa los campos y guarda.',
    'Failed to analyze water bill.': 'No se pudo analizar la factura de agua.',
    'Failed to save water bill.': 'No se pudo guardar la factura de agua.',
    'Contract number': 'Número de contrato',
    'Current reading m³': 'Lectura actual m³',
    'Consumption m³': 'Consumo m³',
    'Price per m³': 'Precio por m³',
    'Includes water, sewer, VAT, and waste taxes.':
      'Incluye agua, alcantarillado, IVA e impuestos de residuos.',
    'Scan electricity bill': 'Escanear factura de electricidad',
    'Electricity bill analyzed and filled':
      'Factura de electricidad analizada y rellenada',
    'Electricity bill analyzed. Review fields, then save.':
      'Factura de electricidad analizada. Revisa los campos y guarda.',
    'Failed to analyze electricity bill.':
      'No se pudo analizar la factura de electricidad.',
    'Failed to save electricity bill.':
      'No se pudo guardar la factura de electricidad.',
    'Contracted power kW': 'Potencia contratada kW',
    'Includes power, energy, discounts, and taxes.':
      'Incluye potencia, energía, descuentos e impuestos.',
    'Bill documents': 'Documentos de la factura',
    Invoice: 'Factura',
    'Invoice number': 'Número de factura',
    'Supply address': 'Dirección de suministro',
    'Postal code': 'Código postal',
    'Billing period': 'Periodo de facturación',
    'Meter number': 'Número de contador',
    CUPS: 'CUPS',
    'Previous reading m³': 'Lectura anterior m³',
    'Meter and consumption': 'Contador y consumo',
    Prices: 'Precios',
    'Conversion factor kWh/m³': 'Factor de conversión kWh/m³',
    'Energy kWh': 'Energía kWh',
    'Price per kWh': 'Precio por kWh',
    'Energy cost': 'Coste de energía',
    'Total invoice': 'Importe factura',
    'Amount to pay or credit. Negative means a refund.':
      'Importe a pagar o abono. Un valor negativo es una devolución.',
    'Gas bill total (Importe Factura). Extra maintenance invoices stay in notes.':
      'Total de la factura de gas (Importe Factura). Las facturas extra de mantenimiento quedan en las notas.',
    'Energy kWh × Price per kWh. This is not the invoice total.':
      'Energía kWh × precio por kWh. Esto no es el total de la factura.',
    'Month spend in m³ × {{factor}} × Price per kWh':
      'Consumo del mes m³ × {{factor}} × precio por kWh',
    'Optional notes': 'Notas opcionales',
    'Add {{category}} Bill': 'Añadir factura de {{category}}',
    'Edit {{category}} Bill': 'Editar factura de {{category}}',
    'Are you sure you want to delete this bill?':
      '¿Seguro que quieres eliminar esta factura?',
    'Utility Bills Total': 'Total de servicios',
    'All Utility Bills': 'Todos los servicios',
    'Utility Bills Timeline': 'Cronología de servicios',
    'No utility bills in this period.': 'No hay facturas de servicios en este período.',
    'No utility bills on this day.': 'No hay facturas de servicios en este día.',
    'No utility bills in this month.': 'No hay facturas de servicios en este mes.',
    'No utility bills in this year.': 'No hay facturas de servicios en este año.',
    Bills: 'Facturas',
    Bill: 'Factura',
    'Bill Details': 'Detalles de la factura',
    'Edit Bill': 'Editar factura',
    Price: 'Precio',
    Currency: 'Moneda',
    Close: 'Cerrar',
    'Created At': 'Creado el',
    'Spend charts for all utility bills together and for each utility category.':
      'Gráficos de gasto de todos los servicios y de cada categoría.',
    'Breakdown of utility spend across every utility category.':
      'Desglose del gasto en servicios por cada categoría.',
    'Total utility bill spend over time across all categories.':
      'Gasto total en servicios a lo largo del tiempo en todas las categorías.',
    'Add daily purchases or utility bills first, then this page will show spend charts by category.':
      'Añade primero compras diarias o facturas de servicios; después esta página mostrará los gráficos de gasto.',
    'No Utility Bills Yet': 'Todavía no hay facturas de servicios',
    'Track electricity, water, gas, internet, and other recurring household bills in one place — amounts, periods, and providers.':
      'Controla electricidad, agua, gas, internet y otras facturas del hogar en un solo lugar: importes, periodos y proveedores.',
    Electricity: 'Electricidad',
    Gas: 'Gas',
    Internet: 'Internet',
    Phone: 'Teléfono',
    Rent: 'Alquiler',
    Telecommunications: 'Telecomunicaciones',
    'Mortgage/Credit': 'Hipoteca/Crédito',
    Mortgage: 'Hipoteca',
    Credit: 'Crédito',
    'Bank Name': 'Nombre del banco',
    Amount: 'Importe',
    Percentage: 'Porcentaje',
    'Monthly Quota': 'Cuota mensual',
    'Monthly Quotas': 'Cuotas mensuales',
    'Main Quota': 'Cuota principal',
    'Percentage Quota': 'Cuota porcentual',
    'Select period start and end to generate monthly quota rows.':
      'Selecciona el inicio y el fin del periodo para generar las filas de cuotas mensuales.',
    'For each month, fill both Main Quota and Percentage Quota, or leave both empty.':
      'Para cada mes, rellena la Cuota principal y la Cuota porcentual, o deja ambas vacías.',
    'Total To Pay': 'Total a pagar',
    Paid: 'Pagado',
    Remaining: 'Restante',
    'Payment Progress': 'Progreso de pago',
    'Payments are counted at the end of each month. {{paidMonths}} of {{plannedMonths}} monthly payments completed.':
      'Los pagos se cuentan al final de cada mes. {{paidMonths}} de {{plannedMonths}} pagos mensuales completados.',
    'Period Start': 'Inicio del periodo',
    'Period End': 'Fin del periodo',
    'Optional description': 'Descripción opcional',
    'No {{category}} Bills Yet': 'Todavía no hay facturas de {{category}}',
    'Track electricity bills here — amounts, periods, and providers.':
      'Controla aquí las facturas de electricidad: importes, periodos y proveedores.',
    'Track water bills here — amounts, periods, and providers.':
      'Controla aquí las facturas de agua: importes, periodos y proveedores.',
    'Track gas bills here — amounts, periods, and providers.':
      'Controla aquí las facturas de gas: importes, periodos y proveedores.',
    'Track internet, phone, and other telecom bills here — amounts, periods, and providers.':
      'Controla aquí las facturas de internet, teléfono y otros servicios de telecomunicaciones: importes, periodos y proveedores.',
    'Track mortgage and credit payments here — amounts, periods, and providers.':
      'Controla aquí los pagos de hipoteca y crédito: importes, periodos y proveedores.',
    Trash: 'Basura',
    'No Comparison Data Yet': 'Todavía no hay datos de comparación',
    'Add daily purchases from receipts first. Comparison tables are built by category, with markets as columns.':
      'Primero añade compras diarias desde tickets. Las tablas de comparación se agrupan por categoría y los mercados son las columnas.',
    'Each table groups receipt products by category. Columns are markets — if one market has several products of the same name, it gets extra columns.':
      'Cada tabla agrupa productos del ticket por categoría. Las columnas son mercados: si un mercado tiene varios productos con el mismo nombre, aparecen columnas extra.',
    Product: 'Producto',
    Markets: 'Mercados',
    Types: 'Tipos',
    'Each table is a category. Rows are specific product types (Beer, Coffee, Milk…). Columns are markets. Green is lowest price, red is highest.':
      'Cada tabla es una categoría. Las filas son tipos concretos (Cerveza, Café, Leche…). Las columnas son mercados. Verde es el precio más bajo, rojo el más alto.',
    Beer: 'Cerveza',
    Wine: 'Vino',
    Cider: 'Sidra',
    Spirits: 'Licores',
    'Soft Drink': 'Refresco',
    Juice: 'Zumo',
    'Energy Drink': 'Bebida energética',
    Coffee: 'Café',
    Tea: 'Té',
    Milk: 'Leche',
    'Plant Milk': 'Leche vegetal',
    Yogurt: 'Yogur',
    Cheese: 'Queso',
    Butter: 'Mantequilla',
    Cream: 'Nata',
    Bread: 'Pan',
    Pastry: 'Bollería',
    Chicken: 'Pollo',
    Beef: 'Ternera',
    Pork: 'Cerdo',
    Fish: 'Pescado',
    Seafood: 'Marisco',
    Fruit: 'Fruta',
    Vegetables: 'Verduras',
    Pasta: 'Pasta',
    Rice: 'Arroz',
    Flour: 'Harina',
    Sugar: 'Azúcar',
    'Salt & Spices': 'Sal y especias',
    'Ready Meal': 'Plato preparado',
    Tofu: 'Tofu',
    Oil: 'Aceite',
    Sauce: 'Salsa',
    Snacks: 'Snacks',
    Chocolate: 'Chocolate',
    Candy: 'Chuches',
    Cereal: 'Cereales',
    'Ice Cream': 'Helado',
    'Frozen Meal': 'Plato congelado',
    Shampoo: 'Champú',
    'Hair Spray': 'Laca',
    Deodorant: 'Desodorante',
    Soap: 'Jabón',
    Toothpaste: 'Pasta de dientes',
    Toothbrush: 'Cepillo de dientes',
    Razor: 'Maquinilla de afeitar',
    'Feminine Care': 'Higiene femenina',
    Detergent: 'Detergente',
    Cleaner: 'Limpiador',
    'Toilet Paper': 'Papel higiénico',
    'Paper Towels': 'Papel de cocina',
    Dishwashing: 'Lavavajillas',
    'Air Freshener': 'Ambientador',
    'Trash Bags': 'Bolsas de basura',
    Diapers: 'Pañales',
    'Baby Food': 'Comida bebé',
    'Pet Food': 'Comida mascotas',
    'Cat Litter': 'Arena para gatos',
    Medicine: 'Medicamento',
    Vitamins: 'Vitaminas',
    Batteries: 'Pilas',
    'No Daily Purchases Yet': 'Todavía no hay compras diarias',
    'Create a daily purchase and track all products bought in one market visit.':
      'Crea una compra diaria y lleva el control de todos los productos comprados en una sola visita al mercado.',
    Occasional: 'Ocasionales',
    'No Occasional Purchases Yet': 'Todavía no hay compras ocasionales',
    'Add non-daily purchases here — larger buys or smaller one-offs — separate from day-to-day shopping.':
      'Añade aquí compras que no son del día a día — grandes o pequeñas — separadas de la compra diaria.',
    'Comparison Is Coming Soon': 'La comparación llegará pronto',
    'This tab is intentionally left as a placeholder for now. We can return later and build product comparison.':
      'Esta pestaña se deja por ahora como marcador de posición. Más adelante podemos volver y construir la comparación de productos.',
    'Purchase Details': 'Detalles de la compra',
    'Create Daily Purchase': 'Crear compra diaria',
    'Create Occasional Purchase': 'Crear compra ocasional',
    'Edit Daily Purchase': 'Editar compra diaria',
    'Add Daily Purchase': 'Añadir compra diaria',
    'Edit Occasional Purchase': 'Editar compra ocasional',
    'Add Occasional Purchase': 'Añadir compra ocasional',
    'Manage your daily shopping, track occasional purchases separately, and leave room for a future products catalog.':
      'Gestiona tus compras diarias, registra por separado las compras ocasionales y deja espacio para un futuro catálogo de productos.',
    'Scan Receipt': 'Escanear recibo',
    'Upload a receipt/check photo to autofill products. Manual add still works.':
      'Sube una foto del recibo para completar los productos automáticamente. También puedes seguir añadiéndolos manualmente.',
    'Scan Purchase Image': 'Escanear imagen de compra',
    'Upload an invoice, receipt, or product photo to autofill this occasional purchase. Manual edit still works.':
      'Sube una factura, recibo o foto del producto para rellenar esta compra ocasional. También puedes editarla manualmente.',
    'Choose purchase image': 'Elegir imagen de compra',
    'Purchase image preview': 'Vista previa de la imagen de compra',
    'Analyze & Fill Form': 'Analizar y rellenar formulario',
    'Purchase image analyzed and filled': 'Imagen de compra analizada y rellenada',
    'Purchase image analyzed. Review fields, then save.':
      'Imagen de compra analizada. Revisa los campos y guarda.',
    'Failed to analyze purchase image.': 'No se pudo analizar la imagen de compra.',
    'Review and edit if needed.': 'Revísalo y edítalo si hace falta.',
    'Receipt preview': 'Vista previa del recibo',
    'Choose receipt image': 'Elegir imagen del recibo',
    'Analyzing...': 'Analizando...',
    'Analyze & Fill List': 'Analizar y rellenar lista',
    'Clear Image': 'Borrar imagen',
    'Market Name': 'Nombre del mercado',
    'Enter market name': 'Introduce el nombre del mercado',
    'Total Amount': 'Importe total',
    'Receipt Number': 'Número de recibo',
    'Payment Method': 'Método de pago',
    'Ticket / invoice number': 'Número de ticket / factura',
    'card, cash, bizum...': 'tarjeta, efectivo, bizum...',
    'Add Product': 'Añadir producto',
    'Enter product name': 'Introduce el nombre del producto',
    'Line Total': 'Total de línea',
    'Line importe / total': 'Importe de línea / total',
    'Receipt analyzed and filled': 'Recibo analizado y completado',
    'Review and edit if needed. You can still add more manually.':
      'Revísalo y edítalo si hace falta. Aún puedes añadir más manualmente.',
    'Receipt analyzed. Review products, then save. You can still add more manually.':
      'Recibo analizado. Revisa los productos y luego guarda. Aún puedes añadir más manualmente.',
    'Failed to analyze receipt image.': 'No se pudo analizar la imagen del recibo.',
    'No Analytics Yet': 'Todavía no hay analítica',
    'Add daily purchases with products first, then this analytics tab will show spend charts by category.':
      'Añade primero compras diarias con productos y luego esta pestaña mostrará gráficos de gasto por categoría.',
    'Add income first, then this page will show charts by type.':
      'Añade primero ingresos y luego esta página mostrará gráficos por tipo.',
    'See charts for salary, bonuses, and other inflows over time.':
      'Consulta gráficos de sueldo, bonos y otros ingresos a lo largo del tiempo.',
    'Total Income': 'Ingresos totales',
    'Regular Income': 'Ingresos regulares',
    'Occasional Income': 'Ingresos ocasionales',
    'Income Records': 'Registros de ingresos',
    'Income by Type': 'Ingresos por tipo',
    'Breakdown of income across salary, freelance, and other types.':
      'Distribución de ingresos entre sueldo, autónomo y otros tipos.',
    'No income in this period.': 'No hay ingresos en este período.',
    'Daily Spend': 'Gasto diario',
    'Occasional Purchases Total': 'Total de compras ocasionales',
    'Total Purchases': 'Cantidad de compras',
    'Tracked Products': 'Productos registrados',
    'Top Category': 'Categoría principal',
    Dairy: 'Lácteos',
    Eggs: 'Huevos',
    Meat: 'Carne',
    'Fish & Seafood': 'Pescado y marisco',
    'Fruit & Veg': 'Frutas y verduras',
    Water: 'Agua',
    Alcohol: 'Alcohol',
    'Other Drinks': 'Otras bebidas',
    Drinks: 'Bebidas',
    Average: 'Media',
    Bakery: 'Panadería',
    'Pasta & Rice': 'Pasta y arroz',
    'Oils & Condiments': 'Aceites y condimentos',
    'Canned Food': 'Conservas',
    Frozen: 'Congelados',
    'Breakfast & Cereals': 'Desayuno y cereales',
    Household: 'Hogar',
    Hygiene: 'Higiene',
    'Personal Care': 'Cuidado personal',
    Baby: 'Bebé',
    Pet: 'Mascotas',
    Pharmacy: 'Farmacia',
    'Ready Meals': 'Platos preparados',
    'Snacks & Sweets': 'Snacks y dulces',
    'Coffee & Tea': 'Café y té',
    Charcuterie: 'Charcutería',
    Legumes: 'Legumbres',
    'Nuts & Seeds': 'Frutos secos y semillas',
    'Baking Ingredients': 'Ingredientes de repostería',
    'Plant-based': 'Vegetal / plant-based',
    Tobacco: 'Tabaco',
    Stationery: 'Papelería',
    'Batteries & Tech': 'Pilas y tecnología',
    'Flowers & Plants': 'Flores y plantas',
    Computers: 'Ordenadores',
    'Phones & Tablets': 'Móviles y tablets',
    Electronics: 'Electrónica',
    'TV & Audio': 'TV y audio',
    Gaming: 'Gaming',
    'Camera & Photo': 'Cámara y foto',
    'Smart Home': 'Hogar inteligente',
    Furniture: 'Muebles',
    'Bedding & Mattresses': 'Ropa de cama y colchones',
    Appliances: 'Electrodomésticos',
    Kitchenware: 'Utensilios de cocina',
    'Home Improvement': 'Reformas del hogar',
    Vehicles: 'Vehículos',
    Bicycles: 'Bicicletas',
    Clothing: 'Ropa',
    'Shoes & Accessories': 'Zapatos y complementos',
    'Jewelry & Watches': 'Joyería y relojes',
    Eyewear: 'Gafas',
    'Luggage & Bags': 'Maletas y bolsos',
    'Home & Decor': 'Hogar y decoración',
    'Garden & Outdoor': 'Jardín y exterior',
    Sports: 'Deporte',
    'Fitness Equipment': 'Equipamiento fitness',
    'Tools & DIY': 'Herramientas',
    'Office Equipment': 'Equipamiento de oficina',
    'Baby Gear': 'Artículos de bebé',
    'Toys & Kids': 'Juguetes',
    Pets: 'Mascotas',
    'Musical Instruments': 'Instrumentos musicales',
    'Books & Media': 'Libros y medios',
    'Hobbies & Collectibles': 'Hobbies y coleccionismo',
    'Health Equipment': 'Equipamiento de salud',
    Education: 'Educación',
    'Software & Digital': 'Software y digital',
    Travel: 'Viajes',
    'Spend by Category': 'Gasto por categoría',
    'Based on daily-purchase product lines and their estimated totals.':
      'Basado en las líneas de productos de las compras diarias y sus importes estimados.',
    'Category Comparison': 'Comparación de categorías',
    'Higher bars show categories where most of your money goes.':
      'Las barras más altas muestran las categorías en las que se va la mayor parte de tu dinero.',
    'Spend Map': 'Mapa de gastos',
    'Larger tiles show categories where most of your money goes.':
      'Las baldosas más grandes muestran las categorías en las que se va la mayor parte de tu dinero.',
    Category: 'Categoría',
    Products: 'Productos',
    Qty: 'Cant.',
    'No products in this category.': 'No hay productos en esta categoría.',
    'Click a category to see its products.':
      'Haz clic en una categoría para ver sus productos.',
    'Last month': 'Último mes',
    'Last 3 months': 'Últimos 3 meses',
    'Last 6 months': 'Últimos 6 meses',
    'No purchases in this period.': 'No hay compras en este período.',
    'Spend Timeline': 'Cronología del gasto',
    'Income Timeline': 'Cronología de ingresos',
    'By year': 'Por año',
    'By month': 'Por mes',
    'See how your income changes over time across recorded inflows.':
      'Mira cómo cambian tus ingresos con el tiempo según los ingresos registrados.',
    'See how your income changes over time across recorded inflows. Click a month to see its income.':
      'Mira cómo cambian tus ingresos con el tiempo. Haz clic en un mes para ver sus ingresos.',
    'A yearly view helps you compare overall income trends.':
      'La vista anual te ayuda a comparar las tendencias generales de ingresos.',
    'A yearly view helps you compare overall income trends. Click a year to see its income.':
      'La vista anual te ayuda a comparar las tendencias generales de ingresos. Haz clic en un año para ver sus ingresos.',
    'No income in this year.': 'No hay ingresos en este año.',
    'No income in this month.': 'No hay ingresos en este mes.',
    Month: 'Mes',
    'Spend by Month': 'Gasto por mes',
    'See how your spending changes over time across recorded purchases.':
      'Mira cómo cambian tus gastos con el tiempo según las compras registradas.',
    'See how your spending changes over time across recorded purchases. Click a day to see its purchases.':
      'Mira cómo cambian tus gastos con el tiempo. Haz clic en un día para ver sus compras.',
    'Spend by Year': 'Gasto por año',
    'A yearly view helps you compare overall purchase trends.':
      'La vista anual te ayuda a comparar las tendencias generales de compra.',
    'A yearly view helps you compare overall purchase trends. Click a year to see its purchases.':
      'La vista anual te ayuda a comparar las tendencias generales de compra. Haz clic en un año para ver sus compras.',
    'No purchases on this day.': 'No hay compras en este día.',
    'No purchases in this year.': 'No hay compras en este año.',
    'Average Daily Receipt Spend': 'Gasto medio por recibo diario',
    Search: 'Buscar',
    Information: 'Información',
    Wedding: 'Boda',
    Language: 'Idioma',
    'Open menu': 'Abrir menú',
    'Close menu': 'Cerrar menú',
    'Search all items... (memories, todos, ideas, events, recipes, movies, and more)':
      'Buscar en todo... (recuerdos, tareas, ideas, eventos, recetas, películas y más)',
    'Show all results for "{{query}}"':
      'Mostrar todos los resultados para "{{query}}"',
    'No results found for "{{query}}"':
      'No se encontraron resultados para "{{query}}"',
    'Open user menu': 'Abrir menú de usuario',
    'User menu': 'Menú de usuario',
    Logout: 'Cerrar sesión',
    'Are you sure you want to log out?': '¿Seguro que quieres cerrar sesión?',
    'Log Out': 'Cerrar sesión',
    'Confirmation Required': 'Se requiere confirmación',
    'Action not allowed': 'Acción no permitida',
    Cancel: 'Cancelar',
    Confirm: 'Confirmar',
    'Clear selected value': 'Borrar valor seleccionado',
    'Search...': 'Buscar...',
    'No items found': 'No se encontraron elementos',
    'Select {{placeholder}}': 'Selecciona {{placeholder}}',
    '{{count}} selected': '{{count}} seleccionados',
    'Select options': 'Selecciona opciones',
    'Good morning': 'Buenos días',
    'Good afternoon': 'Buenas tardes',
    'Good evening': 'Buenas noches',
    'This Week': 'Esta semana',
    'Active To-Dos': 'Tareas activas',
    'Active Objectives': 'Objetivos activos',
    'Total Tasks': 'Tareas totales',
    'View All': 'Ver todo',
    'Upcoming Events': 'Próximos eventos',
    'Next 30 Days': 'Próximos 30 días',
    Today: 'Hoy',
    Tomorrow: 'Mañana',
    '{{count}} days': '{{count}} días',
    'No upcoming events': 'No hay próximos eventos',
    'Your calendar is clear for now': 'Tu calendario está libre por ahora',
    All: 'Todo',
    Vocabulary: 'Vocabulario',
    'Search all items...': 'Buscar en todo...',
    'Found {{count}} result': 'Se encontró {{count}} resultado',
    'Found {{count}} results': 'Se encontraron {{count}} resultados',
    'in {{type}}': 'en {{type}}',
    'No results found': 'No se encontraron resultados',
    'Enter a search term to find memories, todos, ideas, events, and more':
      'Ingresa un término para encontrar recuerdos, tareas, ideas, eventos y más',
    'Untitled Memory': 'Recuerdo sin título',
    'Untitled Todo': 'Tarea sin título',
    'Untitled Idea': 'Idea sin título',
    'Untitled Link': 'Enlace sin título',
    'Untitled Event': 'Evento sin título',
    'Untitled Board': 'Pizarra sin título',
    'Untitled Word': 'Palabra sin título',
    'Untitled Recipe': 'Receta sin título',
    'Untitled Movie': 'Película sin título',
    'Untitled Location': 'Lugar sin título',
    'Untitled Objective': 'Objetivo sin título',
    'Untitled File': 'Archivo sin título',
    'Email address': 'Correo electrónico',
    'Enter email': 'Introduce el correo',
    'Wrong email or password': 'Correo o contraseña incorrectos',
    Password: 'Contraseña',
    'Remember me': 'Recuérdame',
    Login: 'Iniciar sesión',
    'Close notification': 'Cerrar notificación',
    Success: 'Éxito',
    Warning: 'Advertencia',
    Error: 'Error',
    Info: 'Información',
    Trips: 'Viajes',
    Weddings: 'Bodas',
    Filters: 'Filtros',
    Reset: 'Restablecer',
    Delete: 'Eliminar',
    Name: 'Nombre',
    Tag: 'Etiqueta',
    Genre: 'Género',
    Year: 'Año',
    Status: 'Estado',
    Rating: 'Calificación',
    'Rating Mode': 'Modo de calificación',
    'Greater than': 'Mayor que',
    'Less than': 'Menor que',
    'Filter by name': 'Filtrar por nombre',
    'Filter by tag': 'Filtrar por etiqueta',
    'Filter by title': 'Filtrar por título',
    'Filter by year': 'Filtrar por año',
    'Filter by rating': 'Filtrar por calificación',
    'Capture meaningful moments, lessons, and notes you want to keep close':
      'Guarda momentos importantes, lecciones y notas que quieras tener cerca',
    'Create Memory': 'Crear recuerdo',
    '{{count}} memories': '{{count}} recuerdos',
    'No Memories Yet': 'Aún no hay recuerdos',
    'Create your first memory to keep meaningful moments, reflections, and notes in one place.':
      'Crea tu primer recuerdo para guardar momentos importantes, reflexiones y notas en un solo lugar.',
    'Add Memory': 'Agregar recuerdo',
    'Edit Memory': 'Editar recuerdo',
    'Memories filters': 'Filtros de recuerdos',
    'Narrow memories by name or tag': 'Filtra recuerdos por nombre o etiqueta',
    'Are you sure you want to delete this memory?':
      '¿Seguro que quieres eliminar este recuerdo?',
    'Collect sparks, concepts, and future directions worth exploring':
      'Reúne chispas, conceptos y direcciones futuras que valga la pena explorar',
    'Create Idea': 'Crear idea',
    '{{count}} ideas': '{{count}} ideas',
    'No Ideas Yet': 'Aún no hay ideas',
    'Capture your first idea to start building a space for concepts, experiments, and future plans.':
      'Guarda tu primera idea para empezar a construir un espacio para conceptos, experimentos y planes futuros.',
    'Add Idea': 'Agregar idea',
    'Edit Idea': 'Editar idea',
    'Ideas filters': 'Filtros de ideas',
    'Refine ideas by name or tag': 'Refina ideas por nombre o etiqueta',
    'Are you sure you want to delete this idea?':
      '¿Seguro que quieres eliminar esta idea?',
    'Track movies, series, and animations in one place':
      'Sigue películas, series y animaciones en un solo lugar',
    Movie: 'Película',
    Series: 'Series',
    Animations: 'Animaciones',
    Animation: 'Animación',
    'Create {{item}}': 'Crear {{item}}',
    '{{count}} shown in {{category}}': '{{count}} mostrados en {{category}}',
    Filtered: 'Filtrado',
    'No Matching {{category}}': 'No hay {{category}} coincidentes',
    'No {{category}} Yet': 'Aún no hay {{category}}',
    'Try changing or resetting the current filters to see more results.':
      'Prueba cambiar o restablecer los filtros actuales para ver más resultados.',
    'Create your first {{item}} to start building this list.':
      'Crea tu primer {{item}} para empezar esta lista.',
    'Reset Filters': 'Restablecer filtros',
    Previous: 'Anterior',
    Next: 'Siguiente',
    'Showing {{from}}-{{to}} of {{total}}':
      'Mostrando {{from}}-{{to}} de {{total}}',
    'Movies Filters': 'Filtros de películas',
    '{{item}} Details': 'Detalles de {{item}}',
    'Edit {{item}}': 'Editar {{item}}',
    'Add {{item}}': 'Agregar {{item}}',
    'Are you sure you want to delete this {{item}}?':
      '¿Seguro que quieres eliminar este {{item}}?',
    'Narrow movies, series, and animations by key details':
      'Filtra películas, series y animaciones por detalles clave',
    Planned: 'Planificado',
    Watching: 'Viendo',
    Completed: 'Completado',
    'Organize your tasks across paused, active, and finished states':
      'Organiza tus tareas entre estados pausados, activos y finalizados',
    'Create To-Do': 'Crear tarea',
    Paused: 'Pausado',
    Active: 'Activo',
    Finished: 'Finalizado',
    total: 'total',
    'Waiting or deferred': 'En espera o pospuesto',
    'Current focus': 'Enfoque actual',
    'Completed work': 'Trabajo completado',
    'There are no active tasks yet': 'Aún no hay tareas activas',
    'There are no paused tasks yet': 'Aún no hay tareas pausadas',
    'There are no finished tasks yet': 'Aún no hay tareas finalizadas',
    'Add To-Do': 'Agregar tarea',
    'Edit To-Do': 'Editar tarea',
    'Are you sure you want to delete this to-do?':
      '¿Seguro que quieres eliminar esta tarea?',
    Update: 'Actualizar',
    Create: 'Crear',
    'Select genres': 'Seleccionar géneros',
    '{{item}} Title': 'Título de {{item}}',
    'Image Link': 'Enlace de imagen',
    'Watch Link': 'Enlace para ver',
    Note: 'Nota',
    'Rating must be a number between 0.0 and 10.0.':
      'La calificación debe ser un número entre 0.0 y 10.0.',
    'Why do you want to keep this {{item}} in the list?':
      '¿Por qué quieres mantener este {{item}} en la lista?',
    Visited: 'Visitado',
    Upcoming: 'Próximo',
    'To Visit': 'Por visitar',
    State: 'Estado',
    Location: 'Ubicación',
    List: 'Lista',
    'City, place, or map link...': 'Ciudad, lugar o enlace del mapa...',
    'Find on map': 'Buscar en el mapa',
    'Searching...': 'Buscando...',
    'Map pin set': 'Marcador del mapa definido',
    'Use Find on map so this trip can appear as a marker in map view.':
      'Usa «Buscar en el mapa» para que este viaje aparezca como marcador.',
    'Enter a place name or map link first.':
      'Introduce primero un lugar o un enlace del mapa.',
    'No places found for this location.': 'No se encontraron lugares para esta ubicación.',
    'Could not look up this location. Try again.':
      'No se pudo buscar esta ubicación. Inténtalo de nuevo.',
    'No map locations yet': 'Aún no hay ubicaciones en el mapa',
    'Add a location to your trips so they can appear as markers on the map.':
      'Añade una ubicación a tus viajes para que aparezcan como marcadores en el mapa.',
    'Loading map markers...': 'Cargando marcadores del mapa...',
    'Plan and organize your travel history and future adventures.':
      'Planifica y organiza tu historial de viajes y futuras aventuras.',
    'Create Trip': 'Crear viaje',
    'No Trips In {{tab}}': 'No hay viajes en {{tab}}',
    'No Trips Yet': 'Aún no hay viajes',
    'No Trips Match Filters': 'Ningún viaje coincide con los filtros',
    'Try adjusting your filters or reset them to see all trips.':
      'Prueba a ajustar los filtros o restablécelos para ver todos los viajes.',
    '{{count}} trips': '{{count}} viajes',
    'Trips filters': 'Filtros de viajes',
    'Narrow trips by name or state':
      'Filtra viajes por nombre o estado',
    'Add a trip and it will be categorized automatically based on its visit dates.':
      'Agrega un viaje y se categorizará automáticamente según sus fechas de visita.',
    'Add Trip': 'Agregar viaje',
    'Edit Trip': 'Editar viaje',
    'Are you sure you want to delete trip "{{name}}"?':
      '¿Seguro que quieres eliminar el viaje "{{name}}"?',
    'Map link': 'Enlace del mapa',
    Description: 'Descripción',
    'Visit dates': 'Fechas de visita',
    'No dates selected. This trip will appear in "{{tab}}".':
      'No se seleccionaron fechas. Este viaje aparecerá en "{{tab}}".',
    'Image links (one URL per line)': 'Enlaces de imágenes (una URL por línea)',
    'Trip name...': 'Nombre del viaje...',
    'What is this trip about?': '¿De qué trata este viaje?',
    'Add your travel notes...': 'Agrega tus notas de viaje...',
    'Open map': 'Abrir mapa',
    'View details': 'Ver detalles',
    'No notes yet': 'Aún no hay notas',
    'Or paste an image link': 'O pega un enlace de imagen',
    'Use link': 'Usar enlace',
    'Additional images (one URL per line)':
      'Imágenes adicionales (una URL por línea)',
    'Upload PDFs, videos, images, and other trip files.':
      'Sube PDFs, videos, imágenes y otros archivos del viaje.',
    'No files yet': 'Aún no hay archivos',
    'Failed to upload file.': 'No se pudo subir el archivo.',
    'Select a file first.': 'Selecciona un archivo primero.',
    'Add file': 'Añadir archivo',
    '{{count}} images': '{{count}} imágenes',
    '{{count}} notes': '{{count}} notas',
    '{{count}} dates': '{{count}} fechas',
    'Organize and manage your important links and resources':
      'Organiza y administra tus enlaces y recursos importantes',
    Link: 'Enlace',
    Folder: 'Carpeta',
    Board: 'Pizarra',
    'Add Link': 'Agregar enlace',
    'Add Folder': 'Agregar carpeta',
    'Go back': 'Volver',
    Back: 'Atrás',
    '{{count}} items': '{{count}} elementos',
    'No Links Yet': 'Aún no hay enlaces',
    'Add your first link or folder to start organizing useful resources in one place.':
      'Agrega tu primer enlace o carpeta para empezar a organizar recursos útiles en un solo lugar.',
    URL: 'URL',
    'link name...': 'nombre del enlace...',
    'folder name...': 'nombre de la carpeta...',
    'link url...': 'url del enlace...',
    'Save places, routes, and addresses you want to revisit':
      'Guarda lugares, rutas y direcciones a las que quieras volver',
    'Create Location': 'Crear lugar',
    'No Locations Yet': 'Aún no hay lugares',
    'Create your first location to keep addresses, destinations, and useful places easy to find.':
      'Crea tu primer lugar para mantener direcciones, destinos y sitios útiles fáciles de encontrar.',
    'Add Location': 'Agregar lugar',
    'Edit Location': 'Editar lugar',
    'Are you really want to delete this Location?':
      '¿De verdad quieres eliminar este lugar?',
    Address: 'Dirección',
    Map: 'Mapa',
    City: 'Ciudad',
    Type: 'Tipo',
    'Location name...': 'Nombre del lugar...',
    'Location address...': 'Dirección del lugar...',
    'Location map...': 'Mapa del lugar...',
    'Add an address or map link to show this place on the map.':
      'Añade una dirección o un enlace de mapa para mostrar este lugar.',
    'Location city...': 'Ciudad del lugar...',
    'Location description...': 'Descripción del lugar...',
    'Home-Address': 'Dirección de casa',
    'Work-Address': 'Dirección de trabajo',
    Hotel: 'Hotel',
    Restaurant: 'Restaurante',
    Pizzeria: 'Pizzería',
    'Fast-Food': 'Comida rápida',
    Bar: 'Bar',
    Pub: 'Pub',
    Cafe: 'Cafetería',
    Opera: 'Ópera',
    Theater: 'Teatro',
    Cinema: 'Cine',
    Club: 'Club',
    Disco: 'Discoteca',
    'Tourist-Attraction': 'Atracción turística',
    Beach: 'Playa',
    Other: 'Otro',
    'Start at': 'Comienza en',
    'End at': 'Termina en',
    Priority: 'Prioridad',
    High: 'Alta',
    Medium: 'Media',
    Low: 'Baja',
    'To-Do name...': 'Nombre de la tarea...',
    'To-Do description...': 'Descripción de la tarea...',
    'Due today': 'Vence hoy',
    Overdue: 'Vencido',
    'No deadline': 'Sin fecha límite',
    'No active ToDos': 'No hay tareas activas',
    'Create a new to-do to get started': 'Crea una nueva tarea para comenzar',
    'Create and manage your whiteboards': 'Crea y administra tus pizarras',
    'Create New Folder': 'Crear carpeta nueva',
    'New Folder': 'Nueva carpeta',
    'Create New Whiteboard': 'Crear pizarra nueva',
    'New Board': 'Nuevo tablero',
    'Filter By': 'Filtrar por',
    'Sort By': 'Ordenar por',
    Date: 'Fecha',
    Order: 'Orden',
    Show: 'Mostrar',
    'Folders only': 'Solo carpetas',
    'Boards only': 'Solo tableros',
    'A - Z': 'A - Z',
    'Z - A': 'Z - A',
    'Oldest - Newest': 'Más antiguos - más nuevos',
    'Newest - Oldest': 'Más nuevos - más antiguos',
    Root: 'Raíz',
    'Delete folder': 'Eliminar carpeta',
    'Folder must be empty to delete':
      'La carpeta debe estar vacía para eliminarla',
    'No Whiteboards Yet': 'Aún no hay pizarras',
    'Create your first whiteboard to get started':
      'Crea tu primera pizarra para comenzar',
    'Add Whiteboard': 'Agregar pizarra',
    'Edit Whiteboard': 'Editar pizarra',
    'Folder name is required': 'El nombre de la carpeta es obligatorio',
    'Could not create folder. Please try again.':
      'No se pudo crear la carpeta. Inténtalo de nuevo.',
    'Could not update folder. Please try again.':
      'No se pudo actualizar la carpeta. Inténtalo de nuevo.',
    'Create Folder': 'Crear carpeta',
    'Folder name...': 'Nombre de la carpeta...',
    'Edit Folder': 'Editar carpeta',
    'Move to folder': 'Mover a carpeta',
    Save: 'Guardar',
    'Are you really want to delete this Whiteboard?':
      '¿De verdad quieres eliminar esta pizarra?',
    'Delete folder "{{name}}"?': '¿Eliminar la carpeta "{{name}}"?',
    'Whiteboard name...': 'Nombre de la pizarra...',
    'Manage and organize your uploaded files':
      'Administra y organiza tus archivos subidos',
    'Upload File': 'Subir archivo',
    'Available Space': 'Espacio disponible',
    Size: 'Tamaño',
    'Smallest - Largest': 'De menor a mayor',
    'Largest - Smallest': 'De mayor a menor',
    Visibility: 'Visibilidad',
    Public: 'Público',
    Private: 'Privado',
    'This folder is empty': 'Esta carpeta está vacía',
    'Create a folder or upload a file here':
      'Crea una carpeta o sube aquí un archivo',
    'Are you sure you want to delete this file?':
      '¿Seguro que quieres eliminar este archivo?',
    'Create folder in {{name}}': 'Crear carpeta en {{name}}',
    'Create folder in Root': 'Crear carpeta en la raíz',
    'Folder name': 'Nombre de la carpeta',
    'Type folder name...': 'Escribe el nombre de la carpeta...',
    'Creating...': 'Creando...',
    'Upload file to {{name}}': 'Subir archivo a {{name}}',
    'Upload file to Root': 'Subir archivo a la raíz',
    'Edit file: {{name}}': 'Editar archivo: {{name}}',
    'Edit file': 'Editar archivo',
    'File name is required': 'El nombre del archivo es obligatorio',
    'Could not update file. Please try again.':
      'No se pudo actualizar el archivo. Inténtalo de nuevo.',
    'Type file name...': 'Escribe el nombre del archivo...',
    'file description...': 'descripción del archivo...',
    'Public file (accessible via direct link)':
      'Archivo público (accesible por enlace directo)',
    'When disabled, file is only accessible through the app preview.':
      'Cuando está desactivado, el archivo solo es accesible desde la vista previa de la app.',
    'Saving...': 'Guardando...',
    'Text File Preview': 'Vista previa de archivo de texto',
    'Audio Preview': 'Vista previa de audio',
    'Loading audio...': 'Cargando audio...',
    'Failed to load audio': 'No se pudo cargar el audio',
    'Office Preview': 'Vista previa de Office',
    'Archive Preview': 'Vista previa de archivo comprimido',
    'Loading document...': 'Cargando documento...',
    'Failed to load document': 'No se pudo cargar el documento',
    'This file type cannot be previewed': 'Este tipo de archivo no se puede previsualizar',
    'Empty archive': 'El archivo está vacío',
    'Failed to open archive': 'No se pudo abrir el archivo',
    Extract: 'Extraer',
    'This archive is password-protected': 'Este archivo está protegido con contraseña',
    'Open archive': 'Abrir archivo',
    'Edit folder: {{name}}': 'Editar carpeta: {{name}}',
    'Edit folder': 'Editar carpeta',
    'Name is required': 'El nombre es obligatorio',
    Title: 'Título',
    'Select File': 'Seleccionar archivo',
    Upload: 'Subir',
    'File is required': 'El archivo es obligatorio',
    'file name...': 'nombre del archivo...',
    Download: 'Descargar',
    'Copied!': '¡Copiado!',
    'Copy link': 'Copiar enlace',
    'Public file': 'Archivo público',
    'Private file': 'Archivo privado',
    Edit: 'Editar',
    'Configure workspace-level sections from one place.':
      'Configura las secciones del espacio de trabajo desde un solo lugar.',
    'Settings under development': 'La configuración está en desarrollo',
    Sections: 'Secciones',
    'Workspace Settings': 'Configuración del espacio de trabajo',
    Users: 'Usuarios',
    'Manage members, roles, and access for this workspace.':
      'Administra miembros, roles y accesos para este espacio de trabajo.',
    'Failed to load users.': 'No se pudieron cargar los usuarios.',
    'User created successfully.': 'Usuario creado correctamente.',
    'Failed to update user.': 'No se pudo actualizar el usuario.',
    'Failed to create user.': 'No se pudo crear el usuario.',
    'You cannot delete your own user.': 'No puedes eliminar tu propio usuario.',
    'Failed to delete user.': 'No se pudo eliminar el usuario.',
    'Create User': 'Crear usuario',
    'Workspace members': 'Miembros del espacio de trabajo',
    'Users directory': 'Directorio de usuarios',
    'Review all users, update access, and create new accounts from this section.':
      'Revisa todos los usuarios, actualiza accesos y crea nuevas cuentas desde esta sección.',
    'Loading users...': 'Cargando usuarios...',
    'No users yet': 'Aún no hay usuarios',
    'Create the first user for this workspace.':
      'Crea el primer usuario para este espacio de trabajo.',
    Blocked: 'Bloqueado',
    Created: 'Creado',
    'Edit user': 'Editar usuario',
    'Delete user': 'Eliminar usuario',
    User: 'Usuario',
    Admin: 'Administrador',
    Hide: 'Ocultar',
    View: 'Ver',
    'Edit User': 'Editar usuario',
    'First name, last name, and email are required.':
      'El nombre, apellido y correo electrónico son obligatorios.',
    'Enter a valid email address.': 'Ingresa un correo electrónico válido.',
    'Password is required when creating a user.':
      'La contraseña es obligatoria al crear un usuario.',
    'Repeat password is required when creating a user.':
      'Debes repetir la contraseña al crear un usuario.',
    'Password and repeat password are required.':
      'La contraseña y su repetición son obligatorias.',
    'Password and repeat password must match.':
      'La contraseña y su repetición deben coincidir.',
    'First Name': 'Nombre',
    'Last Name': 'Apellido',
    'First name': 'Nombre',
    'Last name': 'Apellido',
    Email: 'Correo electrónico',
    Role: 'Rol',
    Modules: 'Módulos',
    Code: 'Código',
    Permission: 'Permiso',
    'Set all': 'Establecer todos',
    'Enable private mode for this user':
      'Activar el modo privado para este usuario',
    'When private mode is on, only public modules are visible to this user.':
      'Si el modo privado está activado, este usuario solo verá los módulos públicos.',
    'You cannot change your own role or blocked status.':
      'No puedes cambiar tu propio rol ni estado de bloqueo.',
    'New Password': 'Nueva contraseña',
    'Leave blank to keep current password':
      'Déjalo vacío para conservar la contraseña actual',
    'Repeat New Password': 'Repite la nueva contraseña',
    'Repeat the new password': 'Repite la nueva contraseña',
    'Repeat Password': 'Repetir contraseña',
    'Set each module to hide, view, or edit for this user.':
      'Configura cada módulo para ocultar, ver o editar para este usuario.',
    'Admin users keep full access across every module.':
      'Los administradores mantienen acceso completo a todos los módulos.',
    'Update User': 'Actualizar usuario',
    Add: 'Agregar',
    Word: 'Palabra',
    Done: 'Hecho',
    DeadLine: 'Fecha límite',
    'Select date': 'Seleccionar fecha',
    'Choose date': 'Elegir fecha',
    'dd/mm/yyyy': 'dd/mm/aaaa',
    'Invalid date': 'Fecha inválida',
    Periodicity: 'Periodicidad',
    Notification: 'Notificación',
    'Enable notification': 'Activar notificación',
    'Plan and manage your events': 'Planifica y gestiona tus eventos',
    'Add Event': 'Agregar evento',
    'Update Event': 'Actualizar evento',
    'No Events Yet': 'Aún no hay eventos',
    'Create Event': 'Crear evento',
    'Create your first event to start planning your schedule.':
      'Crea tu primer evento para empezar a planificar tu agenda.',
    'event name...': 'nombre del evento...',
    'event description...': 'descripción del evento...',
    'event note...': 'nota del evento...',
    Once: 'Una vez',
    'Every Year': 'Cada año',
    'By Dates': 'Por fechas',
    Period: 'Período',
    'To Do': 'Por hacer',
    Birthday: 'Cumpleaños',
    Death: 'Fallecimiento',
    Vacation: 'Vacaciones',
    From: 'Desde',
    to: 'hasta',
    'Are you really want to delete this event?':
      '¿De verdad quieres eliminar este evento?',
    'Learn and manage your vocabulary': 'Aprende y gestiona tu vocabulario',
    'New Vocabulary': 'Nuevo vocabulario',
    'Select Vocabulary': 'Seleccionar vocabulario',
    'Choose a vocabulary...': 'Elige un vocabulario...',
    'Add word': 'Agregar palabra',
    'Add Word': 'Agregar palabra',
    'Delete vocabulary': 'Eliminar vocabulario',
    'Show translations': 'Mostrar traducciones',
    'Show words': 'Mostrar palabras',
    Words: 'Palabras',
    '{{count}} words': '{{count}} palabras',
    'No words yet': 'Aún no hay palabras',
    'Start building your vocabulary by adding your first word':
      'Empieza a construir tu vocabulario agregando tu primera palabra',
    'Add First Word': 'Agregar primera palabra',
    'Select a vocabulary to start': 'Selecciona un vocabulario para comenzar',
    'Choose a vocabulary from the dropdown above or create a new one':
      'Elige un vocabulario del selector superior o crea uno nuevo',
    'Add New Vocabulary': 'Agregar nuevo vocabulario',
    'Add new vocabulary': 'Agregar nuevo vocabulario',
    'Edit Vocabulary': 'Editar vocabulario',
    'Update vocabulary': 'Actualizar vocabulario',
    'Add New Word': 'Agregar nueva palabra',
    'Add word to {{name}}': 'Agregar palabra a {{name}}',
    'Edit Word': 'Editar palabra',
    'Update word in {{name}}': 'Actualizar palabra en {{name}}',
    'Are you sure you want to delete this vocabulary? All words will be lost.':
      '¿Seguro que quieres eliminar este vocabulario? Se perderán todas las palabras.',
    'Are you sure you want to delete this word?':
      '¿Seguro que quieres eliminar esta palabra?',
    'Vocabulary Name': 'Nombre del vocabulario',
    'Enter vocabulary name...': 'Ingresa el nombre del vocabulario...',
    'Words Voice Language': 'Idioma de voz para palabras',
    'Select language for words': 'Selecciona el idioma para palabras',
    'Translations Voice Language': 'Idioma de voz para traducciones',
    'Select language for translations':
      'Selecciona el idioma para traducciones',
    'Create Vocabulary': 'Crear vocabulario',
    'Update Vocabulary': 'Actualizar vocabulario',
    'Enter word...': 'Ingresa la palabra...',
    'Enter translation...': 'Ingresa la traducción...',
    'Dictionary (Optional)': 'Diccionario (opcional)',
    'Add definition or notes...': 'Agrega una definición o notas...',
    'Update Word': 'Actualizar palabra',
    'This word already exists in this vocabulary.':
      'Esta palabra ya existe en este vocabulario.',
    'Listen to word': 'Escuchar palabra',
    'Listen to translation': 'Escuchar traducción',
    'Enable word pronunciation': 'Activar pronunciación de palabras',
    'Disable word pronunciation': 'Desactivar pronunciación de palabras',
    'Copy to clipboard': 'Copiar al portapapeles',
    'More options': 'Más opciones',
    Move: 'Mover',
    'Move to vocabulary': 'Mover al vocabulario',
    'No other vocabularies': 'No hay otros vocabularios',
    'Track and manage your goals': 'Haz seguimiento y gestiona tus objetivos',
    'Create New Objective': 'Crear nuevo objetivo',
    'New Objective': 'Nuevo objetivo',
    'No active objectives yet': 'Aún no hay objetivos activos',
    'Create your first objective': 'Crea tu primer objetivo',
    'No paused objectives': 'No hay objetivos en pausa',
    'No completed objectives yet': 'Aún no hay objetivos completados',
    'Add Objective': 'Agregar objetivo',
    'Edit Objective': 'Editar objetivo',
    'Are you really want to delete this Objective?':
      '¿De verdad quieres eliminar este objetivo?',
    'Are you really want to delete this objective?':
      '¿De verdad quieres eliminar este objetivo?',
    'Are you really want to delete this ToDo?':
      '¿De verdad quieres eliminar esta tarea?',
    'Objective name...': 'Nombre del objetivo...',
    'Parent objective': 'Objetivo padre',
    'Child objectives': 'Objetivos hijos',
    'parent objective': 'objetivo padre',
    'No parent': 'Sin padre',
    'Select Priority': 'Selecciona prioridad',
    'Select Status': 'Selecciona estado',
    'Objective description...': 'Descripción del objetivo...',
    Passive: 'Pausado',
    'Created at:': 'Creado el:',
    'Finished at:': 'Finalizado el:',
    'Not finished yet': 'Aún no finalizado',
    'Leave a comment here': 'Deja un comentario aquí',
    Steps: 'Pasos',
    Step: 'Paso',
    'Start Date': 'Fecha de inicio',
    'End Date': 'Fecha de fin',
    'Add Step': 'Agregar paso',
    Progress: 'Progreso',
    'No steps yet': 'Aún no hay pasos',
    'Add a step...': 'Agregar un paso...',
    'Search objectives...': 'Buscar objetivos...',
    'All priorities': 'Todas las prioridades',
    'Deadline (newest)': 'Fecha límite (reciente)',
    'Deadline (oldest)': 'Fecha límite (antigua)',
    Activate: 'Activar',
    Pause: 'Pausar',
    Play: 'Reproducir',
    Complete: 'Completar',
    'Toggle step': 'Alternar paso',
    'Toggle to-do': 'Alternar tarea',
    'Update Step': 'Actualizar paso',
    'Are you really want to delete this step?':
      '¿De verdad quieres eliminar este paso?',
    'This step cannot be deleted because it still has to-dos. Remove or complete all to-dos in this step first.':
      'No se puede eliminar este paso porque aún tiene tareas pendientes. Elimina o completa todas las tareas de este paso primero.',
    'Objective name is required': 'El nombre del objetivo es obligatorio',
    "Objective deadLine can't be earlier then current day":
      'La fecha límite del objetivo no puede ser anterior al día actual',
    'Priority is required': 'La prioridad es obligatoria',
    'No whiteboards yet': 'Aún no hay pizarras',
    'No to-dos yet': 'Aún no hay tareas',
    'Browse all scenarios and open details in the drawer.':
      'Explora todos los escenarios y abre los detalles en el panel lateral.',
    'Create Scenario': 'Crear escenario',
    'Loading scenarios...': 'Cargando escenarios...',
    'No Scenarios Yet': 'Aún no hay escenarios',
    'Create your first scenario to start organizing story flows and reusable scripts.':
      'Crea tu primer escenario para empezar a organizar flujos de historia y guiones reutilizables.',
    'No image': 'Sin imagen',
    'Failed to load scenarios.': 'No se pudieron cargar los escenarios.',
    'Failed to load scenario details.':
      'No se pudieron cargar los detalles del escenario.',
    'Failed to delete scenario.': 'No se pudo eliminar el escenario.',
    'Are you sure you want to delete this scenario?':
      '¿Seguro que quieres eliminar este escenario?',
    'Failed to load scenario.': 'No se pudo cargar el escenario.',
    'Name is required.': 'El nombre es obligatorio.',
    'Failed to save scenario.': 'No se pudo guardar el escenario.',
    'Loading scenario...': 'Cargando escenario...',
    'Edit Scenario': 'Editar escenario',
    'Manage scenario base fields and nested objects.':
      'Gestiona los campos base del escenario y los objetos anidados.',
    'Scenario name': 'Nombre del escenario',
    Text: 'Texto',
    'Scenario text': 'Texto del escenario',
    'Scenario description': 'Descripción del escenario',
    'Main image': 'Imagen principal',
    Gallery: 'Galería',
    'Add gallery image': 'Agregar imagen a la galería',
    Places: 'Lugares',
    'No places added yet.': 'Aún no se agregaron lugares.',
    Characters: 'Personajes',
    'No characters added yet.': 'Aún no se agregaron personajes.',
    'Scenario Events': 'Eventos del escenario',
    'No events added yet.': 'Aún no se agregaron eventos.',
    Artifacts: 'Artefactos',
    Expand: 'Expandir',
    Collapse: 'Contraer',
    'No artifacts added yet.': 'Aún no se agregaron artefactos.',
    'No whiteboards added yet.': 'Aún no se agregaron pizarras.',
    'World Maps': 'Mapas del mundo',
    'No maps added yet.': 'Aún no se agregaron mapas.',
    'Untitled map': 'Mapa sin título',
    'Map name': 'Nombre del mapa',
    Land: 'Tierra',
    'Paint land': 'Pintar tierra',
    'Outline part of the land to recolor, then press Enter or Finish shape.':
      'Delimita una parte de la tierra para cambiar el color y pulsa Enter o Terminar forma.',
    Lake: 'Lago',
    River: 'Río',
    Road: 'Camino',
    Mountain: 'Montaña',
    Forest: 'Bosque',
    Castle: 'Castillo',
    Village: 'Aldea',
    'Select tool': 'Seleccionar',
    'Pan tool': 'Mover',
    'Finish shape': 'Terminar forma',
    'Delete selected': 'Eliminar seleccionado',
    'Failed to save map.': 'No se pudo guardar el mapa.',
    'Loading map...': 'Cargando mapa...',
    'Map not found.': 'Mapa no encontrado.',
    'Delete this map?': '¿Eliminar este mapa?',
    'Delete this whiteboard?': '¿Eliminar esta pizarra?',
    'Click to add points, then press Enter or Finish shape.':
      'Haz clic para añadir puntos y luego pulsa Enter o Terminar forma.',
    'Click the map to place this object.':
      'Haz clic en el mapa para colocar este objeto.',
    'Stamp several times and overlap them, working from back to front.':
      'Coloca varias veces y superpón, trabajando de atrás hacia adelante.',
    'Each click uses a different shape. Overlap them from back to front.':
      'Cada clic usa una forma distinta. Superpón de atrás hacia adelante.',
    'Drag objects to move them. Scroll to zoom.':
      'Arrastra los objetos. Usa la rueda para el zoom.',
    'Click to outline an area, then press Enter or Finish shape.':
      'Haz clic para delimitar un área y luego pulsa Enter o Terminar forma.',
    'Outline the forest, then press Enter or Finish shape. Trees will mix automatically.':
      'Delimita el bosque y pulsa Enter o Terminar forma. Los árboles se mezclan solos.',
    'Outline the mountains, then press Enter or Finish shape.':
      'Delimita las montañas y pulsa Enter o Terminar forma.',
    'Choose a mountain type, then outline the range and press Enter or Finish shape.':
      'Elige un tipo de montaña, delimita la sierra y pulsa Enter o Terminar forma.',
    'Click an object to erase it.': 'Haz clic en un objeto para borrarlo.',
    Eraser: 'Borrador',
    'Forest fill': 'Rellenar bosque',
    'Mountain fill': 'Rellenar montañas',
    'Snow peaks': 'Cumbres nevadas',
    'Grey peaks': 'Cumbres grises',
    'Rocky peaks': 'Cumbres rocosas',
    Sparse: 'Claro',
    Dense: 'Denso',
    Normal: 'Normal',
    Hills: 'Colinas',
    Desert: 'Desierto',
    Swamp: 'Pantano',
    Snow: 'Nieve',
    Woods: 'Bosques',
    Volcano: 'Volcán',
    Hill: 'Colina',
    Sea: 'Mar',
    Trail: 'Sendero',
    Border: 'Frontera',
    Wall: 'Muralla',
    Bridge: 'Puente',
    Town: 'Pueblo',
    Port: 'Puerto',
    Fortress: 'Fortaleza',
    Tower: 'Torre',
    Monastery: 'Monasterio',
    Ruins: 'Ruinas',
    Farm: 'Granja',
    Mine: 'Mina',
    Camp: 'Campamento',
    Lighthouse: 'Faro',
    Label: 'Etiqueta',
    Navigate: 'Navegar',
    Terrain: 'Terreno',
    Routes: 'Rutas',
    Settlements: 'Asentamientos',
    Marks: 'Marcas',
    Grid: 'Cuadrícula',
    'Fit map': 'Ajustar mapa',
    'Map size': 'Tamaño del mapa',
    Small: 'Pequeño',
    Large: 'Grande',
    Huge: 'Enorme',
    Width: 'Ancho',
    Height: 'Alto',
    Pines: 'Pinos',
    Tree: 'Árbol',
    Pine: 'Pino',
    Compass: 'Brújula',
    'Scale bar': 'Escala',
    Ship: 'Barco',
    Waves: 'Olas',
    Cave: 'Cueva',
    Waterfall: 'Cascada',
    Oasis: 'Oasis',
    'Standing stones': 'Megalitos',
    Windmill: 'Molino',
    Dunes: 'Dunas',
    'Draw a continent or island coast, then press Enter or Finish shape.':
      'Dibuja la costa de un continente o isla y pulsa Enter o Terminar forma.',
    'Mark a rough land border, then Shape coast or Finish shape.':
      'Marca el borde aproximado de la tierra y pulsa Formar costa o Terminar forma.',
    'Mark a rough sea border, then Shape coast or Finish shape.':
      'Marca el borde aproximado del mar y pulsa Formar costa o Terminar forma.',
    'Mark a rough lake border, then Shape coast or Finish shape.':
      'Marca el borde aproximado del lago y pulsa Formar costa o Terminar forma.',
    'Mark a rough forest border, then Shape edge or Finish shape.':
      'Marca el borde aproximado del bosque y pulsa Formar borde o Terminar forma.',
    'Choose a mountain type, then mark a rough range and Shape edge or Finish shape.':
      'Elige un tipo de montaña, marca un borde aproximado y pulsa Formar borde o Terminar forma.',
    'Shape coast': 'Formar costa',
    'Shaping coast...': 'Formando costa...',
    'Shape edge': 'Formar borde',
    'Shaping edge...': 'Formando borde...',
    'From image': 'Desde imagen',
    'Reading map...': 'Leyendo mapa...',
    'Failed to read map image.': 'No se pudo leer la imagen del mapa.',
    'Map rebuilt from the image. You can undo.':
      'Mapa reconstruido desde la imagen. Puedes deshacer.',
    Duplicate: 'Duplicar',
    Properties: 'Propiedades',
    'Fill color': 'Color de relleno',
    'Custom color': 'Color personalizado',
    'Default colors': 'Colores predeterminados',
    Color: 'Color',
    Scale: 'Escala',
    Rotation: 'Rotación',
    'Linked place': 'Lugar vinculado',
    Rotate: 'Rotar',
    Symbol: 'Símbolo',
    'Next symbol': 'Siguiente símbolo',
    'Choose a symbol': 'Elige un símbolo',
    'Choose a symbol, then click the map to place it.':
      'Elige un símbolo y luego haz clic en el mapa para colocarlo.',
    'Bring forward': 'Traer adelante',
    'Send back': 'Enviar atrás',
    'Select an object to edit its name, size and rotation.':
      'Selecciona un objeto para editar su nombre, tamaño y rotación.',
    'Update Scenario': 'Actualizar escenario',
    'Delete this item?': '¿Eliminar este elemento?',
    'Scenario details': 'Detalles del escenario',
    'Select a scenario.': 'Selecciona un escenario.',
    'Created:': 'Creado:',
    'No text.': 'Sin texto.',
    'No description.': 'Sin descripción.',
    'No description': 'Sin descripción',
    Place: 'Lugar',
    Event: 'Evento',
    Character: 'Personaje',
    Artifact: 'Artefacto',
    Image: 'Imagen',
    'No place': 'Sin lugar',
    'Choose image file': 'Elige un archivo de imagen',
    'Failed to upload image.': 'No se pudo subir la imagen.',
    'Uploading...': 'Subiendo...',
    Remove: 'Quitar',
    'No gallery images.': 'No hay imágenes en la galería.',
    'Create New Purchase list': 'Crear nueva lista de compras',
    'No Purchase Lists Yet': 'Aún no hay listas de compras',
    'Your purchase lists will appear here once this section starts being used.':
      'Tus listas de compras aparecerán aquí cuando esta sección empiece a usarse.',
    Participants: 'Participantes',
    'Loading participants...': 'Cargando participantes...',
    'This event is currently blocked.':
      'Este evento está bloqueado actualmente.',
    'Event not found. Please check the key.':
      'No se encontró el evento. Revisa la clave.',
    'Please enter an event key.': 'Introduce una clave del evento.',
    'Private List': 'Lista privada',
    'Please enter the event key to view the participant list.':
      'Introduce la clave del evento para ver la lista de participantes.',
    'Enter key': 'Introduce la clave',
    'Checking...': 'Comprobando...',
    'Access List': 'Abrir lista',
    Invited: 'Invitados',
    Attending: 'Asisten',
    'Not attending': 'No asisten',
    Gifts: 'Regalos',
    'Husband Side': 'Lado del novio',
    'Wife Side': 'Lado de la novia',
    'No participants on this side.': 'No hay participantes en este lado.',
    Gift: 'Regalo',
    Yes: 'Sí',
    No: 'No',
    Tags: 'Etiquetas',
    Ingredients: 'Ingredientes',
    'No ingredients': 'Sin ingredientes',
    '{{count}} ingredients': '{{count}} ingredientes',
    'type ingredient': 'escribe un ingrediente',
    'Keep your favorite meals, ingredients, and cooking notes in one place':
      'Guarda tus comidas favoritas, ingredientes y notas de cocina en un solo lugar',
    'Create Recipe': 'Crear receta',
    'Create Menu': 'Crear menú',
    'Add Menu': 'Agregar menú',
    'Edit Menu': 'Editar menú',
    'Menu name...': 'Nombre del menú...',
    'Select recipes': 'Selecciona recetas',
    '{{count}} recipes': '{{count}} recetas',
    'Are you really want to delete this Menu?':
      '¿De verdad quieres eliminar este menú?',
    'Group recipes into shared meals.':
      'Agrupa recetas en comidas compartidas.',
    'No Recipes Yet': 'Aún no hay recetas',
    'No Menus Yet': 'Aún no hay menús',
    'Create a menu and pick recipes to group meals together.':
      'Crea un menú y elige recetas para agrupar las comidas.',
    'Create your first recipe to keep ingredients, cooking steps, and meal ideas together.':
      'Crea tu primera receta para guardar ingredientes, pasos de cocina e ideas de comidas en un solo lugar.',
    'Add Recipe': 'Agregar receta',
    'Edit Recipe': 'Editar receta',
    'Are you really want to delete this Recipe?':
      '¿De verdad quieres eliminar esta receta?',
    'Recipe name...': 'Nombre de la receta...',
    Total: 'Total',
    Participating: 'Participan',
    'Not participating': 'No participan',
    'Manage confirmations and guest list':
      'Gestiona las confirmaciones y la lista de invitados',
    Rest: 'Resto',
    'Husband side participants': 'Participantes del lado del novio',
    'No Husband Side Participants': 'No hay participantes del lado del novio',
    'Participants for the husband side will appear here once people respond or are added.':
      'Los participantes del lado del novio aparecerán aquí cuando respondan o se agreguen.',
    'Husband side': 'Lado del novio',
    'Wife side participants': 'Participantes del lado de la novia',
    'No Wife Side Participants': 'No hay participantes del lado de la novia',
    'Participants for the wife side will appear here once people respond or are added.':
      'Los participantes del lado de la novia aparecerán aquí cuando respondan o se agreguen.',
    'Wife side': 'Lado de la novia',
    'Are you really want to delete this Participant?':
      '¿De verdad quieres eliminar este participante?',
    daysShort: 'días',
    hoursShort: 'h',
    minutesShort: 'min',
    secondsShort: 'seg',
    'Loading invitation...': 'Cargando invitación...',
    'Wedding event not found': 'No se encontró el evento de boda',
    'We warmly invite you': 'Te invitamos con cariño',
    'to our wedding ceremony': 'a nuestra ceremonia de boda',
    'Respectfully,': 'Con cariño,',
    'Time left': 'Falta',
    Schedule: 'Programa del día',
    'How to get there': 'Cómo llegar',
    'Please confirm your attendance': 'Por favor confirma tu asistencia',
    'Thank you': 'Gracias',
    'Please fill in the field': 'Por favor completa el campo',
    'We will attend': 'Asistiremos',
    'We cannot attend': 'No podremos asistir',
    'Guest count': 'Cantidad de invitados',
    Send: 'Enviar',
    'Contact us': 'Contáctanos',
    'Wedding Events': 'Eventos de boda',
    'Manage invitations, schedules, and event access from one place.':
      'Gestiona invitaciones, horarios y acceso al evento desde un solo lugar.',
    'Create New Event': 'Crear nuevo evento',
    'Total events': 'Total de eventos',
    'Gift info enabled': 'Información de regalos activada',
    'Edit Wedding Event': 'Editar evento de boda',
    'Create New Wedding Event': 'Crear nuevo evento de boda',
    'Event Slug (URL-friendly)': 'Slug del evento (amigable para URL)',
    'e.g., arman-armine-2025': 'p. ej., arman-armine-2025',
    'This will be used in the URL: /wedding/[slug]/invitation':
      'Esto se usará en la URL: /wedding/[slug]/invitation',
    'Event Title': 'Título del evento',
    'e.g., Arman & Armine Wedding': 'p. ej., Boda de Arman y Armine',
    'Husband Name': 'Nombre del novio',
    'e.g., Arman': 'p. ej., Arman',
    'Wife Name': 'Nombre de la novia',
    'e.g., Armine': 'p. ej., Armine',
    'Wedding Date': 'Fecha de la boda',
    'Hero Image URL (Optional)': 'URL de la imagen principal (opcional)',
    'If provided, this image will replace the default wedding hero image on the invitation page':
      'Si se proporciona, esta imagen reemplazará la imagen principal predeterminada en la página de invitación.',
    Configuration: 'Configuración',
    'Block Public Access': 'Bloquear acceso público',
    'When enabled, public access to this wedding will be blocked, but it will still be visible in the main list.':
      'Cuando está activado, el acceso público a esta boda se bloqueará, pero seguirá visible en la lista principal.',
    'Show Gift Information': 'Mostrar información de regalos',
    'When enabled, users will be able to see gift information on public pages.':
      'Cuando está activado, los usuarios podrán ver la información de regalos en las páginas públicas.',
    'Location {{index}}': 'Lugar {{index}}',
    'Venue Name': 'Nombre del lugar',
    'e.g., Ceremony': 'p. ej., Ceremonia',
    'Place Name': 'Nombre del sitio',
    'e.g., Saint Cross Church': 'p. ej., Iglesia de la Santa Cruz',
    'Icon Type': 'Tipo de icono',
    'Select icon type...': 'Selecciona el tipo de icono...',
    'Map Link': 'Enlace del mapa',
    'Image URL (Optional)': 'URL de la imagen (opcional)',
    'No Wedding Events Yet': 'Aún no hay eventos de boda',
    'Create your first wedding event to manage invitations, schedules, and participants from one place.':
      'Crea tu primer evento de boda para gestionar invitaciones, horarios y participantes desde un solo lugar.',
    'Public blocked': 'Acceso público bloqueado',
    'Public active': 'Acceso público activo',
    'Gift info on': 'Regalos activados',
    'Gift info off': 'Regalos desactivados',
    '{{count}} locations': '{{count}} lugares',
    'Copy URL': 'Copiar URL',
    'View Invitation': 'Ver invitación',
    'Copy Participants URL': 'Copiar URL de participantes',
    Slug: 'Slug',
    'Invitation URL': 'URL de invitación',
    at: 'a las',
    'Are you sure you want to delete "{{title}}"? This action cannot be undone.':
      '¿Seguro que quieres eliminar "{{title}}"? Esta acción no se puede deshacer.',
  },
}

export const isSupportedLanguage = (
  value: string | null | undefined,
): value is SupportedLanguage =>
  LANGUAGE_OPTIONS.some((language) => language.code === value)

export const getLanguageLocale = (language: SupportedLanguage) => {
  const selectedLanguage = LANGUAGE_OPTIONS.find(
    (option) => option.code === language,
  )

  return selectedLanguage?.locale || 'en-GB'
}
